// skills/combat/combat.js
// Combat skill - Real-time HP exchange combat system

import gameState from '../../game/gameState.js';
import { SKILLS, COMBAT_CONFIG } from '../../game/constants.js';
import { addXP, getLevelFromXP } from '../../systems/experience.js';
import { getItem } from '../../data/items/index.js';
import { getSpell, SPELL_EFFECTS } from '../../data/spells.js';
import {
    COMBAT_ZONES,
    MOBS,
    getZone,
    getMob,
    getZoneMobs,
    getRandomMobForZone,
    getRandomVisibleMobForZone,
    getZoneLoot,
    getMobLoot,
    calculateMobDamage,
    getBossMechanics,
    isBoss,
    getMobMechanic
} from './combatData.js';
import logger from '../../utils/logger.js';
import { bountySkill } from '../bounty/bounty.js';
import { getBountyMark } from '../bounty/bountyData.js';
import { pilferingSkill } from '../pilfering/pilfering.js';
import { getTalentFlatBonus, getTalentProcChance, rollTalentProc, getGlobalRareLootBonus, getTalentXPBonus } from '../../systems/talentTree/talentTree.js';
import { toastSuccess, toastWarning } from '../../ui/toast.js';
import { architectingSkill } from '../architecting/architecting.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { rollForCorruption, isSkillCorrupted, deactivateCorruption, getCombatDamageTakenMult, getCombatDefenseReduction, getCombatGlyphSave, getCombatLootMark, getCombatXPMark } from '../../systems/corruption.js';
import { SET_BONUSES, getActiveBonuses } from '../../data/items/setBonusData.js';

class CombatSkill {
    constructor() {
        this.onCombatUpdate = null;  // Callback for UI updates
        this.onMobKilled = null;     // Callback when mob is killed
        this.onPlayerDeath = null;   // Callback when player dies
        this.onRespawn = null;       // Callback when player respawns
        this.onZoneCleared = null;   // Callback when zone is cleared
        this.onDamageDealt = null;   // Callback when player deals damage (damage, type)
        this.onDamageTaken = null;   // Callback when player takes damage (damage, type)
        this.onHeal = null;          // Callback when player heals (amount)
        this.targetMob = null;       // Specific mob to farm (when zone is cleared)

        // Preview state - shows mob info before combat starts
        this.previewZone = null;     // Zone being previewed
        this.previewMob = null;      // Mob being previewed
        this.previewTier = null;     // Tier being previewed (for tiered zones)
        this.isExplicitTarget = false; // True if user clicked specific mob, false if random preview

        // Kill delay - brief pause after killing to show death
        this.mobKillTime = null;     // When mob was killed
        this.killDelay = 350;        // ms to wait before spawning next mob
        this.lastKilledMob = null;   // Store killed mob for UI display during delay

        // Magic combat state - load from gameState for persistence
        this.activeSpell = gameState.getSelectedSpell();
        this.spellEffects = {        // Active spell effects on player/mob
            dot: null,               // { damage, endTime, tickInterval, lastTick }
            slow: null,              // { multiplier, endTime }
            defense: null,           // { bonus, endTime }
            crit: null,              // { bonus, endTime }
            hot: null,               // { amount, endTime, tickInterval, lastTick } - Heal over Time from empowered food
            petBuff: null            // { type, bonus, endTime, petId } - Combat Companion pet buff
        };

        // Combat talent state (resets each fight or on page load)
        this.talentState = {
            berserkerStacks: [],     // Array of { expireTime } for rampage stacks
            lastStandUsed: false,    // Has Last Stand been triggered this fight?
            spellEchoCounter: 0,     // Count for Spell Echo (triggers on every 3rd)
            mobBleed: null,          // { damagePerTick, ticksRemaining, lastTick } - bleed on mob
            lastRegenTick: 0         // Last time combat regen procced
        };

        // Corrupted Prey state - persistent marks on mob
        this.corruptedPrey = {
            marked: false,           // Whether current mob is marked
            lootMultiplier: 1,       // Stored loot mult when marked (2x or 3x)
            xpMultiplier: 1,         // Stored XP mult when marked (1.5x or 2x)
        };

        // Boss mechanic state - reset per fight
        this.bossState = null;       // null = not a boss fight, set by initBossState()

        // Mob mechanic state - reset per fight
        this.mobMechanicState = {
            snare: null,             // { endTime } - player attack speed slowed
            dot: null,               // { damage, interval, endTime, lastTick } - fire/poison DoT
            curse: null,             // { defenseReduction, endTime } - temporary defense reduction
            armorShred: null,        // { stacks, endTime } - stacking defense reduction
            enraged: false,          // Permanent once triggered (pit fiend)
        };

        // Track last hit for crit-dependent effects
        this.lastHitWasCrit = false;

        // Equipment special effect state - reset per fight
        this.equipmentState = {
            hitCounter: 0,           // Tracks consecutive hits (for Smoldering Strikes, Eclipse Slash)
            searingArrowsDot: null,  // { damage, interval, endTime, lastTick } - fire DoT from Cindered Shortbow
            smolderingDot: null,     // { damage, interval, endTime, lastTick } - fire DoT from Emberheart Focus
            emberShieldBuff: null,   // { defenseBonus, endTime } - temp defense from Warden's Ember Ring
            relentlessStacks: [],    // Array of { expireTime } - speed stacks from Eclipse Wrath Band
            markedPrey: null,        // { endTime } - damage amplification from Eclipse Precision Band
        };

        // Set bonus state - reset per fight session
        this.setBonusState = {
            // Ashforged (melee) — Momentum
            momentum: 0,                // Current Momentum stacks (0-10)
            // Ashforged 6pc — Weapon Mastery
            afterimage: null,            // { endTime, nextAttack, cooldownEnd } - clone state
            riposteCounter: 0,           // Hits taken counter for Riposte (every 3rd)
            // Cindermark (ranged) — Piercing
            rangedHitCounter: 0,         // Hits for Piercing Shot (every 5th)
            phantomShots: null,          // { remaining, nextShotTime, baseDamage } - queued phantom shots
            // Pyreweave (magic) — Spell Cascade
            // (spell echo is chance-based, no state needed)
            // Spell Fusion
            spellFusion: {
                shatterReady: false,     // Next hit deals 2x (fire+ice)
                shatterCooldownEnd: 0,   // When Shatter can reapply
                harmonyRefreshTime: 0,   // Next auto-refresh for defense/crit buffs
            },
            // Cached set bonus detection (refreshed when gear changes / combat starts)
            cachedBonuses: null,         // { setId: { count, activeTiers: [2,4,6] } }
        };
    }

    // ==================== BOSS MECHANIC HELPERS ====================

    /**
     * Initialize boss state when a boss mob spawns
     * @param {Object} mob - The mob object
     */
    initBossState(mob) {
        const mechanics = getBossMechanics(mob.id);
        if (!mechanics) {
            this.bossState = null;
            return;
        }

        this.bossState = {
            mechanics,
            fightStartTime: Date.now(),
            currentPhase: mechanics.phases ? mechanics.phases[0] : null,
            enrageStage: -1,            // -1 = not enraged, 0+ = stage index
            periodicLastTick: {},        // { effectId: lastTickTime }
            phaseStartTime: Date.now(),  // When current phase started (for timed phases)
            activePlayerDots: []         // DoT effects from boss on player
        };

        // Initialize periodic effect timers
        if (mechanics.periodicEffects) {
            for (const effect of mechanics.periodicEffects) {
                this.bossState.periodicLastTick[effect.id] = Date.now();
            }
        }

        logger.debug(`[BOSS] Initialized boss state for ${mob.name}`);
    }

    /**
     * Clear boss state (on mob death, zone exit, player death)
     */
    clearBossState() {
        this.bossState = null;
    }

    /**
     * Clear mob mechanic state (on mob death / zone change)
     */
    clearMobMechanicState() {
        this.mobMechanicState = {
            snare: null,
            dot: null,
            curse: null,
            armorShred: null,
            enraged: false,
        };
    }

    /**
     * Process mob mechanic effects on mob attack (player debuffs applied by mob)
     * Called after mob successfully hits player
     * @param {Object} mob - Current mob
     * @param {number} damage - Damage dealt to player
     */
    applyMobMechanicOnHit(mob, damage) {
        const mechanic = getMobMechanic(mob.id);
        if (!mechanic) return;

        const now = Date.now();

        switch (mechanic.type) {
            case 'snare':
                if (Math.random() < mechanic.chance) {
                    this.mobMechanicState.snare = { endTime: now + mechanic.duration };
                    logger.debug(`[COMBAT] ${mob.name} snared player! -${mechanic.slowPercent * 100}% attack speed for ${mechanic.duration / 1000}s`);
                }
                break;

            case 'fireDot':
            case 'poison':
                if (!this.mobMechanicState.dot || this.mobMechanicState.dot.endTime < now) {
                    this.mobMechanicState.dot = {
                        damage: mechanic.damage,
                        interval: mechanic.interval,
                        endTime: now + mechanic.duration,
                        lastTick: now,
                        type: mechanic.type
                    };
                    logger.debug(`[COMBAT] ${mob.name} applied ${mechanic.type} to player!`);
                }
                break;

            case 'curse':
                if (Math.random() < mechanic.chance) {
                    this.mobMechanicState.curse = {
                        defenseReduction: mechanic.defenseReduction,
                        endTime: now + mechanic.duration
                    };
                    logger.debug(`[COMBAT] ${mob.name} cursed player! -${mechanic.defenseReduction} defense for ${mechanic.duration / 1000}s`);
                }
                break;

            case 'armorShred': {
                const shred = this.mobMechanicState.armorShred;
                if (shred && shred.stacks < mechanic.maxStacks) {
                    shred.stacks++;
                    shred.endTime = now + mechanic.duration;
                } else if (!shred || shred.stacks >= mechanic.maxStacks) {
                    this.mobMechanicState.armorShred = {
                        stacks: shred ? Math.min(shred.stacks + 1, mechanic.maxStacks) : 1,
                        endTime: now + mechanic.duration
                    };
                }
                logger.debug(`[COMBAT] ${mob.name} shredded armor! Stack ${this.mobMechanicState.armorShred.stacks}/${mechanic.maxStacks}`);
                break;
            }

            case 'selfHeal':
                if (Math.random() < mechanic.chance) {
                    const combat = gameState.getCombat();
                    const healAmount = Math.min(mechanic.healAmount, mob.hp - combat.mobCurrentHp);
                    if (healAmount > 0) {
                        combat.mobCurrentHp += healAmount;
                        logger.debug(`[COMBAT] ${mob.name} healed ${healAmount} HP!`);
                    }
                }
                break;

            case 'absorption': {
                const healAmt = Math.min(Math.floor(damage * mechanic.healPercent), mechanic.healCap);
                if (healAmt > 0) {
                    const combat = gameState.getCombat();
                    combat.mobCurrentHp = Math.min(mob.hp, combat.mobCurrentHp + healAmt);
                    logger.debug(`[COMBAT] ${mob.name} absorbed ${healAmt} HP from damage dealt!`);
                }
                break;
            }
        }
    }

    /**
     * Check mob mechanic before player attack (phase shift dodge, enrage, time warp)
     * @param {Object} mob - Current mob
     * @returns {{ dodged: boolean, reflectDamage: number }}
     */
    checkMobMechanicOnPlayerAttack(mob) {
        const mechanic = getMobMechanic(mob.id);
        if (!mechanic) return { dodged: false, reflectDamage: 0 };

        switch (mechanic.type) {
            case 'phaseShift':
                if (Math.random() < mechanic.chance) {
                    logger.debug(`[COMBAT] ${mob.name} phase shifted! Dodged player attack!`);
                    return { dodged: true, reflectDamage: 0 };
                }
                break;

            case 'enrage': {
                const combat = gameState.getCombat();
                const hpPercent = combat.mobCurrentHp / mob.hp;
                if (!this.mobMechanicState.enraged && hpPercent <= mechanic.hpThreshold) {
                    this.mobMechanicState.enraged = true;
                    logger.debug(`[COMBAT] ${mob.name} enraged! +${mechanic.speedBoost * 100}% attack speed!`);
                }
                break;
            }
        }

        return { dodged: false, reflectDamage: 0 };
    }

    /**
     * Apply damage reflection after player hits mob
     * @param {Object} mob - Current mob
     * @param {number} damage - Damage dealt to mob
     * @returns {number} Reflected damage to player
     */
    getMobReflectDamage(mob, damage) {
        const mechanic = getMobMechanic(mob.id);
        if (!mechanic || mechanic.type !== 'damageReflection') return 0;
        return Math.min(Math.floor(damage * mechanic.reflectPercent), mechanic.reflectCap);
    }

    /**
     * Process mob DoT effects each tick
     * @returns {number} Total DoT damage dealt this tick
     */
    processMobDotTick() {
        const now = Date.now();
        let totalDotDamage = 0;

        // Process DoT
        if (this.mobMechanicState.dot) {
            const dot = this.mobMechanicState.dot;
            if (now >= dot.endTime) {
                this.mobMechanicState.dot = null;
            } else if (now - dot.lastTick >= dot.interval) {
                dot.lastTick = now;
                totalDotDamage += dot.damage;
            }
        }

        // Expire timed effects
        if (this.mobMechanicState.snare && now >= this.mobMechanicState.snare.endTime) {
            this.mobMechanicState.snare = null;
        }
        if (this.mobMechanicState.curse && now >= this.mobMechanicState.curse.endTime) {
            this.mobMechanicState.curse = null;
        }
        if (this.mobMechanicState.armorShred && now >= this.mobMechanicState.armorShred.endTime) {
            this.mobMechanicState.armorShred = null;
        }

        return totalDotDamage;
    }

    /**
     * Get effective player attack speed considering mob snare mechanic
     * @param {number} baseSpeed - Base player attack speed
     * @returns {number} Modified attack speed
     */
    getSnareModifiedSpeed(baseSpeed) {
        if (!this.mobMechanicState.snare) return baseSpeed;
        const currentMob = gameState.getCombat().currentMob;
        const mobId = typeof currentMob === 'string' ? currentMob : currentMob?.id;
        const mechanic = getMobMechanic(mobId);
        if (!mechanic || mechanic.type !== 'snare') return baseSpeed;
        return baseSpeed / (1 - mechanic.slowPercent); // Slower = higher interval
    }

    /**
     * Get effective mob attack speed considering enrage and time warp mechanics
     * @param {Object} mob - Current mob
     * @returns {number} Modified attack speed
     */
    getMobMechanicAttackSpeed(mob) {
        const mechanic = getMobMechanic(mob.id);
        if (!mechanic) return mob.attackSpeed;

        if (mechanic.type === 'enrage' && this.mobMechanicState.enraged) {
            return Math.floor(mob.attackSpeed / (1 + mechanic.speedBoost));
        }
        if (mechanic.type === 'timeWarp') {
            return mechanic.minSpeed + Math.floor(Math.random() * (mechanic.maxSpeed - mechanic.minSpeed));
        }
        return mob.attackSpeed;
    }

    /**
     * Get total defense reduction from mob mechanics (curse + armor shred)
     * @returns {number} Total flat defense reduction
     */
    getMobMechanicDefenseReduction() {
        let reduction = 0;
        if (this.mobMechanicState.curse) {
            reduction += this.mobMechanicState.curse.defenseReduction;
        }
        if (this.mobMechanicState.armorShred) {
            const currentMob = gameState.getCombat().currentMob;
            const mobId = typeof currentMob === 'string' ? currentMob : currentMob?.id;
            const mechanic = getMobMechanic(mobId);
            if (mechanic && mechanic.type === 'armorShred') {
                reduction += mechanic.defensePerStack * this.mobMechanicState.armorShred.stacks;
            }
        }
        return reduction;
    }

    /**
     * Process boss mechanics each tick. Must be called before player/mob attacks.
     * Handles phase transitions, enrage timers, and periodic effects.
     */
    processBossMechanics() {
        if (!this.bossState) return;

        const combat = gameState.getCombat();
        const mob = combat.currentMob;
        if (!mob) return;

        const now = Date.now();
        const mechanics = this.bossState.mechanics;
        const hpPercent = combat.mobCurrentHp / mob.hp;

        // --- Phase transitions (HP-based) ---
        if (mechanics.phases && mechanics.phases.length > 1) {
            // Find the correct phase based on HP (phases ordered high→low threshold)
            let newPhase = mechanics.phases[0];
            for (let i = mechanics.phases.length - 1; i >= 0; i--) {
                if (hpPercent <= mechanics.phases[i].hpThreshold) {
                    newPhase = mechanics.phases[i];
                    break;
                }
            }

            if (newPhase.id !== this.bossState.currentPhase?.id) {
                const oldPhase = this.bossState.currentPhase;
                this.bossState.currentPhase = newPhase;
                this.bossState.phaseStartTime = now;
                // Clear player DoTs from old phase and apply new ones
                this.bossState.activePlayerDots = [];
                if (newPhase.playerEffects) {
                    for (const effect of newPhase.playerEffects) {
                        if (effect.type === 'dot' || effect.type === 'true_dot') {
                            this.bossState.activePlayerDots.push({
                                ...effect,
                                lastTick: now
                            });
                        }
                    }
                }
                logger.debug(`[BOSS] ${mob.name} phase: ${oldPhase?.name || 'none'} → ${newPhase.name}`);
            }

            // Handle timed phases (revert after duration)
            if (this.bossState.currentPhase.duration) {
                const elapsed = now - this.bossState.phaseStartTime;
                if (elapsed >= this.bossState.currentPhase.duration) {
                    // Revert to the previous phase (find phase with higher HP threshold)
                    const currentIdx = mechanics.phases.indexOf(this.bossState.currentPhase);
                    if (currentIdx > 0) {
                        const revertPhase = mechanics.phases[currentIdx - 1];
                        this.bossState.currentPhase = revertPhase;
                        this.bossState.phaseStartTime = now;
                        this.bossState.activePlayerDots = [];
                        logger.debug(`[BOSS] ${mob.name} timed phase ended, reverted to ${revertPhase.name}`);
                    }
                }
            }
        }

        // --- Enrage timer ---
        if (mechanics.enrage) {
            const fightDuration = now - this.bossState.fightStartTime;
            const stages = mechanics.enrage.stages;
            for (let i = stages.length - 1; i >= 0; i--) {
                if (fightDuration >= stages[i].timer && this.bossState.enrageStage < i) {
                    this.bossState.enrageStage = i;
                    logger.debug(`[BOSS] ${mob.name} ENRAGED! Stage ${i + 1} (${stages[i].damageMult}x dmg, ${stages[i].speedMult}x speed)`);
                }
            }
        }

        // --- Periodic effects (healing pulse, true damage, etc.) ---
        if (mechanics.periodicEffects) {
            for (const effect of mechanics.periodicEffects) {
                // Check if effect is active in current phase
                if (effect.activePhase && this.bossState.currentPhase?.id !== effect.activePhase) {
                    // Check intensify phase as well
                    if (effect.intensify && this.bossState.currentPhase?.id === effect.intensify.phase) {
                        // Use intensified amount
                    } else {
                        continue; // Skip — not in active phase
                    }
                }

                const lastTick = this.bossState.periodicLastTick[effect.id] || now;
                if (now - lastTick >= effect.interval) {
                    this.bossState.periodicLastTick[effect.id] = now;

                    // Determine amount (check intensify)
                    let amount = effect.amount;
                    if (effect.intensify && this.bossState.currentPhase?.id === effect.intensify.phase) {
                        amount = effect.intensify.amount;
                    }

                    if (effect.type === 'heal_mob') {
                        const oldHp = combat.mobCurrentHp;
                        combat.mobCurrentHp = Math.min(mob.hp, combat.mobCurrentHp + amount);
                        const healed = combat.mobCurrentHp - oldHp;
                        if (healed > 0) {
                            logger.debug(`[BOSS] ${effect.name}: ${mob.name} healed ${healed} HP (${combat.mobCurrentHp}/${mob.hp})`);
                        }
                    } else if (effect.type === 'true_damage_player') {
                        // True damage ignores all defense
                        const result = gameState.takeDamage(amount);
                        logger.debug(`[BOSS] ${effect.name}: ${amount} true damage to player (${gameState.getCurrentHp()}/${gameState.getMaxHp()} HP)`);
                        if (result.died) {
                            this.handleDeath();
                            this.clearSpellEffects();
                            return;
                        }
                    }
                }
            }
        }

        // --- Player DoTs from boss phases (Scorching Aura, Arcane Disruption) ---
        for (const dot of this.bossState.activePlayerDots) {
            if (now - dot.lastTick >= dot.interval) {
                dot.lastTick = now;
                if (dot.type === 'true_dot') {
                    // True damage ignores defense
                    const result = gameState.takeDamage(dot.damage);
                    logger.debug(`[BOSS] ${dot.name}: ${dot.damage} true damage to player`);
                    if (result.died) {
                        this.handleDeath();
                        this.clearSpellEffects();
                        return;
                    }
                } else if (dot.type === 'dot') {
                    // Regular damage — still goes through defense (but can't be evaded)
                    const damageResult = this.calculateDamageToPlayer(dot.damage, true);
                    const result = gameState.takeDamage(damageResult.damage);
                    logger.debug(`[BOSS] ${dot.name}: ${damageResult.damage} damage to player (${dot.damage} raw)`);
                    if (result.died) {
                        this.handleDeath();
                        this.clearSpellEffects();
                        return;
                    }
                }
            }
        }
    }

    /**
     * Get current boss modifiers affecting combat calculations.
     * Returns an object with all active modifier values.
     * @returns {Object} Boss modifiers (all default to neutral values if no boss)
     */
    getBossModifiers() {
        const defaults = {
            mobDamageMult: 1.0,
            mobSpeedMult: 1.0,
            mobDefenseBonus: 0,
            playerDamageReduction: 0,     // 0-1 (Void Barrier = 0.25)
            ignoreDefensePercent: 0,       // 0-1 (Cindered Knight = 0.3)
            damageFloor: 0
        };

        if (!this.bossState) return defaults;

        const mechanics = this.bossState.mechanics;
        const phase = this.bossState.currentPhase;

        // Start from defaults
        let mods = { ...defaults };

        // Apply damage floor
        mods.damageFloor = mechanics.damageFloor || 0;

        // Apply phase modifiers
        if (phase?.mobMods) {
            if (phase.mobMods.damageMult) mods.mobDamageMult *= phase.mobMods.damageMult;
            if (phase.mobMods.speedMult) mods.mobSpeedMult *= phase.mobMods.speedMult;
            if (phase.mobMods.defenseBonus) mods.mobDefenseBonus += phase.mobMods.defenseBonus;
            if (phase.mobMods.ignoreDefensePercent) mods.ignoreDefensePercent = phase.mobMods.ignoreDefensePercent;
        }

        // Apply phase player effects (damage reduction like Void Barrier)
        if (phase?.playerEffects) {
            for (const effect of phase.playerEffects) {
                if (effect.type === 'damage_reduction') {
                    mods.playerDamageReduction = Math.max(mods.playerDamageReduction, effect.value);
                }
            }
        }

        // Apply enrage modifiers (stacks with phase)
        if (mechanics.enrage && this.bossState.enrageStage >= 0) {
            const stage = mechanics.enrage.stages[this.bossState.enrageStage];
            mods.mobDamageMult *= stage.damageMult;
            mods.mobSpeedMult *= stage.speedMult;
        }

        return mods;
    }

    /**
     * Get current combat level
     * @returns {number}
     */
    getLevel() {
        const skill = gameState.getSkill(SKILLS.COMBAT);
        return skill ? skill.level : 1;
    }

    /**
     * Get total kills for all mobs in a zone
     * @param {string} zoneId - Zone ID
     * @returns {number} Total kills in zone
     */
    getZoneKillCount(zoneId) {
        const zone = getZone(zoneId);
        if (!zone) return 0;

        let total = 0;
        for (const mobId of zone.mobs) {
            total += gameState.getKillCount(mobId);
        }
        return total;
    }

    /**
     * Get Pyre modifier for a zone (penalty or bonus)
     * Returns multipliers for damage, speed, and loot based on Pyre level
     * - Below requirement: penalty (up to -50%)
     * - Above requirement: bonus (up to +10% at level 99)
     * @param {string} zoneId - Zone ID
     * @returns {{ damageMult: number, speedMult: number, lootMult: number, deficit: number, bonus: number }}
     */
    getPyrePenalty(zoneId) {
        const zone = getZone(zoneId);
        // No modifier if zone doesn't require Pyre
        if (!zone?.pyreLevel) {
            return { damageMult: 1, speedMult: 1, lootMult: 1, deficit: 0, bonus: 0 };
        }

        const pyreLevel = gameState.getSkill(SKILLS.PYRE)?.level || 1;

        // Check if below requirement (penalty)
        if (pyreLevel < zone.pyreLevel) {
            // Calculate deficit (0-1 scale)
            const deficit = (zone.pyreLevel - pyreLevel) / zone.pyreLevel;
            return {
                damageMult: 1 - (deficit * 0.5),   // Up to -50% damage
                speedMult: 1 + (deficit * 0.5),    // Up to +50% slower
                lootMult: 1 - (deficit * 0.5),     // Up to -50% loot chance
                deficit,
                bonus: 0
            };
        }

        // Above requirement - calculate bonus (up to 10% at level 99)
        const maxPyre = 99;
        const levelsAbove = pyreLevel - zone.pyreLevel;
        const maxLevelsAbove = maxPyre - zone.pyreLevel;
        // Scale bonus from 0% at zone.pyreLevel to 10% at level 99
        const bonus = maxLevelsAbove > 0 ? (levelsAbove / maxLevelsAbove) * 0.10 : 0;

        return {
            damageMult: 1 + bonus,      // Up to +10% damage
            speedMult: 1 - bonus,       // Up to -10% faster (reduced attack time)
            lootMult: 1 + bonus,        // Up to +10% loot chance
            deficit: 0,
            bonus
        };
    }

    // ==================== EQUIPMENT SPECIAL EFFECTS ====================

    /**
     * Get all unique/special effects from equipped gear
     * @returns {{ pendantEffect: Object|null, ringEffect: Object|null, weaponEffect: Object|null, shieldEffect: Object|null, quiverEffect: Object|null }}
     */
    getEquippedEffects() {
        const gear = gameState.getEquippedGear();
        const result = {
            pendantEffect: null,
            ringEffect: null,
            weaponEffect: null,
            shieldEffect: null,
            quiverEffect: null
        };

        if (gear.neck) {
            const pendant = getItem(gear.neck);
            if (pendant?.uniqueEffect) result.pendantEffect = pendant.uniqueEffect;
        }
        if (gear.ring) {
            const ring = getItem(gear.ring);
            if (ring?.uniqueEffect) result.ringEffect = ring.uniqueEffect;
        }
        if (gear.weapon) {
            const weapon = getItem(gear.weapon);
            if (weapon?.specialEffect) result.weaponEffect = weapon.specialEffect;
        }
        if (gear.offhand) {
            const offhand = getItem(gear.offhand);
            if (offhand?.specialEffect) result.shieldEffect = offhand.specialEffect;
        }
        if (gear.back) {
            const back = getItem(gear.back);
            if (back?.specialEffect) result.quiverEffect = back.specialEffect;
        }

        return result;
    }

    /**
     * Clear equipment effect state (on stop fighting, death, etc.)
     */
    clearEquipmentEffectState() {
        this.equipmentState.hitCounter = 0;
        this.equipmentState.searingArrowsDot = null;
        this.equipmentState.smolderingDot = null;
        this.equipmentState.emberShieldBuff = null;
        this.equipmentState.relentlessStacks = [];
        this.equipmentState.markedPrey = null;
    }

    /**
     * Get spell damage bonus from jewelry (ring and neck slots)
     * @returns {number} Spell damage multiplier bonus (adds to staff multiplier)
     */
    getJewelrySpellDamage() {
        const gear = gameState.getEquippedGear();
        let bonus = 0;

        // Check ring slot
        if (gear.ring) {
            const ring = getItem(gear.ring);
            if (ring?.spellDamage) bonus += ring.spellDamage;
        }

        // Check neck slot
        if (gear.neck) {
            const pendant = getItem(gear.neck);
            if (pendant?.spellDamage) bonus += pendant.spellDamage;
        }

        return bonus;
    }

    /**
     * Get all damage bonus from pendant unique effects
     * @returns {number} All damage multiplier (e.g., 0.10 = +10%)
     */
    getPendantAllDamageBonus() {
        const effects = this.getEquippedEffects();
        if (!effects.pendantEffect) return 0;
        if (effects.pendantEffect.type === 'allDamageBonus') {
            return effects.pendantEffect.value || 0;
        }
        return 0;
    }

    /**
     * Get lifesteal percentage from equipped gear (pendant + weapon)
     * @returns {number} Lifesteal fraction (e.g., 0.03 = 3%)
     */
    getLifestealPercent() {
        const effects = this.getEquippedEffects();
        let lifesteal = 0;

        // Bloodfang Pendant lifesteal
        if (effects.pendantEffect?.type === 'lifesteal') {
            lifesteal += effects.pendantEffect.value || 0;
        }

        // Eclipse Reaver lifesteal
        if (effects.weaponEffect?.type === 'eclipseReaver') {
            lifesteal += effects.weaponEffect.lifestealPercent || 0;
        }

        return lifesteal;
    }

    /**
     * Process lifesteal healing after dealing damage
     * @param {number} damageDealt - Amount of damage dealt
     */
    processLifesteal(damageDealt) {
        const lifesteal = this.getLifestealPercent();
        if (lifesteal <= 0 || damageDealt <= 0) return;

        const healAmount = Math.floor(damageDealt * lifesteal);
        if (healAmount > 0) {
            const healed = gameState.healHp(healAmount);
            if (healed > 0) {
                logger.debug(`[COMBAT] Lifesteal healed ${healed} HP`);
            }
        }
    }

    /**
     * Process equipment hit counter effects (Smoldering Strikes, Eclipse Slash)
     * Called after each successful player hit
     * @param {number} baseDamage - The base damage of the hit
     * @returns {number} Bonus damage from weapon procs
     */
    processWeaponHitEffects(baseDamage) {
        const effects = this.getEquippedEffects();
        if (!effects.weaponEffect) return 0;

        this.equipmentState.hitCounter++;
        let bonusDamage = 0;

        // Smoldering Strikes (Ashen Greatsword) — every 4th hit +15% fire damage
        if (effects.weaponEffect.type === 'smolderingStrikes') {
            if (this.equipmentState.hitCounter % effects.weaponEffect.hitInterval === 0) {
                bonusDamage = Math.floor(baseDamage * effects.weaponEffect.bonusDamagePercent);
                logger.debug(`[COMBAT] Smoldering Strikes! +${bonusDamage} fire damage`);
            }
        }

        // Eclipse Slash (Eclipse Edge) — every 3rd hit releases wave at 40% attack
        if (effects.weaponEffect.type === 'eclipseSlash') {
            if (this.equipmentState.hitCounter % effects.weaponEffect.hitInterval === 0) {
                bonusDamage = Math.floor(baseDamage * effects.weaponEffect.bonusDamagePercent);
                logger.debug(`[COMBAT] Eclipse Slash! Wave deals ${bonusDamage} damage`);
            }
        }

        return bonusDamage;
    }

    /**
     * Process Searing Arrows ignite DoT (Cindered Shortbow)
     * @param {number} baseDamage - Damage of the hit
     */
    applySearingArrowsDot(baseDamage) {
        const effects = this.getEquippedEffects();
        if (effects.weaponEffect?.type !== 'searingArrows') return;
        if (Math.random() >= effects.weaponEffect.procChance) return;

        const now = Date.now();
        this.equipmentState.searingArrowsDot = {
            damage: effects.weaponEffect.dotDamage,
            interval: effects.weaponEffect.dotInterval,
            endTime: now + effects.weaponEffect.dotDuration,
            lastTick: now
        };
        logger.debug(`[COMBAT] Searing Arrows! Target ignited`);
    }

    /**
     * Process Searing Arrows DoT tick damage
     * @returns {number} DoT damage dealt this tick
     */
    processSearingArrowsDot() {
        const dot = this.equipmentState.searingArrowsDot;
        if (!dot) return 0;

        const now = Date.now();

        // Check expiry
        if (now >= dot.endTime) {
            this.equipmentState.searingArrowsDot = null;
            return 0;
        }

        // Check tick interval
        if (now - dot.lastTick < dot.interval) return 0;

        dot.lastTick = now;

        // Apply Scorched Talisman DoT bonus
        let damage = dot.damage;
        const effects = this.getEquippedEffects();
        if (effects.pendantEffect?.type === 'dotDamageBonus') {
            damage = Math.floor(damage * (1 + effects.pendantEffect.value));
        }

        return damage;
    }

    /**
     * Apply Smoldering Resonance fire DoT on spell cast (Emberheart Focus offhand)
     * 25% chance to apply fire DoT regardless of spell element
     */
    applySmolderingResonance() {
        const effects = this.getEquippedEffects();
        if (effects.shieldEffect?.type !== 'smolderingResonance') return;
        if (Math.random() >= effects.shieldEffect.procChance) return;

        const now = Date.now();
        const totalDuration = effects.shieldEffect.dotTicks * effects.shieldEffect.dotInterval;
        this.equipmentState.smolderingDot = {
            damage: effects.shieldEffect.dotDamage,
            interval: effects.shieldEffect.dotInterval,
            endTime: now + totalDuration,
            lastTick: now
        };
        logger.debug(`[COMBAT] Smoldering Resonance! Fire DoT applied`);
    }

    /**
     * Process Smoldering Resonance DoT tick damage (Emberheart Focus)
     * @returns {number} DoT damage dealt this tick
     */
    processSmolderingDot() {
        const dot = this.equipmentState.smolderingDot;
        if (!dot) return 0;

        const now = Date.now();

        // Check expiry
        if (now >= dot.endTime) {
            this.equipmentState.smolderingDot = null;
            return 0;
        }

        // Check tick interval
        if (now - dot.lastTick < dot.interval) return 0;

        dot.lastTick = now;

        // Apply Scorched Talisman DoT bonus
        let damage = dot.damage;
        const effects = this.getEquippedEffects();
        if (effects.pendantEffect?.type === 'dotDamageBonus') {
            damage = Math.floor(damage * (1 + effects.pendantEffect.value));
        }

        return damage;
    }

    /**
     * Process Volatile Arrows bonus fire damage (Cindered Quiver)
     * @param {number} baseDamage - Damage of the hit
     * @param {boolean} isCrit - Whether this was a critical hit
     * @returns {number} Bonus fire damage
     */
    processVolatileArrows(baseDamage, isCrit) {
        const effects = this.getEquippedEffects();
        if (!effects.quiverEffect || effects.quiverEffect.type !== 'volatileArrows') return 0;

        const bonusPercent = isCrit ? effects.quiverEffect.critBonusDamage : effects.quiverEffect.baseBonusDamage;
        return Math.floor(baseDamage * bonusPercent);
    }

    /**
     * Process on-hit effects when mob hits the player
     * @param {number} damageDealt - Damage the mob dealt
     * @param {boolean} blocked - Whether the attack was blocked
     * @returns {{ counterDamage: number, reflectDamage: number, healed: number }}
     */
    processOnHitDefensiveEffects(damageDealt, blocked) {
        const effects = this.getEquippedEffects();
        const now = Date.now();
        let counterDamage = 0;
        let reflectDamage = 0;
        let healed = 0;

        // Searing Parry (Pyreguard Blade) — 12% chance to counter for 50% of attack
        if (effects.weaponEffect?.type === 'searingParry') {
            if (Math.random() < effects.weaponEffect.procChance) {
                const stats = this.getPlayerStats();
                counterDamage = Math.floor(stats.attack * effects.weaponEffect.counterDamagePercent);
                logger.debug(`[COMBAT] Searing Parry! Counter for ${counterDamage} fire damage`);
            }
        }

        // Ember Shield (Warden's Ember Ring) — 10% chance to gain +8 defense for 5s
        if (effects.ringEffect?.type === 'emberShield') {
            if (Math.random() < effects.ringEffect.procChance) {
                this.equipmentState.emberShieldBuff = {
                    defenseBonus: effects.ringEffect.defenseBonus,
                    endTime: now + effects.ringEffect.duration
                };
                logger.debug(`[COMBAT] Ember Shield! +${effects.ringEffect.defenseBonus} defense for ${effects.ringEffect.duration / 1000}s`);
            }
        }

        // Warden's Resolve (Ashen Bulwark) — 8% chance to heal 5% max HP
        if (effects.shieldEffect?.type === 'wardensResolve') {
            if (Math.random() < effects.shieldEffect.procChance) {
                const maxHp = gameState.getMaxHp();
                const healAmount = Math.floor(maxHp * effects.shieldEffect.healPercent);
                healed = gameState.healHp(healAmount);
                if (healed > 0) {
                    logger.debug(`[COMBAT] Warden's Resolve! Healed ${healed} HP`);
                }
            }
        }

        // Eclipse Reflection (Eclipse Aegis) — 10% of damage taken reflected
        if (effects.shieldEffect?.type === 'eclipseReflection') {
            reflectDamage = Math.floor(damageDealt * effects.shieldEffect.reflectPercent);
            if (reflectDamage > 0) {
                logger.debug(`[COMBAT] Eclipse Reflection! Reflected ${reflectDamage} damage`);
            }
        }

        // Relentless (Eclipse Wrath Band) — crits tracked separately in damage calc
        // Marked Prey (Eclipse Precision Band) — crits tracked separately in damage calc

        return { counterDamage, reflectDamage, healed };
    }

    /**
     * Process crit-triggered ring effects (Relentless speed, Marked Prey)
     */
    processRingCritEffects() {
        const effects = this.getEquippedEffects();
        const now = Date.now();

        // Relentless (Eclipse Wrath Band) — +3% speed per stack for 5s, max 5 stacks
        if (effects.ringEffect?.type === 'relentless') {
            this.equipmentState.relentlessStacks.push({
                expireTime: now + effects.ringEffect.stackDuration
            });
            // Cap at max stacks
            while (this.equipmentState.relentlessStacks.length > effects.ringEffect.maxStacks) {
                this.equipmentState.relentlessStacks.shift();
            }
            logger.debug(`[COMBAT] Relentless! ${this.equipmentState.relentlessStacks.length}/${effects.ringEffect.maxStacks} speed stacks`);
        }

        // Marked Prey (Eclipse Precision Band) — mark target for +12% damage for 8s
        if (effects.ringEffect?.type === 'markedPrey') {
            this.equipmentState.markedPrey = {
                endTime: now + effects.ringEffect.markDuration
            };
            logger.debug(`[COMBAT] Marked Prey! Target marked for ${effects.ringEffect.markDuration / 1000}s`);
        }
    }

    /**
     * Get active Relentless speed bonus (percentage reduction to attack interval)
     * @returns {number} Speed bonus as fraction (e.g., 0.09 = 9%)
     */
    getRelentlessSpeedBonus() {
        const now = Date.now();
        // Prune expired stacks
        this.equipmentState.relentlessStacks = this.equipmentState.relentlessStacks.filter(
            s => now < s.expireTime
        );
        const effects = this.getEquippedEffects();
        if (effects.ringEffect?.type !== 'relentless') return 0;

        const stackCount = this.equipmentState.relentlessStacks.length;
        return stackCount * (effects.ringEffect.speedBonusPerStack / 100);
    }

    /**
     * Check if Marked Prey is active on current target
     * @returns {number} Damage amplification multiplier (e.g., 0.12 = +12%)
     */
    getMarkedPreyBonus() {
        if (!this.equipmentState.markedPrey) return 0;
        const now = Date.now();
        if (now >= this.equipmentState.markedPrey.endTime) {
            this.equipmentState.markedPrey = null;
            return 0;
        }
        const effects = this.getEquippedEffects();
        if (effects.ringEffect?.type !== 'markedPrey') return 0;
        return effects.ringEffect.damageAmplification || 0;
    }

    /**
     * Get Ember Shield defense bonus (temporary from ring proc)
     * @returns {number} Defense bonus (0 if expired/inactive)
     */
    getEmberShieldDefenseBonus() {
        if (!this.equipmentState.emberShieldBuff) return 0;
        const now = Date.now();
        if (now >= this.equipmentState.emberShieldBuff.endTime) {
            this.equipmentState.emberShieldBuff = null;
            return 0;
        }
        return this.equipmentState.emberShieldBuff.defenseBonus;
    }

    /**
     * Get Arcane Overflow damage bonus (Eclipse Arcana Band)
     * +10% all damage while spell effect active, +8% spell damage with 2+ effects
     * @returns {{ allDamageBonus: number, spellDamageBonus: number }}
     */
    getArcaneOverflowBonus() {
        const effects = this.getEquippedEffects();
        if (effects.ringEffect?.type !== 'arcaneOverflow') return { allDamageBonus: 0, spellDamageBonus: 0 };

        // Count active spell effects
        let activeCount = 0;
        if (this.spellEffects.dot) activeCount++;
        if (this.spellEffects.slow) activeCount++;
        if (this.spellEffects.defense) activeCount++;
        if (this.spellEffects.crit) activeCount++;

        let allDamageBonus = 0;
        let spellDamageBonus = 0;

        if (activeCount >= 1) {
            allDamageBonus = effects.ringEffect.baseDamageBonus || 0;
        }
        if (activeCount >= 2) {
            spellDamageBonus = effects.ringEffect.multiEffectSpellBonus || 0;
        }

        return { allDamageBonus, spellDamageBonus };
    }

    /**
     * Get food healing bonus from equipment (Flame Barrier ring)
     * @returns {number} Heal bonus fraction (e.g., 0.20 = +20%)
     */
    getEquipmentFoodHealBonus() {
        const effects = this.getEquippedEffects();
        if (effects.ringEffect?.type === 'flameBarrier') {
            return effects.ringEffect.foodHealBonus || 0;
        }
        return 0;
    }

    // ==================== SET BONUS SYSTEM ====================

    /**
     * Detect active set bonuses from equipped awakened set armor
     * Scans all 6 armor slots for items with setId + awakened: true
     * @returns {{ [setId]: { count: number, activeTiers: number[] } }}
     */
    getActiveSetBonuses() {
        // Use cached value if available (refreshed on combat start / gear change)
        if (this.setBonusState.cachedBonuses) return this.setBonusState.cachedBonuses;

        const gear = gameState.getEquippedGear();
        const setCounts = {};
        const armorSlots = ['head', 'shoulders', 'hands', 'body', 'legs', 'feet'];

        for (const slot of armorSlots) {
            if (!gear[slot]) continue;
            const item = getItem(gear[slot]);
            if (item?.setId && item?.awakened) {
                if (!setCounts[item.setId]) setCounts[item.setId] = 0;
                setCounts[item.setId]++;
            }
        }

        // Build result with active bonus tiers
        const result = {};
        for (const [setId, count] of Object.entries(setCounts)) {
            const activeTiers = [];
            if (count >= 2) activeTiers.push(2);
            if (count >= 4) activeTiers.push(4);
            if (count >= 6) activeTiers.push(6);
            if (activeTiers.length > 0) {
                result[setId] = { count, activeTiers };
            }
        }

        this.setBonusState.cachedBonuses = result;
        return result;
    }

    /**
     * Check if a specific set bonus tier is active
     * @param {string} setId - 'ashforged', 'cindermark', 'pyreweave'
     * @param {number} tier - 2, 4, or 6
     * @returns {boolean}
     */
    hasSetBonus(setId, tier) {
        const bonuses = this.getActiveSetBonuses();
        return bonuses[setId]?.activeTiers?.includes(tier) || false;
    }

    /**
     * Clear set bonus cache (call when gear changes)
     */
    invalidateSetBonusCache() {
        this.setBonusState.cachedBonuses = null;
    }

    /**
     * Clear all set bonus state (on stop fighting, death)
     */
    clearSetBonusState() {
        this.setBonusState.momentum = 0;
        this.setBonusState.afterimage = null;
        this.setBonusState.riposteCounter = 0;
        this.setBonusState.rangedHitCounter = 0;
        this.setBonusState.phantomShots = null;
        this.setBonusState.spellFusion.shatterReady = false;
        this.setBonusState.spellFusion.shatterCooldownEnd = 0;
        this.setBonusState.spellFusion.harmonyRefreshTime = 0;
        this.setBonusState.cachedBonuses = null;
    }

    /**
     * Clear target-specific set bonus state (on mob kill — keep player buffs)
     */
    clearTargetSetBonusState() {
        // Momentum resets on target change per spec
        this.setBonusState.momentum = 0;
        this.setBonusState.rangedHitCounter = 0;
        this.setBonusState.phantomShots = null;
        this.setBonusState.spellFusion.shatterReady = false;
        this.setBonusState.spellFusion.shatterCooldownEnd = 0;
    }

    // --- Ashforged 2pc: Momentum ---

    /**
     * Process a melee hit for Momentum stacking (Ashforged 2pc)
     * Call after each successful melee hit
     */
    processMomentumHit() {
        if (!this.hasSetBonus('ashforged', 2)) return;
        const maxStacks = SET_BONUSES.ashforged.bonuses[2].maxStacks;
        if (this.setBonusState.momentum < maxStacks) {
            this.setBonusState.momentum++;
        }
    }

    /**
     * Get Momentum damage bonus (Ashforged 2pc)
     * @returns {number} Damage multiplier bonus (e.g., 0.20 = +20% at 10 stacks)
     */
    getMomentumDamageBonus() {
        if (!this.hasSetBonus('ashforged', 2)) return 0;
        const bonus = SET_BONUSES.ashforged.bonuses[2];
        return this.setBonusState.momentum * bonus.damagePerStack;
    }

    // --- Ashforged 4pc: Devastating Blow ---

    /**
     * Check and apply Devastating Blow (Ashforged 4pc)
     * At max Momentum, 20% chance for 4x damage + 50% lifesteal
     * @param {number} baseDamage - The base damage before Devastating Blow
     * @returns {{ triggered: boolean, damage: number, healed: number }}
     */
    processDevastatingBlow(baseDamage) {
        if (!this.hasSetBonus('ashforged', 4)) return { triggered: false, damage: baseDamage, healed: 0 };

        const bonus = SET_BONUSES.ashforged.bonuses[4];
        const maxStacks = SET_BONUSES.ashforged.bonuses[2].maxStacks;

        // Only procs at max Momentum
        if (this.setBonusState.momentum < maxStacks) return { triggered: false, damage: baseDamage, healed: 0 };

        if (Math.random() < bonus.procChance) {
            const devastatingDamage = Math.floor(baseDamage * bonus.damageMultiplier);
            const healAmount = Math.floor(devastatingDamage * bonus.lifestealPercent);
            gameState.healHp(healAmount);
            logger.debug(`[SET BONUS] Devastating Blow! ${devastatingDamage} damage, healed ${healAmount}`);
            return { triggered: true, damage: devastatingDamage, healed: healAmount };
        }

        return { triggered: false, damage: baseDamage, healed: 0 };
    }

    // --- Ashforged 6pc: Weapon Mastery ---

    /**
     * Process Afterimage clone attacks (Ashforged 6pc, 2H weapon)
     * Called each tick while afterimage is active
     * @param {number} playerAttackSpeed - Current attack speed in ms
     * @returns {number} Damage dealt by afterimage this tick
     */
    processAfterimage(playerAttackSpeed) {
        if (!this.hasSetBonus('ashforged', 6)) return 0;

        const gear = gameState.getEquippedGear();
        const weapon = gear.weapon ? getItem(gear.weapon) : null;
        if (!weapon?.twoHanded) return 0;

        const now = Date.now();
        const bonus = SET_BONUSES.ashforged.bonuses[6].afterimage;

        // Check cooldown — activate afterimage when at max Momentum and off cooldown
        if (!this.setBonusState.afterimage) {
            const maxStacks = SET_BONUSES.ashforged.bonuses[2].maxStacks;
            if (this.setBonusState.momentum >= maxStacks &&
                now > (this.setBonusState.afterimage?.cooldownEnd || 0)) {
                this.setBonusState.afterimage = {
                    endTime: now + bonus.duration,
                    nextAttack: now + playerAttackSpeed,
                    cooldownEnd: now + bonus.duration + bonus.cooldown
                };
                logger.debug('[SET BONUS] Afterimage activated!');
            }
            return 0;
        }

        // Check if afterimage expired
        if (now >= this.setBonusState.afterimage.endTime) {
            const cooldownEnd = this.setBonusState.afterimage.cooldownEnd;
            this.setBonusState.afterimage = { endTime: 0, nextAttack: 0, cooldownEnd };
            return 0;
        }

        // Clone attacks at same speed as player, dealing 50% damage
        if (now >= this.setBonusState.afterimage.nextAttack) {
            this.setBonusState.afterimage.nextAttack = now + playerAttackSpeed;
            const cloneDamage = Math.floor(this.calculatePlayerDamage() * bonus.damagePercent);
            // Clone hits can build Momentum
            this.processMomentumHit();
            logger.debug(`[SET BONUS] Afterimage attack: ${cloneDamage} damage`);
            return cloneDamage;
        }

        return 0;
    }

    /**
     * Process Riposte counter (Ashforged 6pc, 1H weapon)
     * Called when player takes damage
     * @returns {number} Counter damage dealt (0 if no riposte)
     */
    processRiposteCounter() {
        if (!this.hasSetBonus('ashforged', 6)) return 0;

        const gear = gameState.getEquippedGear();
        const weapon = gear.weapon ? getItem(gear.weapon) : null;
        // Only works with 1H weapons (not twoHanded)
        if (!weapon || weapon.twoHanded) return 0;

        this.setBonusState.riposteCounter++;
        const bonus = SET_BONUSES.ashforged.bonuses[6].riposte;

        if (this.setBonusState.riposteCounter >= bonus.hitInterval) {
            this.setBonusState.riposteCounter = 0;
            const counterDamage = Math.floor(this.calculatePlayerDamage() * bonus.damagePercent);
            // Riposte can build Momentum
            this.processMomentumHit();
            logger.debug(`[SET BONUS] Riposte counter: ${counterDamage} damage`);
            return counterDamage;
        }

        return 0;
    }

    // --- Cindermark 2pc: Bleed Acceleration ---

    /**
     * Get bleed tick interval considering Cindermark 2pc
     * @returns {number} Tick interval in ms (1000 base, 500 with 2pc)
     */
    getBleedTickInterval() {
        if (this.hasSetBonus('cindermark', 2)) {
            return 500; // Ticks 2x as fast
        }
        return 1000; // Default 1s interval
    }

    // --- Cindermark 4pc: Piercing Shot ---

    /**
     * Process a ranged hit for Piercing Shot counter (Cindermark 4pc)
     * @returns {{ triggered: boolean, damage: number }}
     */
    processPiercingShot() {
        if (!this.hasSetBonus('cindermark', 4)) return { triggered: false, damage: 0 };

        this.setBonusState.rangedHitCounter++;
        const bonus = SET_BONUSES.cindermark.bonuses[4];

        if (this.setBonusState.rangedHitCounter >= bonus.hitInterval) {
            this.setBonusState.rangedHitCounter = 0;

            const combat = gameState.getCombat();
            const mob = combat.currentMob;
            if (!mob) return { triggered: false, damage: 0 };

            // Calculate max hit with damage multiplier
            const stats = this.getPlayerStats();
            let maxHit = Math.floor(stats.attack * bonus.damageMultiplier);

            // Apply partial defense (ignores 50% of mob defense) with new formula
            const defenseIgnore = bonus.defenseIgnorePercent || 0.50;
            const effectiveDefense = Math.floor(mob.defense * (1 - defenseIgnore));
            const reduction = effectiveDefense / (effectiveDefense + 50);
            maxHit = Math.max(1, Math.floor(maxHit * (1 - reduction)));

            // Roll 1 to maxHit
            let piercingDamage = 1 + Math.floor(Math.random() * maxHit);

            logger.debug(`[SET BONUS] Piercing Shot! ${piercingDamage} damage (ignores ${defenseIgnore * 100}% defense)`);

            // Trigger Phantom Archer if 6pc active
            this.triggerPhantomArcher(stats.attack);

            return { triggered: true, damage: piercingDamage };
        }

        return { triggered: false, damage: 0 };
    }

    // --- Cindermark 6pc: Phantom Archer ---

    /**
     * Trigger Phantom Archer shots after a Piercing Shot (Cindermark 6pc)
     * @param {number} baseDamage - Player base attack for calculating phantom damage
     */
    triggerPhantomArcher(baseDamage) {
        if (!this.hasSetBonus('cindermark', 6)) return;

        const bonus = SET_BONUSES.cindermark.bonuses[6];
        const shotInterval = bonus.shotDuration / bonus.shotCount; // ~667ms between shots

        this.setBonusState.phantomShots = {
            remaining: bonus.shotCount,
            nextShotTime: Date.now() + shotInterval,
            baseDamage: baseDamage,
            shotInterval: shotInterval
        };

        logger.debug(`[SET BONUS] Phantom Archer triggered! ${bonus.shotCount} shots queued`);
    }

    /**
     * Process queued Phantom Archer shots (called each tick)
     * @returns {number} Damage dealt by phantom shots this tick
     */
    processPhantomArcherShots() {
        const phantom = this.setBonusState.phantomShots;
        if (!phantom || phantom.remaining <= 0) return 0;

        const now = Date.now();
        if (now < phantom.nextShotTime) return 0;

        phantom.remaining--;
        phantom.nextShotTime = now + phantom.shotInterval;

        const bonus = SET_BONUSES.cindermark.bonuses[6];
        // Calculate max hit for phantom shot
        const maxHit = Math.max(1, Math.floor(phantom.baseDamage * bonus.shotDamagePercent));
        // Roll 1 to maxHit
        const shotDamage = 1 + Math.floor(Math.random() * maxHit);

        // Phantom shots can proc bleeds
        this.applyBleedToMob('ranged');

        if (phantom.remaining <= 0) {
            this.setBonusState.phantomShots = null;
        }

        logger.debug(`[SET BONUS] Phantom shot: ${shotDamage} damage (${phantom.remaining} remaining)`);
        return shotDamage;
    }

    // --- Pyreweave 2pc: Spell Echo ---

    /**
     * Process set bonus Spell Echo (Pyreweave 2pc) — 25% chance to recast at 75% power
     * @param {Object} spell - The spell just cast
     * @returns {{ triggered: boolean, damage: number }}
     */
    processSetSpellEcho(spell) {
        if (!this.hasSetBonus('pyreweave', 2)) return { triggered: false, damage: 0 };

        const bonus = SET_BONUSES.pyreweave.bonuses[2];
        if (Math.random() < bonus.echoChance) {
            const fullDamage = this.calculateMagicDamage(spell);
            const echoDamage = Math.floor(fullDamage * bonus.echoPowerPercent);

            // Echo applies spell effect too
            if (spell.effect) this.applySpellEffect(spell);

            logger.debug(`[SET BONUS] Spell Echo! ${spell.name} recast for ${echoDamage} damage`);

            // Pyreweave 4pc: echoed spells can trigger Elemental Cascade
            let cascadeDamage = 0;
            if (this.hasSetBonus('pyreweave', 4)) {
                const cascadeChance = getTalentProcChance(SKILLS.COMBAT, 'elementalCascade');
                if (cascadeChance > 0 && Math.random() < cascadeChance) {
                    const cascadeResult = this.castEnhancedCascade(spell);
                    if (cascadeResult.success) {
                        cascadeDamage = cascadeResult.damage;
                    }
                }
            }

            return { triggered: true, damage: echoDamage + cascadeDamage };
        }

        return { triggered: false, damage: 0 };
    }

    // --- Pyreweave 4pc: Arcane Cascade (Enhanced) ---

    /**
     * Cast enhanced Elemental Cascade with 3rd spell at 50% power (Pyreweave 4pc)
     * Normal cascade casts 1 extra spell at 50%; enhanced adds a 2nd extra at 50%
     * @param {Object} originalSpell - The spell that triggered cascade
     * @returns {{ success: boolean, damage: number }}
     */
    castEnhancedCascade(originalSpell) {
        // Cast the normal cascade first
        const normalCascade = this.castElementalCascade(originalSpell);
        if (!normalCascade.success) return { success: false, damage: 0 };

        let totalDamage = normalCascade.damage;

        // 4pc: fire a 3rd spell of a different element at 50% power
        const elements = ['fire', 'ice', 'earth', 'air'];
        const usedElements = [originalSpell.element];
        if (normalCascade.spell) usedElements.push(normalCascade.spell.element);

        const available = elements.filter(e => !usedElements.includes(e));
        if (available.length > 0) {
            const thirdElement = available[Math.floor(Math.random() * available.length)];
            const thirdSpellId = `${thirdElement}_bolt`;
            const thirdSpell = getSpell(thirdSpellId);
            if (thirdSpell) {
                const bonus = SET_BONUSES.pyreweave.bonuses[4];
                const fullDmg = this.calculateMagicDamage(thirdSpell);
                const thirdDamage = Math.floor(fullDmg * bonus.thirdSpellPower);
                this.applySpellEffect(thirdSpell);
                totalDamage += thirdDamage;
                logger.debug(`[SET BONUS] Arcane Cascade 3rd spell! ${thirdSpell.name} for ${thirdDamage} damage`);
            }
        }

        return { success: true, damage: totalDamage };
    }

    // --- Pyreweave 6pc: Spell Fusion ---

    /**
     * Get currently active spell fusions (Pyreweave 6pc)
     * Checks which spell effects are simultaneously active and returns matching fusions
     * @returns {Object[]} Array of active fusion objects
     */
    getActiveSpellFusions() {
        if (!this.hasSetBonus('pyreweave', 6)) return [];

        const fusions = SET_BONUSES.pyreweave.bonuses[6].fusions;
        const active = [];

        // Map spell effect slots to element keys used by fusions
        // dot = fire, slow = ice, defense = earth, crit = air
        const activeElements = new Set();
        if (this.spellEffects.dot) activeElements.add('dot');
        if (this.spellEffects.slow) activeElements.add('slow');
        if (this.spellEffects.defense) activeElements.add('defense');
        if (this.spellEffects.crit) activeElements.add('crit');

        for (const [fusionId, fusion] of Object.entries(fusions)) {
            const hasAll = fusion.elements.every(e => activeElements.has(e));
            if (hasAll) {
                active.push({ id: fusionId, ...fusion });
            }
        }

        return active;
    }

    /**
     * Get Spell Fusion combat modifiers (Pyreweave 6pc)
     * Returns aggregate bonuses from all active fusions
     * @returns {{ shatterReady: boolean, mobDefenseReduction: number, dotTickSpeedBonus: number, dotDamageBonus: number, mobAttackSpeedReduction: number, allDamageAmp: number, buffStrengthBonus: number }}
     */
    getSpellFusionModifiers() {
        const mods = {
            shatterReady: false,
            mobDefenseReduction: 0,
            dotTickSpeedBonus: 0,
            dotDamageBonus: 0,
            mobAttackSpeedReduction: 0,
            allDamageAmp: 0,
            buffStrengthBonus: 0
        };

        const fusions = this.getActiveSpellFusions();
        if (fusions.length === 0) return mods;

        const now = Date.now();

        for (const fusion of fusions) {
            switch (fusion.effect) {
                case 'nextHit2x': // Shatter (fire+ice)
                    if (this.setBonusState.spellFusion.shatterReady) {
                        mods.shatterReady = true;
                    } else if (now > this.setBonusState.spellFusion.shatterCooldownEnd) {
                        // Arm Shatter
                        this.setBonusState.spellFusion.shatterReady = true;
                        mods.shatterReady = true;
                    }
                    break;
                case 'mobDefenseReduction': // Magma (fire+earth)
                    mods.mobDefenseReduction = fusion.value;
                    break;
                case 'dotEnhance': // Firestorm (fire+air)
                    mods.dotTickSpeedBonus = fusion.tickSpeedBonus;
                    mods.dotDamageBonus = fusion.damageBonus;
                    break;
                case 'deepFreeze': // Permafrost (ice+earth)
                    mods.mobAttackSpeedReduction = fusion.attackSpeedReduction;
                    break;
                case 'damageAmp': // Hailstorm (ice+air)
                    mods.allDamageAmp = fusion.value;
                    break;
                case 'buffEnhance': // Harmony (earth+air)
                    mods.buffStrengthBonus = fusion.buffStrengthBonus;
                    break;
            }
        }

        return mods;
    }

    /**
     * Process Shatter consumption (Pyreweave 6pc — fire+ice)
     * Call after a hit deals damage; if Shatter is ready, double the damage
     * @param {number} damage - Base damage
     * @returns {number} Modified damage (doubled if Shatter consumed)
     */
    processShatter(damage) {
        if (!this.setBonusState.spellFusion.shatterReady) return damage;

        this.setBonusState.spellFusion.shatterReady = false;
        this.setBonusState.spellFusion.shatterCooldownEnd = Date.now() + 3000;
        logger.debug(`[SET BONUS] Shatter! 2x damage`);
        return damage * 2;
    }

    /**
     * Process Harmony auto-refresh (Pyreweave 6pc — earth+air)
     * Auto-refreshes defense and crit buffs every 5s when both are active
     */
    processHarmonyRefresh() {
        const fusions = this.getActiveSpellFusions();
        const harmony = fusions.find(f => f.effect === 'buffEnhance');
        if (!harmony) return;

        const now = Date.now();
        if (now < this.setBonusState.spellFusion.harmonyRefreshTime) return;

        this.setBonusState.spellFusion.harmonyRefreshTime = now + harmony.autoRefreshInterval;

        // Refresh defense and crit buffs (extend their duration)
        const refreshDuration = 8000; // Refresh to 8s remaining
        if (this.spellEffects.defense) {
            this.spellEffects.defense.endTime = Math.max(this.spellEffects.defense.endTime, now + refreshDuration);
        }
        if (this.spellEffects.crit) {
            this.spellEffects.crit.endTime = Math.max(this.spellEffects.crit.endTime, now + refreshDuration);
        }
    }

    // ==================== PREVIEW SYSTEM ====================

    /**
     * Select a zone for preview (shows random mob from zone)
     * @param {string} zoneId - Zone ID
     * @returns {{ success: boolean, reason?: string }}
     */
    selectZone(zoneId, tier = null) {
        const zone = getZone(zoneId);
        if (!zone) {
            return { success: false, reason: 'Zone not found' };
        }

        // Check level requirement
        const playerLevel = this.getLevel();
        if (playerLevel < zone.level) {
            return { success: false, reason: `Requires Combat level ${zone.level}` };
        }

        // For tiered zones, check tier unlock
        if (zone.tiers && tier) {
            const tierData = zone.tiers[tier];
            if (!tierData) {
                return { success: false, reason: 'Invalid tier' };
            }
            const unlocks = gameState.player.quests?.unlocks || [];
            if (!unlocks.includes(tierData.unlock)) {
                return { success: false, reason: `Tier not unlocked. Complete the required quest.` };
            }
        }

        // Pick a random mob from the zone (or tier) for preview
        const mobs = getZoneMobs(zoneId, tier);
        if (mobs.length === 0) {
            return { success: false, reason: 'No mobs available' };
        }
        const mobId = mobs[Math.floor(Math.random() * mobs.length)];
        const mob = getMob(mobId);

        this.previewZone = zoneId;
        this.previewMob = mobId;
        this.previewTier = tier;
        this.isExplicitTarget = false; // Random preview, not explicit target

        logger.debug(`[COMBAT] Preview zone: ${zone.name}${tier ? ` (${tier})` : ''}, mob: ${mob?.name}`);
        return { success: true };
    }

    /**
     * Select a specific mob for preview (target farming)
     * @param {string} zoneId - Zone ID
     * @param {string} mobId - Mob ID
     * @returns {{ success: boolean, reason?: string }}
     */
    selectMob(zoneId, mobId, tier = null) {
        const zone = getZone(zoneId);
        if (!zone) {
            return { success: false, reason: 'Zone not found' };
        }

        // Check if zone is cleared for target farming
        if (!gameState.isZoneCleared(zoneId)) {
            return { success: false, reason: 'Clear zone first to target specific mobs' };
        }

        // Check level requirement
        const playerLevel = this.getLevel();
        if (playerLevel < zone.level) {
            return { success: false, reason: `Requires Combat level ${zone.level}` };
        }

        // Verify mob exists in zone (supports tiered zones via getZoneMobs)
        const zoneMobs = getZoneMobs(zoneId, tier);
        if (!zoneMobs.includes(mobId)) {
            return { success: false, reason: 'Mob not found in zone' };
        }

        // For tiered zones, auto-detect tier from mob if not provided
        if (zone.tiers && !tier) {
            for (const [tId, tData] of Object.entries(zone.tiers)) {
                if (tData.mobs && tData.mobs.includes(mobId)) {
                    tier = tId;
                    break;
                }
            }
        }

        const mob = getMob(mobId);
        this.previewZone = zoneId;
        this.previewMob = mobId;
        this.previewTier = tier;
        this.isExplicitTarget = true; // User explicitly selected this mob

        logger.debug(`[COMBAT] Preview target: ${zone.name}${tier ? ` (${tier})` : ''} - ${mob?.name}`);
        return { success: true };
    }

    /**
     * Select a specific boss in a tiered zone for preview (e.g., Ashen Crucible)
     * Does NOT require zone to be cleared since bosses are the main content
     * @param {string} zoneId - Zone ID
     * @param {string} tierId - Tier ID (t1, t2, t3)
     * @param {string} mobId - Boss mob ID
     * @returns {{ success: boolean, reason?: string }}
     */
    selectBoss(zoneId, tierId, mobId) {
        const zone = getZone(zoneId);
        if (!zone || !zone.tiers) {
            return { success: false, reason: 'Zone not found or not a tiered zone' };
        }

        const tierData = zone.tiers[tierId];
        if (!tierData) {
            return { success: false, reason: 'Invalid tier' };
        }

        // Check level requirement
        const playerLevel = this.getLevel();
        if (playerLevel < zone.level) {
            return { success: false, reason: `Requires Combat level ${zone.level}` };
        }

        // Check tier unlock
        const unlocks = gameState.player.quests?.unlocks || [];
        if (!unlocks.includes(tierData.unlock)) {
            return { success: false, reason: 'Tier not unlocked. Complete the required quest.' };
        }

        // Verify boss exists in tier
        const tierMobs = tierData.mobs || [];
        if (!tierMobs.includes(mobId)) {
            return { success: false, reason: 'Boss not found in tier' };
        }

        // Set preview with explicit target
        const mob = getMob(mobId);
        this.previewZone = zoneId;
        this.previewMob = mobId;
        this.previewTier = tierId;
        this.isExplicitTarget = true; // User explicitly selected this boss
        this.isBossTarget = true; // Flag for boss targeting (bypasses zone clear check)

        logger.debug(`[COMBAT] Preview boss target: ${zone.name} ${tierId.toUpperCase()} - ${mob?.name}`);
        return { success: true };
    }

    /**
     * Clear preview state
     */
    clearPreview() {
        this.previewZone = null;
        this.previewMob = null;
        this.previewTier = null;
        this.isExplicitTarget = false;
        this.isBossTarget = false;
    }

    /**
     * Check if currently previewing
     * @returns {boolean}
     */
    isPreviewing() {
        return this.previewZone !== null && this.previewMob !== null;
    }

    /**
     * Get preview state for UI
     * @returns {{ zone: Object, mob: Object, loot: Object, pyrePenalty: Object } | null}
     */
    getPreviewState() {
        if (!this.previewZone || !this.previewMob) return null;

        const zone = getZone(this.previewZone);
        const mob = getMob(this.previewMob);
        const loot = getZoneLoot(this.previewZone);
        const pyrePenalty = this.getPyrePenalty(this.previewZone);
        const isTargetFarming = this.isExplicitTarget; // Based on user selection, not zone clear status

        return {
            zone,
            mob,
            loot,
            pyrePenalty,
            isTargetFarming,
            zoneId: this.previewZone,
            mobId: this.previewMob,
            tier: this.previewTier
        };
    }

    /**
     * Start combat from preview state
     * @returns {{ success: boolean, reason?: string }}
     */
    startFromPreview() {
        if (!this.previewZone) {
            return { success: false, reason: 'No zone selected' };
        }

        const zoneId = this.previewZone;
        const mobId = this.previewMob;
        const tier = this.previewTier;
        const isExplicitTarget = this.isExplicitTarget; // Only target farm if user explicitly chose a mob
        const isBossTarget = this.isBossTarget; // Boss targeting bypasses zone clear check

        // Clear preview
        this.clearPreview();

        // Start combat - only target farm if user clicked a specific mob button
        if (isExplicitTarget && mobId) {
            return this.startFightingMob(zoneId, mobId, tier, isBossTarget);
        } else {
            return this.startFighting(zoneId, tier);
        }
    }

    /**
     * Start fighting a specific mob in a zone (target farming)
     * Requires zone to be cleared first, unless targeting a boss in a tiered zone
     * @param {string} zoneId - Zone ID
     * @param {string} mobId - Specific mob ID to farm
     * @param {string|null} tier - Tier ID for tiered zones
     * @param {boolean} isBossTarget - If true, bypass zone clear check (for tiered zone bosses)
     * @returns {{ success: boolean, reason?: string }}
     */
    startFightingMob(zoneId, mobId, tier = null, isBossTarget = false) {
        // Check if zone is cleared (skip for boss targeting in tiered zones)
        if (!isBossTarget && !gameState.isZoneCleared(zoneId)) {
            return { success: false, reason: 'Clear zone first to target specific mobs' };
        }

        // Check if mob exists in zone (supports tiered zones)
        const zone = getZone(zoneId);
        const zoneMobs = getZoneMobs(zoneId, tier);
        if (!zone || !zoneMobs.includes(mobId)) {
            return { success: false, reason: 'Mob not found in zone' };
        }

        // For tiered zones, auto-detect tier from mob if not provided
        if (zone.tiers && !tier) {
            for (const [tId, tData] of Object.entries(zone.tiers)) {
                if (tData.mobs && tData.mobs.includes(mobId)) {
                    tier = tId;
                    break;
                }
            }
        }

        // Set target mob and start fighting
        this.targetMob = mobId;
        return this.startFighting(zoneId, tier);
    }

    /**
     * Stop target farming (return to random mobs)
     */
    clearTargetMob() {
        this.targetMob = null;
    }

    /**
     * Check if player can fight in a zone
     * @param {string} zoneId - Zone ID
     * @returns {{ success: boolean, reason?: string }}
     */
    canFight(zoneId, tier = null) {
        const zone = getZone(zoneId);
        if (!zone) {
            return { success: false, reason: 'Invalid zone' };
        }

        const level = this.getLevel();
        if (level < zone.level) {
            return { success: false, reason: `Requires Combat level ${zone.level}` };
        }

        if (gameState.isDead()) {
            return { success: false, reason: 'You are dead! Wait for respawn.' };
        }

        // For tiered zones, check tier unlock via quest unlocks
        if (zone.tiers && tier) {
            const tierData = zone.tiers[tier];
            if (!tierData) {
                return { success: false, reason: 'Invalid tier' };
            }
            const unlocks = gameState.player.quests?.unlocks || [];
            if (!unlocks.includes(tierData.unlock)) {
                return { success: false, reason: `Tier locked. Complete the required quest.` };
            }
        }

        return { success: true };
    }

    /**
     * Warn player if current set has no weapon but another set does.
     * Lets players fight unarmed if they want, but alerts them if gear is in wrong set.
     */
    checkEquipmentSetWarning() {
        const gear = gameState.getEquippedGear();
        const currentSet = gameState.getActiveEquipmentSet() || 'melee';

        // If current set has a weapon, no warning needed
        if (gear.weapon) {
            return;
        }

        // Check other sets for weapons
        const sets = gameState.getEquipmentSets();
        const setNames = { melee: 'Melee', ranged: 'Ranged', magic: 'Magic' };

        for (const setId of ['ranged', 'melee', 'magic']) {
            if (setId === currentSet) continue;
            const setGear = sets[setId]?.gear;
            if (setGear?.weapon) {
                toastWarning(`No weapon! Your ${setNames[setId]} set has one equipped.`);
                logger.debug(`[COMBAT] Warning: ${currentSet} set has no weapon, but ${setId} does`);
                return;
            }
        }

        // No weapon in any set
        logger.debug('[COMBAT] No weapon found in any equipment set');
    }

    /**
     * Start fighting in a zone (optionally in a specific tier for tiered zones)
     * @param {string} zoneId - Zone ID
     * @param {string|null} tier - Tier ID for tiered zones (e.g., 't1', 't2', 't3')
     * @returns {{ success: boolean, reason?: string }}
     */
    startFighting(zoneId, tier = null) {
        const canFight = this.canFight(zoneId, tier);
        if (!canFight.success) {
            return canFight;
        }

        // Warn if current set has no weapon but another set does
        this.checkEquipmentSetWarning();

        const combat = gameState.getCombat();
        const zone = getZone(zoneId);

        // Clear any existing action
        gameState.clearAction();

        // Clear Corrupted Prey marks and corruption timer on zone change
        // Prevents exploiting corruption by farming easy zones then switching to bosses
        this.clearCorruptedPrey();
        if (isSkillCorrupted(SKILLS.COMBAT)) {
            deactivateCorruption(SKILLS.COMBAT);
        }

        // Set up combat state
        combat.inCombat = true;
        combat.currentZone = zoneId;
        combat.currentTier = tier;

        // Spawn first mob
        this.spawnNextMob();

        // Get player stats to save with action (for offline progress)
        const playerStats = this.getPlayerStats();
        const selectedSpell = gameState.getSelectedSpell();

        // One-time debug log of all combat stats when combat starts
        const activeSet = gameState.getActiveEquipmentSet() || 'melee';
        const debugGear = gameState.getEquippedGear();
        logger.debug(`[COMBAT START] === STAT DEBUG ===`);
        logger.debug(`[COMBAT START] Active Set: ${activeSet}`);
        logger.debug(`[COMBAT START] Weapon: ${debugGear.weapon || 'NONE'}`);
        logger.debug(`[COMBAT START] Ammo: ${debugGear.ammo || 'none'}`);
        logger.debug(`[COMBAT START] Armor: head=${debugGear.head || 'none'}, body=${debugGear.body || 'none'}, legs=${debugGear.legs || 'none'}`);
        logger.debug(`[COMBAT START] Armor: feet=${debugGear.feet || 'none'}, hands=${debugGear.hands || 'none'}, shoulders=${debugGear.shoulders || 'none'}`);
        logger.debug(`[COMBAT START] Accessories: neck=${debugGear.neck || 'none'}, ring=${debugGear.ring || 'none'}, back=${debugGear.back || 'none'}, offhand=${debugGear.offhand || 'none'}`);
        logger.debug(`[COMBAT START] Final Stats: attack=${playerStats.attack}, defense=${playerStats.defense}, speed=${playerStats.attackSpeed}ms`);
        logger.debug(`[COMBAT START] Style: ${playerStats.attackStyle}, accuracy=${playerStats.accuracy}%, crit=${playerStats.critChance}%`);
        logger.debug(`[COMBAT START] =================`);

        // Get gear IDs and weapon info for offline equipment effect simulation
        const gear = gameState.getEquippedGear();
        const weapon = gear.weapon ? getItem(gear.weapon) : null;

        // Count available ammo/glyphs for offline calculation
        let glyphCount = 0;
        let arrowCount = 0;
        if (playerStats.attackStyle === 'magic' && selectedSpell) {
            const spell = getSpell(selectedSpell);
            if (spell?.glyphCost?.[0]) {
                const glyphId = spell.glyphCost[0].glyphId;
                // Count from glyph pouch
                if (gameState.isGlyphPouchUnlocked?.()) {
                    const pouch = gameState.getGlyphPouch?.();
                    if (pouch?.slots) {
                        for (const slot of pouch.slots) {
                            if (slot?.itemId === glyphId) glyphCount += slot.quantity;
                        }
                    }
                }
                // Count from inventory
                glyphCount += gameState.getItemCount(glyphId);
            }
        } else if (playerStats.attackStyle === 'ranged') {
            const ammo = gameState.getEquippedAmmo?.();
            if (ammo?.itemId) arrowCount = ammo.quantity || 0;
        }

        // Save auto-eat config for offline calculation
        const autoEat = gameState.getAutoEat();
        if (!autoEat.unlocked) gameState.checkAutoEatUnlock();
        const autoEatConfig = {
            enabled: autoEat.unlocked && !!autoEat.storedFood,
            threshold: autoEat.threshold || 50,
            storedFoodId: autoEat.storedFood?.itemId || null,
            storedFoodQty: autoEat.storedFood?.quantity || 0
        };
        // Get heal amount if food is stored (including HoT total for empowered food)
        if (autoEatConfig.storedFoodId) {
            const foodItem = getItem(autoEatConfig.storedFoodId);
            autoEatConfig.storedFoodHeal = foodItem?.healAmount || 0;
            // For empowered food with HoT, calculate total effective healing
            // HoT total = amount per tick * number of ticks
            if (foodItem?.hot) {
                const hotTotal = foodItem.hot.amount * foodItem.hot.ticks;
                autoEatConfig.storedFoodTotalHeal = autoEatConfig.storedFoodHeal + hotTotal;
            } else {
                autoEatConfig.storedFoodTotalHeal = autoEatConfig.storedFoodHeal;
            }
        }

        // Create combat action
        const action = {
            type: 'combat',
            skill: SKILLS.COMBAT,
            name: `Fighting in ${zone.name}`,
            zoneId: zoneId,
            zoneName: zone.name,
            startTime: Date.now(),
            // Save combat stats for offline calculation
            savedStats: {
                attack: playerStats.attack,
                defense: playerStats.defense,
                attackSpeed: playerStats.attackSpeed,
                attackStyle: playerStats.attackStyle,
                accuracy: playerStats.accuracy,
                critChance: playerStats.critChance,
                critDamage: playerStats.critDamage,
                // For magic, also save spell crit bonus (from air spells)
                spellCritBonus: playerStats.attackStyle === 'magic' ? this.getSpellCritBonus() : 0,
                selectedSpell: selectedSpell,
                // Save target mob for offline (if target farming)
                targetMob: this.targetMob,
                // Save tier for tiered zones (offline progress)
                tier: tier,
                // Save resource counts at combat start
                glyphCount: glyphCount,
                arrowCount: arrowCount,
                // Save auto-eat config at combat start
                autoEat: autoEatConfig,
                // Save equipment IDs for offline effect simulation
                equippedWeaponId: gear.weapon || null,
                equippedRingId: gear.ring || null,
                equippedNeckId: gear.neck || null,
                equippedOffhandId: gear.offhand || null,
                equippedBackId: gear.back || null,
                // Save weapon info (needed for set bonus 6pc)
                isTwoHanded: weapon?.twoHanded || false,
                // Save active set bonuses (pre-computed)
                activeSetBonuses: this.getActiveSetBonuses()
            },
            tick: (speedMultiplier = 1) => this.tick(speedMultiplier)
        };

        gameState.setAction(action);
        logger.game(`Started fighting in ${zone.name}`);

        // Auto-drink potions from belt on combat start
        this.drinkAllBeltPotions();

        return { success: true };
    }

    /**
     * Auto-drink all potions from Apothecary Belt that don't have active buffs
     * Called when combat starts
     */
    drinkAllBeltPotions() {
        // Check if Apothecary Belt is unlocked and enabled
        if (!gameState.isApothecaryBeltUnlocked()) return;

        const belt = gameState.getApothecaryBelt();
        if (!belt.enabled) return;

        const activeBuffs = gameState.getPotionBuffs();
        const slots = gameState.getApothecarySlots();

        // Check each belt slot (4 per equipment set)
        for (let i = 0; i < slots.length; i++) {
            const slot = slots[i];
            if (!slot || slot.quantity <= 0) continue;

            const potion = getItem(slot.itemId);
            if (!potion || !potion.effects) continue;

            const category = potion.effects.category;

            // Skip if there's already an active buff for this category
            if (activeBuffs[category]) continue;

            // Consume and apply
            const consumeResult = gameState.consumeApothecaryPotion(category);
            if (!consumeResult.success) continue;

            const effects = potion.effects;
            const buffData = {
                potionId: consumeResult.itemId,
                potionName: potion.name,
                effects: { ...effects },
                combatTimeRemaining: effects.duration,
                originalDuration: effects.duration,
                startedAt: Date.now()
            };

            gameState.setPotionBuff(category, buffData);
            logger.debug(`[COMBAT] Auto-drink on start: ${potion.name} from Apothecary Belt`);
        }
    }

    /**
     * Handle equipment set switch mid-combat - clear non-matching buffs and drink new set's potions
     * Only operates during combat to prevent consuming potions while configuring belt
     */
    onEquipmentSetSwitch() {
        if (!gameState.getCombat().inCombat) return;
        if (!gameState.isApothecaryBeltUnlocked()) return;
        const belt = gameState.getApothecaryBelt();
        if (!belt.enabled) return;

        // Get categories present in the NEW set's belt
        const newSlots = gameState.getApothecarySlots();
        const newCategories = new Set();
        for (const slot of newSlots) {
            if (slot && slot.quantity > 0) {
                const potion = getItem(slot.itemId);
                if (potion?.effects?.category) newCategories.add(potion.effects.category);
            }
        }

        // Clear any active potion buffs NOT in the new set (prevents buff accumulation)
        const activeBuffs = gameState.getPotionBuffs();
        for (const [category] of Object.entries(activeBuffs)) {
            if (!newCategories.has(category)) {
                gameState.removePotionBuff(category);
            }
        }

        // Drink new set's potions for any unfilled categories
        this.drinkAllBeltPotions();
    }

    /**
     * Stop fighting
     */
    stopFighting() {
        const combat = gameState.getCombat();
        combat.inCombat = false;
        combat.currentZone = null;
        combat.currentTier = null;
        combat.currentMob = null;
        combat.mobCurrentHp = 0;
        this.targetMob = null;  // Clear target mob when stopping
        this.clearSpellEffects(); // Clear spell effects when stopping
        this.clearCorruptedPrey(); // Clear Corrupted Prey marks
        if (isSkillCorrupted(SKILLS.COMBAT)) deactivateCorruption(SKILLS.COMBAT);
        this.clearBossState();    // Clear boss mechanic state
        this.clearMobMechanicState(); // Clear mob mechanic effects
        this.clearEquipmentEffectState(); // Clear equipment procs/buffs
        this.clearSetBonusState();        // Clear set bonus state
        gameState.clearAction();
        logger.game('Stopped fighting');
    }

    /**
     * Spawn the next mob in the current zone
     * Uses targetMob if set (for target farming), otherwise random from visible mobs
     */
    spawnNextMob() {
        const combat = gameState.getCombat();
        if (!combat.currentZone) return;

        // Reset talent state for new fight (Last Stand, bleed)
        this.resetTalentState();

        // Clear the killed mob display
        this.lastKilledMob = null;

        // Get player's Pyre level for visible mob filtering
        const pyreLevel = gameState.getSkill(SKILLS.PYRE)?.level || 1;

        let mob;
        // Use target mob if set (zone must be cleared to set target)
        if (this.targetMob) {
            mob = getMob(this.targetMob);
        }
        // Fall back to random visible mob (respects Pyre Veil + tier filtering)
        if (!mob) {
            mob = getRandomVisibleMobForZone(combat.currentZone, pyreLevel, combat.currentTier);
        }
        if (!mob) return;

        combat.currentMob = mob;
        combat.mobCurrentHp = mob.hp;
        combat.lastPlayerAttack = Date.now();
        combat.lastMobAttack = Date.now();

        // Initialize boss mechanics if this is a boss
        this.initBossState(mob);

        logger.debug(`Spawned ${mob.name} (${mob.hp} HP)${this.bossState ? ' [BOSS]' : ''}`);
    }

    // ==================== COMBAT TALENT HELPERS ====================

    /**
     * Reset talent state for new fight
     */
    resetTalentState() {
        this.talentState.lastStandUsed = false;
        this.talentState.mobBleed = null;
        // Keep berserker stacks (they persist between mobs, expire by time)
        // Keep spell echo counter (persists across mobs)
    }

    /**
     * Get total damage multiplier from combat talents (all sources)
     * @param {string} attackStyle - 'melee', 'ranged', or 'magic'
     * @returns {number} Total damage multiplier (1.0 = no bonus)
     */
    getCombatTalentDamageBonus(attackStyle) {
        let bonus = 1.0;

        // Battle Hardened: +5% all damage per rank
        const allDamageBonus = getTalentFlatBonus(SKILLS.COMBAT, 'allDamage');
        if (allDamageBonus > 0) bonus *= (1 + allDamageBonus);

        if (attackStyle === 'melee') {
            // Physical Prowess: +8% melee damage per rank
            // Blade Mastery: +10% melee damage per rank
            const meleeDamageBonus = getTalentFlatBonus(SKILLS.COMBAT, 'meleeDamage');
            if (meleeDamageBonus > 0) bonus *= (1 + meleeDamageBonus);
        } else if (attackStyle === 'ranged') {
            // Archer's Eye: +10% ranged damage per rank
            const rangedDamageBonus = getTalentFlatBonus(SKILLS.COMBAT, 'rangedDamage');
            if (rangedDamageBonus > 0) bonus *= (1 + rangedDamageBonus);
        } else if (attackStyle === 'magic') {
            // Precision Training: +8% spell damage per rank
            // Arcane Affinity: +10% spell damage per rank
            const spellDamageBonus = getTalentFlatBonus(SKILLS.COMBAT, 'spellDamage');
            if (spellDamageBonus > 0) bonus *= (1 + spellDamageBonus);
        }

        // Berserker's Rampage stacks
        const berserkerBonus = this.getBerserkerDamageBonus();
        if (berserkerBonus > 0) bonus *= (1 + berserkerBonus);

        return bonus;
    }

    /**
     * Get Berserker's Rampage damage bonus (stacking buff from kills)
     * @returns {number} Damage bonus (0.0 - 0.5 for 0-5 stacks)
     */
    getBerserkerDamageBonus() {
        const hasBerserker = getTalentFlatBonus(SKILLS.COMBAT, 'berserkerRampage') > 0;
        if (!hasBerserker) return 0;

        const now = Date.now();
        // Filter out expired stacks
        this.talentState.berserkerStacks = this.talentState.berserkerStacks.filter(
            stack => stack.expireTime > now
        );

        // Each stack = +10% damage, max 5 stacks = 50%
        return Math.min(this.talentState.berserkerStacks.length * 0.10, 0.50);
    }

    /**
     * Add Berserker's Rampage stack on kill
     */
    addBerserkerStack() {
        const hasBerserker = getTalentFlatBonus(SKILLS.COMBAT, 'berserkerRampage') > 0;
        if (!hasBerserker) return;

        const now = Date.now();

        // Berserker's Tooth pendant: +10s duration, +2 max stacks
        const effects = this.getEquippedEffects();
        let stackDuration = 30000;
        let maxStacks = 5;
        if (effects.pendantEffect?.type === 'berserkerEnhance') {
            stackDuration += effects.pendantEffect.rampageExtraDuration || 0;
            maxStacks += effects.pendantEffect.rampageExtraStacks || 0;
        }

        // Add new stack
        this.talentState.berserkerStacks.push({ expireTime: now + stackDuration });

        // Cap at max stacks (remove oldest if over)
        while (this.talentState.berserkerStacks.length > maxStacks) {
            this.talentState.berserkerStacks.shift();
        }

        logger.debug(`[COMBAT] Berserker stack added! ${this.talentState.berserkerStacks.length}/${maxStacks} stacks`);
    }

    /**
     * Get execute bonus damage (Executioner talent)
     * @param {number} mobCurrentHp - Mob's current HP
     * @param {number} mobMaxHp - Mob's max HP
     * @returns {number} Bonus multiplier (0.25 = +25% damage)
     */
    getExecuteBonus(mobCurrentHp, mobMaxHp) {
        const executeBonus = getTalentFlatBonus(SKILLS.COMBAT, 'executeBonus');
        if (executeBonus <= 0) return 0;

        // Only applies below 25% HP
        if (mobCurrentHp / mobMaxHp > 0.25) return 0;

        return executeBonus;
    }

    /**
     * Get total defense multiplier from talents
     * @returns {number} Defense multiplier (1.0 = no bonus)
     */
    getCombatTalentDefenseBonus() {
        let bonus = 1.0;

        // Physical Prowess: +8% defense per rank
        // Iron Skin: +5% defense per rank
        const defenseBonus = getTalentFlatBonus(SKILLS.COMBAT, 'defense');
        if (defenseBonus > 0) bonus *= (1 + defenseBonus);

        return bonus;
    }

    /**
     * Get max HP bonus from talents
     * @returns {number} Flat HP bonus
     */
    getCombatTalentMaxHpBonus() {
        // Vital Force: +5 max HP per rank
        return getTalentFlatBonus(SKILLS.COMBAT, 'maxHp') || 0;
    }

    /**
     * Get food healing bonus from talents
     * @returns {number} Healing multiplier (0.5 = +50%)
     */
    getCombatTalentFoodHealingBonus() {
        // Combat Medic: +25% food healing per rank
        return getTalentFlatBonus(SKILLS.COMBAT, 'foodHealing') || 0;
    }

    /**
     * Get accuracy bonus from talents (for ranged)
     * @returns {number} Accuracy bonus percentage
     */
    getCombatTalentAccuracyBonus() {
        let bonus = 0;
        // Precision Training: +8% accuracy per rank
        bonus += getTalentFlatBonus(SKILLS.COMBAT, 'accuracy') || 0;
        // Steady Aim: +10% ranged accuracy per rank (applied separately in getPlayerStats)
        return bonus;
    }

    /**
     * Get attack speed bonus from talents
     * @param {string} attackStyle - 'melee', 'ranged', or 'magic'
     * @returns {number} Speed bonus (0.1 = 10% faster)
     */
    getCombatTalentAttackSpeedBonus(attackStyle) {
        let bonus = 0;

        // Combat Reflexes: -5% attack cooldown per rank (all styles)
        bonus += getTalentFlatBonus(SKILLS.COMBAT, 'attackSpeed') || 0;

        // Relentless Assault: +5% melee attack speed per rank
        if (attackStyle === 'melee') {
            bonus += getTalentFlatBonus(SKILLS.COMBAT, 'meleeSpeed') || 0;
        }

        return bonus;
    }

    /**
     * Get glyph cost reduction from talents
     * @returns {number} Reduction percentage (0.3 = 30% reduction)
     */
    getCombatTalentGlyphCostReduction() {
        // Spell Efficiency: -15% glyph cost per rank
        return getTalentFlatBonus(SKILLS.COMBAT, 'glyphCostReduction') || 0;
    }

    /**
     * Get elemental weakness bonus from talents
     * @returns {number} Extra weakness bonus (0.4 = +40%)
     */
    getCombatTalentWeaknessBonus() {
        // Elemental Mastery: +20% elemental weakness bonus per rank
        return getTalentFlatBonus(SKILLS.COMBAT, 'weaknessBonus') || 0;
    }

    /**
     * Get spell effect duration bonus from talents
     * @returns {number} Duration multiplier (0.15 = +15%)
     */
    getCombatTalentSpellDuration() {
        // Spell Weaver: +15% spell effect duration
        return getTalentFlatBonus(SKILLS.COMBAT, 'spellDuration') || 0;
    }

    /**
     * Get thorn damage reflection percentage
     * @returns {number} Reflection percentage (0.2 = 20%)
     */
    getCombatTalentThornDamage() {
        // Thorn Armor: 10% damage reflect per rank
        return getTalentFlatBonus(SKILLS.COMBAT, 'thornDamage') || 0;
    }

    /**
     * Check and apply Last Stand (survive fatal damage once)
     * @param {number} incomingDamage - Damage that would be dealt
     * @param {number} currentHp - Player's current HP
     * @returns {{ triggered: boolean, newHp: number }}
     */
    checkLastStand(incomingDamage, currentHp) {
        const hasLastStand = getTalentFlatBonus(SKILLS.COMBAT, 'lastStand') > 0;

        // Check if would be fatal and Last Stand hasn't been used
        if (hasLastStand && !this.talentState.lastStandUsed && incomingDamage >= currentHp) {
            this.talentState.lastStandUsed = true;
            logger.game('Last Stand! Survived fatal damage at 1 HP!');
            toastSuccess('Last Stand activated!');
            return { triggered: true, newHp: 1 };
        }

        return { triggered: false, newHp: Math.max(0, currentHp - incomingDamage) };
    }

    /**
     * Apply bleed effect to mob (from melee or ranged talents)
     * @param {string} attackStyle - 'melee' or 'ranged'
     */
    applyBleedToMob(attackStyle) {
        let bleedDamage = 0;
        let bleedChance = 0;

        if (attackStyle === 'melee') {
            // Deep Wounds: 35% chance, 5 damage/tick per rank
            bleedDamage = getTalentFlatBonus(SKILLS.COMBAT, 'meleeBleed') || 0;
            bleedChance = getTalentFlatBonus(SKILLS.COMBAT, 'meleeBleedChance') || 0;
        } else if (attackStyle === 'ranged') {
            // Barbed Arrows: 35% chance, 4 damage/tick per rank
            bleedDamage = getTalentFlatBonus(SKILLS.COMBAT, 'rangedBleed') || 0;
            bleedChance = getTalentFlatBonus(SKILLS.COMBAT, 'rangedBleedChance') || 0;
        }

        if (bleedDamage <= 0 || bleedChance <= 0) return;

        // Roll for bleed proc
        if (Math.random() > bleedChance) return;

        // Apply or refresh bleed (5 ticks)
        this.talentState.mobBleed = {
            damagePerTick: bleedDamage,
            ticksRemaining: 5,
            lastTick: Date.now()
        };
        logger.debug(`[COMBAT] Applied bleed: ${bleedDamage} dmg/tick for 5 ticks (${Math.round(bleedChance * 100)}% chance)`);
    }

    /**
     * Process bleed damage on mob (called each tick)
     * @returns {number} Damage dealt by bleed this tick
     */
    processBleedDamage() {
        if (!this.talentState.mobBleed || this.talentState.mobBleed.ticksRemaining <= 0) {
            return 0;
        }

        const now = Date.now();
        const bleed = this.talentState.mobBleed;

        // Bleed ticks every 1 second (Cindermark 2pc: 500ms)
        const bleedInterval = this.getBleedTickInterval();
        if (now - bleed.lastTick >= bleedInterval) {
            bleed.lastTick = now;
            bleed.ticksRemaining--;
            // Corrupted Prey: mob takes more damage from bleeds
            let damage = bleed.damagePerTick;
            const corruptionDmgMult = getCombatDamageTakenMult();
            if (corruptionDmgMult > 1) {
                damage = Math.floor(damage * corruptionDmgMult);
            }
            // Scorched Talisman pendant: +20% DoT damage (bleeds + fire)
            const effects = this.getEquippedEffects();
            if (effects.pendantEffect?.type === 'dotDamageBonus') {
                damage = Math.floor(damage * (1 + effects.pendantEffect.value));
            }
            logger.debug(`[COMBAT] Bleed tick: ${damage} damage (${bleed.ticksRemaining} ticks left)`);
            return damage;
        }

        return 0;
    }

    /**
     * Process combat regeneration (heal 1 HP every 5 seconds)
     */
    processCombatRegen() {
        const hasRegen = getTalentFlatBonus(SKILLS.COMBAT, 'combatRegen') > 0;
        if (!hasRegen) return;

        const now = Date.now();
        if (now - this.talentState.lastRegenTick >= 5000) {
            this.talentState.lastRegenTick = now;
            const healed = gameState.healHp(1);
            if (healed > 0) {
                logger.debug(`[COMBAT] Regeneration: healed 1 HP`);
            }
        }
    }

    // ==================== CORRUPTED PREY ====================

    /**
     * Roll for combat corruption on player attack.
     * If corruption activates and mob isn't already marked, apply persistent marks.
     */
    rollCombatCorruption() {
        const potionBonus = this.getPotionCorruptionBonus() / 100;
        rollForCorruption(SKILLS.COMBAT, potionBonus);
        // If corruption active and mob not yet marked, apply mark
        if (isSkillCorrupted(SKILLS.COMBAT) && !this.corruptedPrey.marked) {
            this.corruptedPrey.marked = true;
            this.corruptedPrey.lootMultiplier = getCombatLootMark();
            this.corruptedPrey.xpMultiplier = getCombatXPMark();
            logger.debug(`[COMBAT] Corrupted Prey! Mob marked: ${this.corruptedPrey.lootMultiplier}x loot, ${this.corruptedPrey.xpMultiplier}x XP`);
        }
    }

    /**
     * Clear Corrupted Prey marks (on zone switch, death, stop fighting)
     */
    clearCorruptedPrey() {
        this.corruptedPrey.marked = false;
        this.corruptedPrey.lootMultiplier = 1;
        this.corruptedPrey.xpMultiplier = 1;
    }

    /**
     * Get player's attack stats including accuracy and style
     * @returns {{ attack: number, attackSpeed: number, defense: number, attackStyle: string, accuracy: number, critChance: number, critDamage: number, blockChance: number, ammoEfficiency: number }}
     */
    getPlayerStats() {
        const level = this.getLevel();
        const gear = gameState.getEquippedGear();
        const activeSet = gameState.getActiveEquipmentSet() || 'melee';

        // Base stats from level
        let attack = Math.floor(level * 0.5) + 1;  // 1-50 base attack
        const baseAttack = attack;
        let defense = Math.floor(level * 0.3);      // 0-30 base defense
        let attackSpeed = COMBAT_CONFIG.BASE_ATTACK_SPEED; // 3000ms default
        let attackStyle = null;

        // Base accuracy from combat level (50% at level 1, up to 100% at level 99)
        let baseAccuracy = 50 + (level * 0.5);

        // Track accuracy bonuses by style
        let meleeAccuracy = 0;
        let rangedAccuracy = 0;
        let magicAccuracy = 0;

        // Offhand/back stats
        let blockChance = 0;      // From bulwarks (melee offhand)
        let ammoEfficiency = 0;   // From quivers (ranged back)

        // Add weapon stats
        if (gear.weapon) {
            const weapon = getItem(gear.weapon);
            if (weapon) {
                attack += weapon.attackBonus || 0;
                if (weapon.attackSpeed) {
                    attackSpeed = weapon.attackSpeed;
                }
                attackStyle = weapon.attackStyle || null;

                // Add weapon accuracy to matching style
                if (weapon.meleeAccuracy) meleeAccuracy += weapon.meleeAccuracy;
                if (weapon.rangedAccuracy) rangedAccuracy += weapon.rangedAccuracy;
                if (weapon.magicAccuracy) magicAccuracy += weapon.magicAccuracy;
            }
        }

        // Add armor, offhand, back, and ammo stats
        let equipmentSpeedBonus = 0;
        let equipmentCritBonus = 0;
        let equipmentCritDamageBonus = 0;
        let equipmentAccuracyBonus = 0;
        let armorAttackTotal = 0;
        const armorSlots = ['head', 'neck', 'shoulders', 'hands', 'body', 'legs', 'ring', 'feet', 'offhand', 'back', 'ammo'];
        for (const slot of armorSlots) {
            if (gear[slot]) {
                const armor = getItem(gear[slot]);
                if (armor) {
                    if (armor.defenseBonus) defense += armor.defenseBonus;
                    if (armor.attackBonus) {
                        attack += armor.attackBonus;
                        armorAttackTotal += armor.attackBonus;
                    }

                    // Add armor accuracy to matching styles
                    if (armor.meleeAccuracy) meleeAccuracy += armor.meleeAccuracy;
                    if (armor.rangedAccuracy) rangedAccuracy += armor.rangedAccuracy;
                    if (armor.magicAccuracy) magicAccuracy += armor.magicAccuracy;

                    // Jewelry stats (speedBonus, critChance, critDamage, accuracy)
                    if (armor.speedBonus) equipmentSpeedBonus += armor.speedBonus;
                    if (armor.critChance) equipmentCritBonus += armor.critChance;
                    if (armor.critDamage) equipmentCritDamageBonus += armor.critDamage;
                    if (armor.accuracy) equipmentAccuracyBonus += armor.accuracy;

                    // Offhand stats (bulwarks)
                    if (armor.blockChance) blockChance += armor.blockChance;

                    // Back slot stats (quivers)
                    if (armor.ammoEfficiency) ammoEfficiency += armor.ammoEfficiency;
                }
            }
        }

        // Check for built-in ammo if no ammo equipped (for starter weapons like practice bow)
        if (!gear.ammo && gear.weapon) {
            const weapon = getItem(gear.weapon);
            if (weapon?.builtInAmmo) {
                // Add built-in ammo stats
                attack += weapon.builtInAmmo.attackBonus || 0;
                rangedAccuracy += weapon.builtInAmmo.rangedAccuracy || 0;
            }
        }

        // Calculate final accuracy based on attack style
        let gearAccuracy = 0;
        if (attackStyle === 'melee') {
            gearAccuracy = meleeAccuracy;
        } else if (attackStyle === 'ranged') {
            gearAccuracy = rangedAccuracy;
        } else if (attackStyle === 'magic') {
            gearAccuracy = magicAccuracy;
        }

        // Final accuracy (capped at 5-95%)
        let accuracy = Math.min(95, Math.max(5, baseAccuracy + gearAccuracy));

        // Base crit chance (flat 5% for all combat styles)
        let critChance = 5;
        // Base crit damage (50% bonus = 1.5x multiplier)
        let critDamage = 50;

        // Apply equipment bonuses (jewelry stats)
        accuracy = Math.min(95, accuracy + equipmentAccuracyBonus);
        critChance += equipmentCritBonus;
        critDamage += equipmentCritDamageBonus;

        // Apply potion bonuses
        attack += this.getPotionAttackBonus();
        defense += this.getPotionDefenseBonus();
        accuracy = Math.min(95, accuracy + this.getPotionAccuracyBonus());
        critChance += this.getPotionCritBonus();
        critDamage += this.getPotionCritDamageBonus();

        // Apply speed bonus (reduces attack interval) - potions first, then equipment
        const speedBonus = this.getPotionSpeedBonus() + equipmentSpeedBonus;
        if (speedBonus > 0) {
            attackSpeed = Math.floor(attackSpeed * (1 - speedBonus / 100));
        }

        // Apply combat talent bonuses
        // Defense bonus (Physical Prowess, Iron Skin)
        const defenseMultiplier = this.getCombatTalentDefenseBonus();
        defense = Math.floor(defense * defenseMultiplier);

        // Accuracy bonus (Precision Training, Steady Aim for ranged)
        const accuracyBonus = this.getCombatTalentAccuracyBonus();
        if (accuracyBonus > 0) {
            accuracy = Math.min(95, accuracy + (accuracy * accuracyBonus));
        }
        // Extra ranged accuracy from Steady Aim
        if (attackStyle === 'ranged') {
            const rangedAccuracyBonus = getTalentFlatBonus(SKILLS.COMBAT, 'rangedAccuracy') || 0;
            if (rangedAccuracyBonus > 0) {
                accuracy = Math.min(95, accuracy + (accuracy * rangedAccuracyBonus));
            }
        }

        // Attack speed bonus from talents (Combat Reflexes, Relentless Assault)
        const talentSpeedBonus = this.getCombatTalentAttackSpeedBonus(attackStyle);
        if (talentSpeedBonus > 0) {
            attackSpeed = Math.floor(attackSpeed * (1 - talentSpeedBonus));
        }

        // Pet companion attack speed bonus (Felltracker/Eidolon buff)
        const petSpeedBonus = this.getPetAttackSpeedBonus();
        if (petSpeedBonus > 0) {
            attackSpeed = Math.floor(attackSpeed * (1 - petSpeedBonus / 100));
        }

        // Pet companion defense bonus (Ichorebound/Eidolon buff)
        defense += this.getPetDefenseBonus();

        return { attack, attackSpeed, defense, attackStyle, accuracy, critChance, critDamage, blockChance, ammoEfficiency };
    }

    /**
     * Roll to see if an attack hits
     * @param {number} accuracy - Accuracy percentage (0-100)
     * @returns {boolean} True if hit, false if miss
     */
    rollHit(accuracy) {
        return Math.random() * 100 < accuracy;
    }

    /**
     * Get style modifier for damage/accuracy against current mob
     * @param {string} attackStyle - Player's attack style
     * @returns {{ accuracyMod: number, damageMod: number }}
     */
    getStyleModifiers(attackStyle) {
        const combat = gameState.getCombat();
        const mob = combat.currentMob;

        let accuracyMod = 1.0;
        let damageMod = 1.0;

        if (!mob || !attackStyle) return { accuracyMod, damageMod };

        // Weakness: +25% accuracy, +20% damage
        if (mob.weakTo === attackStyle) {
            accuracyMod = 1.25;
            damageMod = 1.20;
        }

        // Resistance: -35% accuracy, -25% damage
        if (mob.resistantTo === attackStyle) {
            accuracyMod = 0.65;
            damageMod = 0.75;
        }

        return { accuracyMod, damageMod };
    }

    /**
     * Calculate player's damage to mob (after hit confirmed)
     * @returns {number}
     */
    calculatePlayerDamage() {
        const stats = this.getPlayerStats();
        const combat = gameState.getCombat();
        const mob = combat.currentMob;

        if (!mob) return 0;

        // Get style modifiers for weakness/resistance
        const styleMods = this.getStyleModifiers(stats.attackStyle);

        // Get Pyre penalty for current zone
        const pyrePenalty = this.getPyrePenalty(combat.currentZone);

        // === CALCULATE MAX HIT ===
        // Start with base attack stat
        let maxHit = stats.attack;

        // Apply potion melee damage bonus (Rallying Elixir, Ascendant)
        if (stats.attackStyle === 'melee') {
            const meleeBonus = this.getPotionMeleeDamageBonus();
            if (meleeBonus > 0) {
                maxHit = Math.floor(maxHit * (1 + meleeBonus / 100));
            }
        }

        // Apply potion ranged damage bonus (Sharpshot Brew, Ascendant)
        if (stats.attackStyle === 'ranged') {
            const rangedBonus = this.getPotionRangedDamageBonus();
            if (rangedBonus > 0) {
                maxHit = Math.floor(maxHit * (1 + rangedBonus / 100));
            }
        }

        // Apply equipment damage bonuses (pendants, rings, weapons)
        const equipEffects = this.getEquippedEffects();

        // Pendant: allDamageBonus (Void Siphon +5%, Elder Sigil +10%, Ember Ward +12%)
        const allDmgBonus = this.getPendantAllDamageBonus();
        if (allDmgBonus > 0) {
            maxHit = Math.floor(maxHit * (1 + allDmgBonus));
        }

        // Berserker's Tooth pendant: +10% damage below 50% HP
        if (equipEffects.pendantEffect?.type === 'berserkerEnhance') {
            const currentHp = gameState.getCurrentHp();
            const maxHp = gameState.getMaxHp();
            if (currentHp / maxHp < equipEffects.pendantEffect.lowHpThreshold) {
                maxHit = Math.floor(maxHit * (1 + equipEffects.pendantEffect.lowHpDamageBonus));
            }
        }

        // Eclipse Piercer weapon: +10% ranged damage
        if (stats.attackStyle === 'ranged' && equipEffects.weaponEffect?.type === 'eclipsePiercer') {
            maxHit = Math.floor(maxHit * (1 + equipEffects.weaponEffect.rangedDamageBonus));
        }

        // Arcane Overflow ring: +10% all damage while spell effects active
        const arcaneOverflow = this.getArcaneOverflowBonus();
        if (arcaneOverflow.allDamageBonus > 0) {
            maxHit = Math.floor(maxHit * (1 + arcaneOverflow.allDamageBonus));
        }

        // Inferno's Fury ring: +20% damage to targets below 33% HP
        if (equipEffects.ringEffect?.type === 'infernosFury') {
            if (combat.mobCurrentHp / mob.hp < equipEffects.ringEffect.executeThreshold) {
                maxHit = Math.floor(maxHit * (1 + equipEffects.ringEffect.executeDamageBonus));
                logger.debug(`[COMBAT] Inferno's Fury! +${equipEffects.ringEffect.executeDamageBonus * 100}% max hit to low HP target`);
            }
        }

        // Marked Prey ring: +12% damage to marked targets
        const markedPreyBonus = this.getMarkedPreyBonus();
        if (markedPreyBonus > 0) {
            maxHit = Math.floor(maxHit * (1 + markedPreyBonus));
        }

        // Set bonus: Ashforged 2pc Momentum (+2% per stack, max +20%)
        const momentumBonus = this.getMomentumDamageBonus();
        if (momentumBonus > 0) {
            maxHit = Math.floor(maxHit * (1 + momentumBonus));
        }

        // Set bonus: Pyreweave 6pc Hailstorm fusion (ice+air = +8% all damage)
        const fusionMods = this.getSpellFusionModifiers();
        if (fusionMods.allDamageAmp > 0) {
            maxHit = Math.floor(maxHit * (1 + fusionMods.allDamageAmp));
        }

        // Apply combat talent damage bonuses (Battle Hardened, Blade Mastery, Archer's Eye, Berserker)
        const talentDamageMultiplier = this.getCombatTalentDamageBonus(stats.attackStyle);
        if (talentDamageMultiplier > 1) {
            maxHit = Math.floor(maxHit * talentDamageMultiplier);
        }

        // Apply Executioner bonus (extra damage to low HP enemies)
        const executeBonus = this.getExecuteBonus(combat.mobCurrentHp, mob.hp);
        if (executeBonus > 0) {
            maxHit = Math.floor(maxHit * (1 + executeBonus));
            logger.debug(`[COMBAT] Executioner! Bonus max hit to low HP target`);
        }

        // === APPLY MOB DEFENSE ===
        // Calculate effective mob defense (Corrupted Prey reduces mob defense)
        let effectiveMobDefense = mob.defense;
        const corruptionDefReduction = getCombatDefenseReduction();
        if (corruptionDefReduction > 0) {
            effectiveMobDefense = Math.floor(effectiveMobDefense * (1 - corruptionDefReduction));
        }
        // Apply boss phase defense bonus (e.g., Pyre Guardian +15, Molten Colossus +10/+20)
        const bossMods = this.getBossModifiers();
        if (bossMods.mobDefenseBonus !== 0) {
            effectiveMobDefense = Math.max(0, effectiveMobDefense + bossMods.mobDefenseBonus);
        }
        // Set bonus: Pyreweave 6pc Magma fusion (fire+earth = -15% mob defense)
        if (fusionMods.mobDefenseReduction > 0) {
            effectiveMobDefense = Math.floor(effectiveMobDefense * (1 - fusionMods.mobDefenseReduction));
        }
        // Bounty mark defense ignore (flat subtraction)
        const mobIdForMark = typeof mob === 'string' ? mob : mob.id;
        const markForDef = getBountyMark(mobIdForMark);
        if (markForDef.defIgnore > 0) {
            effectiveMobDefense = Math.max(0, effectiveMobDefense - markForDef.defIgnore);
        }

        // Apply mob defense reduction: def/(def+50) for stronger defense scaling
        const reduction = effectiveMobDefense / (effectiveMobDefense + 50);
        maxHit = Math.max(1, Math.floor(maxHit * (1 - reduction)));

        // Apply style damage modifier (weakness/resistance)
        maxHit = Math.max(1, Math.floor(maxHit * styleMods.damageMod));

        // Apply Pyre damage penalty
        maxHit = Math.max(1, Math.floor(maxHit * pyrePenalty.damageMult));

        // === ROLL DAMAGE (1 to maxHit) ===
        let damage = 1 + Math.floor(Math.random() * maxHit);

        // Apply Corrupted Prey damage taken multiplier (mob takes more damage)
        const corruptionDmgMult = getCombatDamageTakenMult();
        if (corruptionDmgMult > 1) {
            damage = Math.floor(damage * corruptionDmgMult);
        }

        // Apply crit chance (base 5% for melee/ranged)
        this.lastHitWasCrit = false;
        const critChance = stats.critChance;
        if (critChance > 0 && Math.random() * 100 < critChance) {
            this.lastHitWasCrit = true;
            const critMultiplier = 1 + (stats.critDamage / 100);
            damage = Math.floor(damage * critMultiplier);
            logger.debug(`[COMBAT] Critical hit! ${stats.critDamage}% bonus, damage boosted to ${damage}`);

            // Crit-triggered ring effects (Relentless speed, Marked Prey)
            this.processRingCritEffects();
        }

        // Apply boss player damage reduction (e.g., Void Barrier -25%)
        if (bossMods.playerDamageReduction > 0) {
            damage = Math.max(1, Math.floor(damage * (1 - bossMods.playerDamageReduction)));
        }

        // Damage floor is now a gate check in startFighting() - no per-hit penalty

        return damage;
    }

    /**
     * Calculate player's effective accuracy against current mob
     * @returns {number} Accuracy percentage (5-95)
     */
    getEffectiveAccuracy() {
        const stats = this.getPlayerStats();
        const styleMods = this.getStyleModifiers(stats.attackStyle);

        // Apply style accuracy modifier
        let effectiveAccuracy = stats.accuracy * styleMods.accuracyMod;

        // Bounty mark accuracy bonus (flat addition)
        const combat = gameState.getCombat();
        const mob = combat.currentMob;
        if (mob) {
            const mobId = typeof mob === 'string' ? mob : mob.id;
            const mark = getBountyMark(mobId);
            effectiveAccuracy += mark.accuracy;
        }

        // Cap between 5-95%
        return Math.min(95, Math.max(5, effectiveAccuracy));
    }

    /**
     * Calculate mob's evasion chance based on their defense
     * Mobs weak to player's attack style have reduced evasion (harder to dodge)
     * @returns {number} Evasion chance (0-0.35)
     */
    getMobEvasionChance() {
        const combat = gameState.getCombat();
        const mob = combat.currentMob;
        if (!mob) return 0;

        const stats = this.getPlayerStats();

        // Calculate effective mob defense (same reductions as damage calc)
        let effectiveMobDefense = mob.defense;
        const corruptionDefReduction = getCombatDefenseReduction();
        if (corruptionDefReduction > 0) {
            effectiveMobDefense = Math.floor(effectiveMobDefense * (1 - corruptionDefReduction));
        }
        const bossMods = this.getBossModifiers();
        if (bossMods.mobDefenseBonus !== 0) {
            effectiveMobDefense = Math.max(0, effectiveMobDefense + bossMods.mobDefenseBonus);
        }
        const fusionMods = this.getSpellFusionModifiers();
        if (fusionMods.mobDefenseReduction > 0) {
            effectiveMobDefense = Math.floor(effectiveMobDefense * (1 - fusionMods.mobDefenseReduction));
        }
        // Bounty mark defense ignore (flat subtraction)
        if (mob.id) {
            const markEvasion = getBountyMark(mob.id);
            if (markEvasion.defIgnore > 0) {
                effectiveMobDefense = Math.max(0, effectiveMobDefense - markEvasion.defIgnore);
            }
        }

        // Base evasion: def/(def+200), capped at 35%
        // 75 def boss = ~27%, 45 def = ~18%, 10 def = ~5%
        let evasionChance = Math.min(0.35, effectiveMobDefense / (effectiveMobDefense + 200));

        // Style weakness reduces mob evasion by 50% (they're worse at dodging attacks they're weak to)
        // Style resistance increases mob evasion by 40% (they're better at evading attacks they resist)
        if (mob.weakTo === stats.attackStyle) {
            evasionChance *= 0.50; // 50% reduction in evasion
        } else if (mob.resistantTo === stats.attackStyle) {
            evasionChance = Math.min(0.50, evasionChance * 1.40); // 40% boost, cap at 50%
        }

        return evasionChance;
    }

    /**
     * Check if mob evades the player's attack
     * @returns {boolean} True if mob evaded
     */
    checkMobEvasion() {
        const evasionChance = this.getMobEvasionChance();
        if (evasionChance > 0 && Math.random() < evasionChance) {
            logger.debug(`[COMBAT] Mob evaded! (${(evasionChance * 100).toFixed(1)}% chance)`);
            return true;
        }
        return false;
    }

    /**
     * Calculate damage from mob to player (after defense and block)
     * Defense provides: evasion chance (dodge) + smaller damage reduction
     * This ensures hits hurt but can be avoided, making food consumption essential
     * @param {number} rawDamage - Raw damage from mob
     * @param {boolean} skipEvasion - If true, skip evasion roll (for DoT damage that can't be dodged)
     * @returns {{ damage: number, blocked: boolean, evaded: boolean }}
     */
    calculateDamageToPlayer(rawDamage, skipEvasion = false) {
        const stats = this.getPlayerStats();
        let blocked = false;
        let evaded = false;

        // Apply boss damage multiplier (enrage, phase bonuses)
        const bossMods = this.getBossModifiers();
        if (bossMods.mobDamageMult !== 1.0) {
            rawDamage = Math.floor(rawDamage * bossMods.mobDamageMult);
        }

        // Include defense buff from spell effects + Ember Shield equipment buff
        let totalDefense = stats.defense + this.getSpellDefenseBonus() + this.getEmberShieldDefenseBonus();

        // Apply boss defense ignore (Cindered Knight Berserker ignores 30% of player defense)
        if (bossMods.ignoreDefensePercent > 0) {
            totalDefense = Math.floor(totalDefense * (1 - bossMods.ignoreDefensePercent));
        }

        // Apply mob mechanic defense reduction (curse, armor shred)
        const mechDefReduction = this.getMobMechanicDefenseReduction();
        if (mechDefReduction > 0) {
            totalDefense = Math.max(0, totalDefense - mechDefReduction);
        }

        // Evasion check - defense gives chance to completely dodge the attack
        // Formula: def/(def+500), capped at 60%
        // 324 def (voidsteel) = ~39% dodge, 399 def (full set) = ~44% dodge
        // Note: DoT damage (burns, poison) cannot be evaded - it's already on you
        if (!skipEvasion) {
            const evasionChance = Math.min(0.60, totalDefense / (totalDefense + 500));
            if (Math.random() < evasionChance) {
                evaded = true;
                logger.debug(`[COMBAT] Evaded! (${(evasionChance * 100).toFixed(1)}% chance)`);
                return { damage: 0, blocked: false, evaded: true };
            }
        }

        // Check for block (bulwark offhand) - 50% damage reduction when block procs
        if (stats.blockChance > 0 && Math.random() * 100 < stats.blockChance) {
            blocked = true;
            rawDamage = Math.floor(rawDamage * 0.5);
            logger.debug(`[COMBAT] Blocked! Damage reduced by 50%`);
        }

        // Percentage-based damage reduction (smaller than before)
        // Formula: def/(def+800) gives ~29% at 324 def, ~33% at 399 def
        // This means bosses hit for ~60-80 damage when they land (before potions)
        const reduction = totalDefense / (totalDefense + 800);
        let damage = Math.max(1, Math.floor(rawDamage * (1 - reduction)));

        // Apply potion damage reduction (Sustaining Brew)
        const potionReduction = this.getPotionDamageReduction();
        if (potionReduction > 0) {
            damage = Math.max(1, Math.floor(damage * (1 - potionReduction / 100)));
        }

        return { damage, blocked, evaded };
    }

    // ==================== MAGIC COMBAT ====================

    /**
     * Select a spell to use for magic attacks
     * @param {string} spellId - Spell ID
     * @returns {{ success: boolean, reason?: string }}
     */
    selectSpell(spellId) {
        const spell = getSpell(spellId);
        if (!spell) {
            return { success: false, reason: 'Spell not found' };
        }

        // Check level requirement
        const level = this.getLevel();
        if (level < spell.level) {
            return { success: false, reason: `Requires Combat level ${spell.level}` };
        }

        // Reset attack timer if switching to a different spell (prevents spell spam)
        if (this.activeSpell !== spellId) {
            const combat = gameState.getCombat();
            if (combat.currentZone) {
                combat.lastPlayerAttack = Date.now();
                logger.debug(`[COMBAT] Attack timer reset due to spell switch`);
            }
        }

        this.activeSpell = spellId;
        gameState.setSelectedSpell(spellId);  // Persist to gameState
        logger.debug(`[COMBAT] Selected spell: ${spell.name}`);
        return { success: true, spell };
    }

    /**
     * Clear the active spell
     */
    clearSpell() {
        this.activeSpell = null;
        gameState.setSelectedSpell(null);  // Clear from gameState
    }

    /**
     * Get the currently selected spell
     * @returns {Object|null} Spell data or null
     */
    getActiveSpell() {
        // Sync from gameState if out of sync (handles page load after save restore)
        const savedSpell = gameState.getSelectedSpell();
        if (savedSpell && !this.activeSpell) {
            this.activeSpell = savedSpell;
        }
        return this.activeSpell ? getSpell(this.activeSpell) : null;
    }

    /**
     * Get the equipped weapon's built-in spell (for starter weapons like apprentice staff)
     * @returns {Object|null} Built-in spell data or null
     */
    getBuiltInSpell() {
        const gear = gameState.getEquippedGear();
        if (!gear.weapon) return null;

        const weapon = getItem(gear.weapon);
        if (!weapon?.builtInSpell) return null;

        // Convert built-in spell to spell-like object with required properties
        return {
            id: 'built_in_spell',
            name: weapon.builtInSpell.name,
            baseDamage: weapon.builtInSpell.baseDamage,
            element: weapon.builtInSpell.element,
            description: weapon.builtInSpell.description,
            level: 1,
            tier: 0,
            glyphCost: [],  // No glyph cost for built-in spells
            effect: null,   // No special effects for basic built-in spell
            isBuiltIn: true // Flag to identify as built-in
        };
    }

    /**
     * Get the equipped weapon's built-in ammo (for starter weapons like practice bow)
     * @returns {Object|null} Built-in ammo data or null
     */
    getBuiltInAmmo() {
        const gear = gameState.getEquippedGear();
        if (!gear.weapon) return null;

        const weapon = getItem(gear.weapon);
        if (!weapon?.builtInAmmo) return null;

        return {
            name: weapon.builtInAmmo.name,
            attackBonus: weapon.builtInAmmo.attackBonus || 0,
            rangedAccuracy: weapon.builtInAmmo.rangedAccuracy || 0,
            isBuiltIn: true
        };
    }

    /**
     * Check if player can cast the active spell (or use built-in spell)
     * @returns {{ success: boolean, reason?: string, useBuiltIn?: boolean, freeGlyphs?: boolean }}
     */
    canCastSpell() {
        const spell = this.getActiveSpell();
        const builtInSpell = this.getBuiltInSpell();
        const gear = gameState.getEquippedGear();
        const weapon = gear.weapon ? getItem(gear.weapon) : null;
        const hasFreeGlyphs = weapon?.freeGlyphs === true;

        // If no regular spell selected, check for built-in
        if (!spell) {
            if (builtInSpell) {
                // Built-in spell always available (no glyph cost)
                return { success: true, reducedCost: [], useBuiltIn: true };
            }
            return { success: false, reason: 'No spell selected' };
        }

        // Check level requirement
        const level = this.getLevel();
        if (level < spell.level) {
            // Fall back to built-in if available
            if (builtInSpell) {
                return { success: true, reducedCost: [], useBuiltIn: true };
            }
            return { success: false, reason: `Requires Combat level ${spell.level}` };
        }

        // If weapon has freeGlyphs (Eclipse Conduit), skip glyph cost check
        if (hasFreeGlyphs) {
            return { success: true, reducedCost: [], useBuiltIn: false, freeGlyphs: true };
        }

        // Check glyph cost (apply glyph cost reduction from talent)
        const glyphCost = spell.glyphCost || [];
        const costReduction = this.getCombatTalentGlyphCostReduction();
        const reducedCost = glyphCost.map(cost => ({
            glyphId: cost.glyphId,
            quantity: Math.max(1, Math.floor(cost.quantity * (1 - costReduction)))
        }));
        const glyphCheck = gameState.hasGlyphsForSpell(reducedCost);
        if (!glyphCheck.success) {
            // Fall back to built-in if available
            if (builtInSpell) {
                return { success: true, reducedCost: [], useBuiltIn: true };
            }
            return { success: false, reason: glyphCheck.reason || 'Not enough glyphs' };
        }

        return { success: true, reducedCost, useBuiltIn: false };
    }

    /**
     * Cast the active spell - consume glyphs and calculate damage
     * @returns {{ success: boolean, damage?: number, spell?: Object, reason?: string, glyphSaved?: boolean }}
     */
    castSpell() {
        const canCast = this.canCastSpell();
        if (!canCast.success) {
            return canCast;
        }

        // Use built-in spell if falling back, otherwise use selected spell
        const spell = canCast.useBuiltIn ? this.getBuiltInSpell() : this.getActiveSpell();
        const isBuiltIn = canCast.useBuiltIn;
        const hasFreeGlyphs = canCast.freeGlyphs === true;

        let glyphSaved = false;

        // Only check glyph consumption for non-built-in, non-free-glyph spells
        if (!isBuiltIn && !hasFreeGlyphs) {
            // Check for glyph save chance (Arcane Tincture potion + Corrupted Prey)
            const potionGlyphSave = this.getPotionGlyphSaveChance();
            const corruptionGlyphSave = getCombatGlyphSave() * 100; // 0.25 → 25%
            const totalGlyphSave = potionGlyphSave + corruptionGlyphSave;
            glyphSaved = totalGlyphSave > 0 && Math.random() * 100 < totalGlyphSave;

            if (!glyphSaved) {
                // Consume glyphs with talent cost reduction applied
                const consumed = gameState.consumeGlyphsForSpell(canCast.reducedCost);
                if (!consumed.success) {
                    return { success: false, reason: 'Failed to consume glyphs' };
                }
            } else {
                logger.debug(`[COMBAT] Glyph save proc! No glyphs consumed.`);
            }
        } else if (hasFreeGlyphs) {
            glyphSaved = true; // Eclipse Conduit - always free
            logger.debug(`[COMBAT] Free glyph cast (Eclipse Conduit)`);
        }

        // Calculate magic damage
        let damage = this.calculateMagicDamage(spell);

        // Apply spell effect with duration bonus (skip for built-in spells with no effect)
        if (spell.effect) {
            this.applySpellEffect(spell);
        }

        // Talent procs apply to non-built-in spells (including free glyph spells from Eclipse Conduit)
        let echoTriggered = false;
        let cascadeTriggered = false;

        if (!isBuiltIn) {
            // Increment Spell Echo counter and check for proc (every 3rd spell casts twice)
            this.talentState.spellEchoCounter++;
            const spellEcho = getTalentFlatBonus(SKILLS.COMBAT, 'spellEcho');
            if (spellEcho > 0 && this.talentState.spellEchoCounter >= 3) {
                this.talentState.spellEchoCounter = 0;
                // Cast spell again at full power (no glyph cost)
                const echoDamage = this.calculateMagicDamage(spell);
                damage += echoDamage;
                echoTriggered = true;
                logger.debug(`[COMBAT] Spell Echo! Extra ${echoDamage} damage`);
            }

            // Check for Elemental Cascade proc (cast random different element at 50% damage)
            // Pyreweave 4pc: enhanced cascade fires a 3rd spell at 50% power
            const cascadeChance = getTalentProcChance(SKILLS.COMBAT, 'elementalCascade');
            if (cascadeChance > 0 && Math.random() < cascadeChance) {
                const useEnhanced = this.hasSetBonus('pyreweave', 4);
                const cascadeResult = useEnhanced
                    ? this.castEnhancedCascade(spell)
                    : this.castElementalCascade(spell);
                if (cascadeResult.success) {
                    damage += cascadeResult.damage;
                    cascadeTriggered = true;
                }
            }

            // Pyreweave 2pc: 25% chance to recast spell at 75% power
            const setEchoResult = this.processSetSpellEcho(spell);
            if (setEchoResult.triggered) {
                damage += setEchoResult.damage;
                echoTriggered = true;
            }
        }

        logger.debug(`[COMBAT] Cast ${spell.name} for ${damage} damage${isBuiltIn ? ' (built-in)' : ''}${echoTriggered ? ' (Echo!)' : ''}${cascadeTriggered ? ' (Cascade!)' : ''}`);

        return { success: true, damage, spell, glyphSaved, echoTriggered, cascadeTriggered, isBuiltIn };
    }

    /**
     * Cast Elemental Cascade - fires a random different element at 50% damage
     * Also applies that element's buff/debuff effects
     * @param {Object} originalSpell - The spell that triggered the cascade
     * @returns {{ success: boolean, damage?: number, spell?: Object }}
     */
    castElementalCascade(originalSpell) {
        const elements = ['fire', 'ice', 'earth', 'air'];
        const availableElements = elements.filter(e => e !== originalSpell.element);

        if (availableElements.length === 0) return { success: false };

        // Pick random different element
        const cascadeElement = availableElements[Math.floor(Math.random() * availableElements.length)];

        // Find a spell of that element (use tier 1 for simplicity)
        const cascadeSpellId = `${cascadeElement}_bolt`;
        const cascadeSpell = getSpell(cascadeSpellId);
        if (!cascadeSpell) return { success: false };

        // Calculate damage at 50% power
        const fullDamage = this.calculateMagicDamage(cascadeSpell);
        const cascadeDamage = Math.floor(fullDamage * 0.5);

        // Apply the cascade spell's effect (buffs/debuffs)
        this.applySpellEffect(cascadeSpell);

        logger.debug(`[COMBAT] Elemental Cascade! ${cascadeSpell.name} for ${cascadeDamage} damage`);

        return { success: true, damage: cascadeDamage, spell: cascadeSpell };
    }

    /**
     * Calculate magic damage from a spell
     * @param {Object} spell - Spell data
     * @returns {number} Final damage amount
     */
    calculateMagicDamage(spell) {
        const combat = gameState.getCombat();
        const mob = combat.currentMob;
        if (!mob) return 0;

        // Base damage from spell
        // === CALCULATE MAGIC MAX HIT ===
        // Start with spell base damage
        let maxHit = spell.baseDamage;

        // Add magic damage multiplier from staff + armor
        const spellMultiplier = this.getTotalSpellDamage();
        maxHit = Math.floor(maxHit * spellMultiplier);

        // Add level scaling (0.5% per combat level)
        const level = this.getLevel();
        maxHit = Math.floor(maxHit * (1 + level * 0.005));

        // Apply potion magic damage bonus (Arcane Tincture)
        const magicBonus = this.getPotionMagicDamageBonus();
        if (magicBonus > 0) {
            maxHit = Math.floor(maxHit * (1 + magicBonus / 100));
        }

        // Apply equipment damage bonuses (pendants, rings)
        const allDmgBonus = this.getPendantAllDamageBonus();
        if (allDmgBonus > 0) {
            maxHit = Math.floor(maxHit * (1 + allDmgBonus));
        }

        // Arcane Overflow ring: +10% all damage while spell effects active
        const arcaneOverflow = this.getArcaneOverflowBonus();
        if (arcaneOverflow.allDamageBonus > 0) {
            maxHit = Math.floor(maxHit * (1 + arcaneOverflow.allDamageBonus));
        }

        // Inferno's Fury ring: +20% damage to targets below 33% HP
        const equipEffects = this.getEquippedEffects();
        if (equipEffects.ringEffect?.type === 'infernosFury') {
            if (combat.mobCurrentHp / mob.hp < equipEffects.ringEffect.executeThreshold) {
                maxHit = Math.floor(maxHit * (1 + equipEffects.ringEffect.executeDamageBonus));
            }
        }

        // Marked Prey ring: +12% damage to marked targets
        const markedPreyBonus = this.getMarkedPreyBonus();
        if (markedPreyBonus > 0) {
            maxHit = Math.floor(maxHit * (1 + markedPreyBonus));
        }

        // Set bonus: Pyreweave 6pc Hailstorm fusion (ice+air = +8% all damage)
        const fusionMods = this.getSpellFusionModifiers();
        if (fusionMods.allDamageAmp > 0) {
            maxHit = Math.floor(maxHit * (1 + fusionMods.allDamageAmp));
        }

        // === APPLY MOB MAGIC DEFENSE ===
        // Magic uses half defense or explicit magicDefense
        let mobDefense = mob.magicDefense || Math.floor(mob.defense * 0.5);
        const corruptionDefReduction = getCombatDefenseReduction();
        if (corruptionDefReduction > 0) {
            mobDefense = Math.floor(mobDefense * (1 - corruptionDefReduction));
        }
        // Apply boss phase defense bonus (e.g., Pyre Guardian +15, Molten Colossus +10/+20)
        const bossMods = this.getBossModifiers();
        if (bossMods.mobDefenseBonus !== 0) {
            mobDefense = Math.max(0, mobDefense + bossMods.mobDefenseBonus);
        }
        // Set bonus: Pyreweave 6pc Magma fusion (fire+earth = -15% mob defense)
        if (fusionMods.mobDefenseReduction > 0) {
            mobDefense = Math.floor(mobDefense * (1 - fusionMods.mobDefenseReduction));
        }

        // Apply mob defense reduction: def/(def+50) for stronger defense scaling
        // Note: Magic uses half defense, so effective formula is (def*0.5)/(def*0.5+50)
        const reduction = mobDefense / (mobDefense + 50);
        maxHit = Math.max(1, Math.floor(maxHit * (1 - reduction)));

        // Get Pyre penalty for current zone
        const pyrePenalty = this.getPyrePenalty(combat.currentZone);
        maxHit = Math.max(1, Math.floor(maxHit * pyrePenalty.damageMult));

        // === ROLL DAMAGE (1 to maxHit) ===
        let damage = 1 + Math.floor(Math.random() * maxHit);

        // Apply Corrupted Prey damage taken multiplier
        const corruptionDmgMult = getCombatDamageTakenMult();
        if (corruptionDmgMult > 1) {
            damage = Math.floor(damage * corruptionDmgMult);
        }

        // Apply elemental weakness bonus (+20% base + talent bonus if spell element matches mob weakness)
        if (mob.elementalWeakness && spell.element) {
            if (mob.elementalWeakness.includes(spell.element)) {
                const weaknessBonus = this.getCombatTalentWeaknessBonus();
                damage = Math.floor(damage * (1.2 + weaknessBonus));
                logger.debug(`[COMBAT] Elemental weakness! ${spell.element} damage boosted to ${damage} (bonus: +${(weaknessBonus * 100).toFixed(0)}%)`);
            }
        }

        // Apply crit chance (base 5% + air spell bonus)
        this.lastHitWasCrit = false;
        const stats = this.getPlayerStats();
        const totalCrit = stats.critChance + this.getSpellCritBonus();
        if (totalCrit > 0 && Math.random() * 100 < totalCrit) {
            this.lastHitWasCrit = true;
            const critMultiplier = 1 + (stats.critDamage / 100);
            damage = Math.floor(damage * critMultiplier);
            logger.debug(`[COMBAT] Critical hit! ${stats.critDamage}% bonus, damage boosted to ${damage}`);

            // Crit-triggered ring effects
            this.processRingCritEffects();
        }

        // Apply boss player damage reduction (e.g., Void Barrier -25%)
        if (bossMods.playerDamageReduction > 0) {
            damage = Math.max(1, Math.floor(damage * (1 - bossMods.playerDamageReduction)));
        }

        // Damage floor is now a gate check in startFighting() - no per-hit penalty

        return damage;
    }

    /**
     * Get magic damage multiplier from equipped staff
     * @returns {number} Magic damage multiplier (1.0 base)
     */
    getStaffMagicDamage() {
        const gear = gameState.getEquippedGear();
        if (!gear.weapon) return 1.0;

        const weapon = getItem(gear.weapon);
        if (!weapon || weapon.attackStyle !== 'magic') return 1.0;

        return weapon.magicDamage || 1.0;
    }

    /**
     * Get spell damage bonus from equipped magic armor (T3+)
     * @returns {number} Spell damage multiplier bonus (0 base, adds to staff multiplier)
     */
    getArmorSpellDamage() {
        const gear = gameState.getEquippedGear();
        let bonus = 0;

        // Check all armor slots for spellDamage bonus
        const armorSlots = ['head', 'shoulders', 'hands', 'body', 'legs', 'feet'];
        for (const slot of armorSlots) {
            if (gear[slot]) {
                const armor = getItem(gear[slot]);
                if (armor?.spellDamage) {
                    bonus += armor.spellDamage;
                }
            }
        }

        return bonus;
    }

    /**
     * Get spell damage bonus from equipped focus (offhand)
     * @returns {number} Spell damage multiplier bonus (0 base, adds to staff multiplier)
     */
    getFocusSpellDamage() {
        const gear = gameState.getEquippedGear();
        if (!gear.offhand) return 0;

        const offhand = getItem(gear.offhand);
        if (!offhand || offhand.offhandType !== 'focus') return 0;

        return offhand.spellDamage || 0;
    }

    /**
     * Get total spell damage multiplier (staff + armor + focus)
     * @returns {number} Total spell damage multiplier
     */
    getTotalSpellDamage() {
        let total = this.getStaffMagicDamage() + this.getArmorSpellDamage() + this.getFocusSpellDamage() + this.getJewelrySpellDamage();

        // Shaman's Focus pendant: +8% spell damage
        const effects = this.getEquippedEffects();
        if (effects.pendantEffect?.type === 'spellDamageBonus') {
            total *= (1 + effects.pendantEffect.value);
        }

        // Eclipse Conduit weapon: +10% spell damage
        if (effects.weaponEffect?.type === 'eclipseConduit') {
            total *= (1 + effects.weaponEffect.spellDamageBonus);
        }

        // Arcane Overflow ring: +8% spell damage with 2+ spell effects active
        const arcaneOverflow = this.getArcaneOverflowBonus();
        if (arcaneOverflow.spellDamageBonus > 0) {
            total *= (1 + arcaneOverflow.spellDamageBonus);
        }

        return total;
    }

    /**
     * Apply spell effect based on spell type
     * @param {Object} spell - Spell data
     */
    applySpellEffect(spell) {
        const now = Date.now();
        const effect = spell.effect;

        // Apply spell duration bonus from talent (Spell Weaver) + Pyre Resonance weapon
        const durationBonus = this.getCombatTalentSpellDuration();
        const effects = this.getEquippedEffects();
        const pyreResonanceBonus = effects.weaponEffect?.type === 'pyreResonance' ? (effects.weaponEffect.spellDurationBonus || 0) : 0;
        const adjustedDuration = Math.floor(effect.duration * (1 + durationBonus + pyreResonanceBonus));

        switch (effect.type) {
            case SPELL_EFFECTS.DOT:
                this.spellEffects.dot = {
                    damage: effect.damage,
                    endTime: now + adjustedDuration,
                    tickInterval: adjustedDuration / effect.ticks,
                    lastTick: now
                };
                break;

            case SPELL_EFFECTS.SLOW:
                this.spellEffects.slow = {
                    multiplier: effect.multiplier,
                    endTime: now + adjustedDuration
                };
                break;

            case SPELL_EFFECTS.DEFENSE:
                this.spellEffects.defense = {
                    bonus: effect.bonus,
                    endTime: now + adjustedDuration
                };
                break;

            case SPELL_EFFECTS.CRIT:
                this.spellEffects.crit = {
                    bonus: effect.bonus,
                    endTime: now + adjustedDuration
                };
                break;
        }
    }

    /**
     * Process active spell effects (DoT ticks, expiration)
     * @returns {{ dotDamage: number }} Damage dealt by DoT this tick
     */
    processSpellEffects() {
        const now = Date.now();
        const combat = gameState.getCombat();
        let dotDamage = 0;

        // Process DoT
        if (this.spellEffects.dot) {
            const dot = this.spellEffects.dot;
            if (now >= dot.endTime) {
                this.spellEffects.dot = null;
            } else {
                // Firestorm fusion (fire+air): DoT ticks 50% faster
                let effectiveTickInterval = dot.tickInterval;
                const fusionMods = this.getSpellFusionModifiers();
                if (fusionMods.dotTickSpeedBonus > 0) {
                    effectiveTickInterval = Math.floor(dot.tickInterval / (1 + fusionMods.dotTickSpeedBonus));
                }

                if (now - dot.lastTick >= effectiveTickInterval) {
                    dot.lastTick = now;
                    dotDamage = dot.damage;
                    // Corrupted Prey: mob takes more damage from DoTs
                    const corruptionDotMult = getCombatDamageTakenMult();
                    if (corruptionDotMult > 1) {
                        dotDamage = Math.floor(dotDamage * corruptionDotMult);
                    }
                    // Scorched Talisman pendant: +20% DoT damage (fire DoTs)
                    const dotEffects = this.getEquippedEffects();
                    if (dotEffects.pendantEffect?.type === 'dotDamageBonus') {
                        dotDamage = Math.floor(dotDamage * (1 + dotEffects.pendantEffect.value));
                    }
                    // Firestorm fusion (fire+air): +30% DoT damage
                    if (fusionMods.dotDamageBonus > 0) {
                        dotDamage = Math.floor(dotDamage * (1 + fusionMods.dotDamageBonus));
                    }

                    // Apply DoT damage to mob
                    if (combat.currentMob && combat.mobCurrentHp > 0) {
                        combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - dotDamage);
                        logger.debug(`[COMBAT] DoT tick: ${dotDamage} damage (${combat.mobCurrentHp}/${combat.currentMob.hp} HP)`);
                        // Trigger damage callback for DoT
                        if (this.onDamageDealt) {
                            this.onDamageDealt(dotDamage, 'dot');
                        }
                    }
                }
            }
        }

        // Clear expired effects
        if (this.spellEffects.slow && now >= this.spellEffects.slow.endTime) {
            this.spellEffects.slow = null;
        }
        if (this.spellEffects.defense && now >= this.spellEffects.defense.endTime) {
            this.spellEffects.defense = null;
        }
        if (this.spellEffects.crit && now >= this.spellEffects.crit.endTime) {
            this.spellEffects.crit = null;
        }
        if (this.spellEffects.petBuff && now >= this.spellEffects.petBuff.endTime) {
            this.spellEffects.petBuff = null;
        }

        // Harmony fusion (earth+air): auto-refresh defense/crit buffs every 5s
        this.processHarmonyRefresh();

        return { dotDamage };
    }

    /**
     * Get mob's effective attack speed (with slow effect)
     * @param {Object} mob - Mob data
     * @returns {number} Effective attack speed in ms
     */
    getMobEffectiveAttackSpeed(mob) {
        let attackSpeed = mob.attackSpeed;

        // Apply slow effect (higher attack speed = slower)
        if (this.spellEffects.slow) {
            // Multiplier < 1 means slower, so we divide to increase attack interval
            attackSpeed = attackSpeed / this.spellEffects.slow.multiplier;
        }

        // Permafrost fusion (ice+earth): -25% mob attack speed (stronger slow)
        const fusionMods = this.getSpellFusionModifiers();
        if (fusionMods.mobAttackSpeedReduction > 0) {
            attackSpeed = Math.floor(attackSpeed * (1 + fusionMods.mobAttackSpeedReduction));
        }

        return attackSpeed;
    }

    /**
     * Get defense bonus from spell effects
     * @returns {number} Defense bonus
     */
    getSpellDefenseBonus() {
        if (this.spellEffects.defense) {
            let bonus = this.spellEffects.defense.bonus;
            // Harmony fusion (earth+air): buffs are 40% stronger
            const fusionMods = this.getSpellFusionModifiers();
            if (fusionMods.buffStrengthBonus > 0) {
                bonus = Math.floor(bonus * (1 + fusionMods.buffStrengthBonus));
            }
            return bonus;
        }
        return 0;
    }

    /**
     * Get crit bonus from spell effects
     * @returns {number} Crit chance bonus percentage
     */
    getSpellCritBonus() {
        if (this.spellEffects.crit) {
            let bonus = this.spellEffects.crit.bonus;
            // Harmony fusion (earth+air): buffs are 40% stronger
            const fusionMods = this.getSpellFusionModifiers();
            if (fusionMods.buffStrengthBonus > 0) {
                bonus = Math.floor(bonus * (1 + fusionMods.buffStrengthBonus));
            }
            return bonus;
        }
        return 0;
    }

    /**
     * Apply Heal over Time (HoT) effect from empowered food
     * @param {{ amount: number, duration: number, ticks: number }} hot - HoT config from food
     */
    applyHotEffect(hot) {
        if (!hot) return;
        const now = Date.now();
        // Apply Comfort Food talent (+25% HoT duration from Cooking tree)
        const hotDurationBonus = getTalentFlatBonus(SKILLS.COOKING, 'hotDurationBonus') || 0;
        const effectiveDuration = hot.duration * (1 + hotDurationBonus);
        this.spellEffects.hot = {
            amount: hot.amount,                              // HP per tick
            endTime: now + effectiveDuration,                // When effect expires
            tickInterval: effectiveDuration / hot.ticks,     // Time between ticks
            lastTick: now                                    // Last tick timestamp
        };
        logger.debug(`[COMBAT] Applied HoT: ${hot.amount} HP/tick for ${effectiveDuration}ms`);
    }

    /**
     * Process Heal over Time effect - called in tick
     * @returns {number} Amount healed this tick
     */
    processHotEffect() {
        const hot = this.spellEffects.hot;
        if (!hot) return 0;

        const now = Date.now();
        let healed = 0;

        // Check if expired
        if (now >= hot.endTime) {
            this.spellEffects.hot = null;
            logger.debug('[COMBAT] HoT effect expired');
            return 0;
        }

        // Check for tick
        if (now - hot.lastTick >= hot.tickInterval) {
            healed = gameState.healHp(hot.amount);
            hot.lastTick = now;
            if (healed > 0) {
                logger.debug(`[COMBAT] HoT tick: healed ${healed} HP`);
                // Show heal number for HoT tick
                if (this.onHeal) {
                    this.onHeal(healed);
                }
            }
        }

        return healed;
    }

    // ==================== COMBAT COMPANION SYSTEM ====================

    /**
     * Pet damage stats by role
     * Rootling: Magic, Felltracker: Melee, Ichorebound: Ranged, Chimera: Random, Eidolon: Pure
     */
    getPetCombatStats(petId) {
        const petStats = {
            rootling: { damageType: 'magic', minDamage: 8, maxDamage: 18, buffType: 'regen', buffValue: 2 },
            felltracker: { damageType: 'melee', minDamage: 12, maxDamage: 25, buffType: 'attackSpeed', buffValue: 10 },
            ichorebound: { damageType: 'ranged', minDamage: 10, maxDamage: 22, buffType: 'defense', buffValue: 5 },
            chimera: { damageType: 'random', minDamage: 10, maxDamage: 20, buffType: 'random', buffValue: 0 },
            eidolon: { damageType: 'pure', minDamage: 15, maxDamage: 28, buffType: 'all', buffValue: 0.5 }
        };
        return petStats[petId] || null;
    }

    /**
     * Check and trigger Combat Companion proc after player attack
     * @param {Object} mob - Current mob being fought
     * @returns {{ procced: boolean, damage: number, buffApplied: boolean }}
     */
    checkCombatCompanionProc(mob) {
        // Check if talent is unlocked
        const procChance = getTalentProcChance(SKILLS.HUSBANDRY, 'combatCompanion');
        if (procChance <= 0) {
            return { procced: false, damage: 0, buffApplied: false };
        }

        // Get all active pets with contracts and pick one randomly
        const pets = gameState.getPets();
        const activePets = pets.filter(p => p.unlocked && (p.contractId || p.secondaryContractId));
        if (activePets.length === 0) {
            return { procced: false, damage: 0, buffApplied: false };
        }
        const activePet = activePets[Math.floor(Math.random() * activePets.length)];

        // Roll for proc
        if (Math.random() >= procChance) {
            return { procced: false, damage: 0, buffApplied: false };
        }

        // Get pet combat stats
        const petStats = this.getPetCombatStats(activePet.id);
        if (!petStats) {
            return { procced: false, damage: 0, buffApplied: false };
        }

        // Calculate pet damage
        let damage = Math.floor(Math.random() * (petStats.maxDamage - petStats.minDamage + 1)) + petStats.minDamage;
        let actualDamageType = petStats.damageType;

        // Chimera rolls random damage type
        if (petStats.damageType === 'random') {
            const types = ['melee', 'ranged', 'magic'];
            actualDamageType = types[Math.floor(Math.random() * types.length)];
        }

        // Apply weakness/resistance for non-pure damage
        if (actualDamageType !== 'pure' && mob.weakness) {
            if (mob.weakness === actualDamageType) {
                damage = Math.floor(damage * 1.25); // 25% more damage
            } else if (mob.resistance === actualDamageType) {
                damage = Math.floor(damage * 0.75); // 25% less damage
            }
        }
        // Pure damage ignores defense and weaknesses - damage stays as is

        logger.debug(`[COMBAT COMPANION] ${activePet.id} attacks for ${damage} ${actualDamageType} damage!`);

        // Apply buff (30s duration)
        const buffApplied = this.applyPetCompanionBuff(activePet.id, petStats);

        return { procced: true, damage, buffApplied, petId: activePet.id, damageType: actualDamageType };
    }

    /**
     * Apply pet companion buff based on pet type
     * @param {string} petId - Pet ID
     * @param {Object} petStats - Pet combat stats
     * @returns {boolean} Whether buff was applied
     */
    applyPetCompanionBuff(petId, petStats) {
        const now = Date.now();
        const buffDuration = 30000; // 30 seconds

        let buffType = petStats.buffType;
        let buffValue = petStats.buffValue;

        // Chimera gets random buff type
        if (buffType === 'random') {
            const buffTypes = ['regen', 'attackSpeed', 'defense'];
            buffType = buffTypes[Math.floor(Math.random() * buffTypes.length)];
            // Use corresponding values
            const values = { regen: 2, attackSpeed: 10, defense: 5 };
            buffValue = values[buffType];
        }

        // Eidolon gets all buffs at 50% effectiveness
        if (buffType === 'all') {
            buffValue = 0.5; // Multiplier for all buff types
        }

        this.spellEffects.petBuff = {
            type: buffType,
            bonus: buffValue,
            endTime: now + buffDuration,
            petId: petId
        };

        logger.debug(`[COMBAT COMPANION] Applied ${buffType} buff (${buffValue}) for 30s from ${petId}`);
        return true;
    }

    /**
     * Get attack speed bonus from pet buff (Felltracker)
     * @returns {number} Attack speed reduction percentage (0-10)
     */
    getPetAttackSpeedBonus() {
        const buff = this.spellEffects.petBuff;
        if (!buff) return 0;

        if (buff.type === 'attackSpeed') {
            return buff.bonus; // 10% faster attacks
        }
        if (buff.type === 'all') {
            return 10 * buff.bonus; // Eidolon: 5% (10 * 0.5)
        }
        return 0;
    }

    /**
     * Get defense bonus from pet buff (Ichorebound)
     * @returns {number} Defense bonus
     */
    getPetDefenseBonus() {
        const buff = this.spellEffects.petBuff;
        if (!buff) return 0;

        if (buff.type === 'defense') {
            return buff.bonus; // +5 defense
        }
        if (buff.type === 'all') {
            return Math.floor(5 * buff.bonus); // Eidolon: +2 (5 * 0.5)
        }
        return 0;
    }

    /**
     * Get regen amount from pet buff (Rootling)
     * @returns {number} HP regen per tick
     */
    getPetRegenAmount() {
        const buff = this.spellEffects.petBuff;
        if (!buff) return 0;

        if (buff.type === 'regen') {
            return buff.bonus; // 2 HP regen per tick
        }
        if (buff.type === 'all') {
            return Math.floor(2 * buff.bonus); // Eidolon: 1 HP (2 * 0.5)
        }
        return 0;
    }

    /**
     * Process pet regen buff - called in tick (heals once per second)
     * @returns {number} Amount healed
     */
    processPetRegenBuff() {
        const buff = this.spellEffects.petBuff;
        if (!buff) return 0;

        const regenAmount = this.getPetRegenAmount();
        if (regenAmount <= 0) return 0;

        const now = Date.now();
        // Initialize lastRegenTick if not set
        if (!buff.lastRegenTick) {
            buff.lastRegenTick = now;
        }

        // Tick once per second
        if (now - buff.lastRegenTick < 1000) return 0;
        buff.lastRegenTick = now;

        const healed = gameState.healHp(regenAmount);
        if (healed > 0) {
            logger.debug(`[COMBAT COMPANION] Pet regen healed ${healed} HP`);
        }
        return healed;
    }

    /**
     * Clear all spell effects (on combat stop or death)
     */
    clearSpellEffects() {
        this.spellEffects = {
            dot: null,
            slow: null,
            defense: null,
            crit: null,
            hot: null,
            petBuff: null
        };
    }

    /**
     * Clear only mob debuffs (DoT, slow) - called on mob death
     * Player buffs (defense, crit) persist until they expire
     */
    clearMobDebuffs() {
        this.spellEffects.dot = null;
        this.spellEffects.slow = null;
    }

    /**
     * Get current spell effects state for UI
     * @returns {Object} Spell effects with remaining durations
     */
    getSpellEffectsState() {
        const now = Date.now();
        const state = {};

        if (this.spellEffects.dot) {
            state.dot = {
                active: true,
                damage: this.spellEffects.dot.damage,
                remaining: Math.max(0, this.spellEffects.dot.endTime - now)
            };
        }
        if (this.spellEffects.slow) {
            state.slow = {
                active: true,
                multiplier: this.spellEffects.slow.multiplier,
                remaining: Math.max(0, this.spellEffects.slow.endTime - now)
            };
        }
        if (this.spellEffects.defense) {
            state.defense = {
                active: true,
                bonus: this.spellEffects.defense.bonus,
                remaining: Math.max(0, this.spellEffects.defense.endTime - now)
            };
        }
        if (this.spellEffects.crit) {
            state.crit = {
                active: true,
                bonus: this.spellEffects.crit.bonus,
                remaining: Math.max(0, this.spellEffects.crit.endTime - now)
            };
        }
        if (this.spellEffects.hot) {
            state.hot = {
                active: true,
                amount: this.spellEffects.hot.amount,
                remaining: Math.max(0, this.spellEffects.hot.endTime - now)
            };
        }
        if (this.spellEffects.petBuff) {
            state.petBuff = {
                active: true,
                type: this.spellEffects.petBuff.type,
                bonus: this.spellEffects.petBuff.bonus,
                petId: this.spellEffects.petBuff.petId,
                remaining: Math.max(0, this.spellEffects.petBuff.endTime - now)
            };
        }

        return state;
    }

    // ==================== POTION BUFF SYSTEM ====================

    /**
     * Drink a potion from inventory to apply its buff
     * @param {string} potionId - Potion item ID
     * @returns {{ success: boolean, reason?: string }}
     */
    drinkPotion(potionId) {
        // Check if player has the potion
        const count = gameState.getItemCount(potionId);
        if (count <= 0) {
            return { success: false, reason: 'No potion in inventory' };
        }

        // Get potion data
        const potion = getItem(potionId);
        if (!potion || !potion.effects) {
            return { success: false, reason: 'Invalid potion' };
        }

        const effects = potion.effects;
        const category = effects.category;

        // Check for conflicts
        if (gameState.hasPotionConflict(category)) {
            const conflicts = gameState.getConflictingBuffs(category);
            // If trying to drink Ascendant, clear all existing buffs first
            if (category === 'all' && conflicts.length > 0) {
                gameState.clearAllPotionBuffs();
            } else if (conflicts.length > 0) {
                // For same-category conflicts, replace the old buff
                // For 'all' conflict, show warning
                if (conflicts.includes('all')) {
                    return { success: false, reason: 'Ascendant Concoction is active (clears all potion effects)' };
                }
                // Remove conflicting buff of same category
                gameState.removePotionBuff(category);
            }
        }

        // Check for Potion Saver proc (10% chance to not consume potion)
        const potionSaved = rollTalentProc(SKILLS.ALCHEMY, 'potionSaver');
        if (!potionSaved) {
            // Consume potion from inventory
            gameState.removeItem(potionId, 1);
        } else {
            recordTalentProc(SKILLS.ALCHEMY, 'Potion Saver', 'Potion preserved!', null);
        }

        // Apply Essence Overflow talent (+15% potion duration)
        const durationBonus = getTalentFlatBonus(SKILLS.ALCHEMY, 'potionDuration');
        const adjustedDuration = Math.floor(effects.duration * (1 + durationBonus));

        // Check for Bubbling Brews talent (5% chance for +30% effect)
        const isBubbling = rollTalentProc(SKILLS.ALCHEMY, 'bubblingBrew');
        const effectMultiplier = isBubbling ? 1.3 : 1.0;

        // Apply effect multiplier to numeric effects
        const adjustedEffects = { ...effects };
        if (isBubbling) {
            // Boost numeric effect values by 30%
            if (adjustedEffects.attackBonus) adjustedEffects.attackBonus = Math.floor(adjustedEffects.attackBonus * effectMultiplier);
            if (adjustedEffects.defenseBonus) adjustedEffects.defenseBonus = Math.floor(adjustedEffects.defenseBonus * effectMultiplier);
            if (adjustedEffects.accuracyBonus) adjustedEffects.accuracyBonus = Math.floor(adjustedEffects.accuracyBonus * effectMultiplier);
            if (adjustedEffects.speedBonus) adjustedEffects.speedBonus = adjustedEffects.speedBonus * effectMultiplier;
            if (adjustedEffects.xpBonus) adjustedEffects.xpBonus = adjustedEffects.xpBonus * effectMultiplier;
            if (adjustedEffects.lootBonus) adjustedEffects.lootBonus = adjustedEffects.lootBonus * effectMultiplier;
            recordTalentProc(SKILLS.ALCHEMY, 'Bubbling Brews', '+30% potion effect!', null);
        }

        // Create buff data
        const buffData = {
            potionId: potionId,
            potionName: potion.name,
            effects: adjustedEffects,
            combatTimeRemaining: adjustedDuration,
            originalDuration: adjustedDuration,
            startedAt: Date.now(),
            active: true,
            isBubbling: isBubbling,
            totalExtension: 0 // Track total extension for Lingering Essence cap (max 30s)
        };

        // Apply the buff
        gameState.setPotionBuff(category, buffData);
        const durationMinutes = Math.floor(adjustedDuration / 60000);
        const durationSeconds = Math.floor((adjustedDuration % 60000) / 1000);
        const durationText = durationMinutes > 0 ? `${durationMinutes}m ${durationSeconds}s` : `${durationSeconds}s`;
        logger.game(`Drank ${potion.name}! Buff active for ${durationText}.`);

        return { success: true, buff: buffData };
    }

    /**
     * Process potion buff durations - only counts down while in combat
     * Called from tick function
     * @param {number} elapsed - Time elapsed in ms
     */
    processPotionBuffs(elapsed) {
        const combat = gameState.getCombat();
        if (!combat.inCombat) return; // Only tick down while in combat

        const buffs = gameState.getPotionBuffs();
        const expiredCategories = [];

        for (const [category, buff] of Object.entries(buffs)) {
            buff.combatTimeRemaining -= elapsed;

            // Handle Rallying Elixir decay (attack bonus decays from 15 to 5 over duration)
            if (buff.effects.decays && buff.effects.attackBonus !== undefined) {
                const timeElapsed = buff.originalDuration - buff.combatTimeRemaining;
                const progress = Math.min(1, timeElapsed / buff.originalDuration);
                const maxBonus = buff.effects.attackBonus; // Original max (15)
                const minBonus = buff.effects.minBonus || 0; // Min at end (5)
                buff.currentAttackBonus = Math.floor(maxBonus - (maxBonus - minBonus) * progress);
            }

            // Check for expiration
            if (buff.combatTimeRemaining <= 0) {
                expiredCategories.push(category);
            }
        }

        // Remove expired buffs and check for auto-potion
        for (const category of expiredCategories) {
            gameState.removePotionBuff(category);
            logger.debug(`[COMBAT] Potion buff expired: ${category}`);

            // Auto-potion from Apothecary Belt
            this.checkAutoPotion(category);
        }
    }

    /**
     * Check and trigger auto-potion from Apothecary Belt when buff expires
     * @param {string} category - The buff category that expired
     */
    checkAutoPotion(category) {
        // Check if Apothecary Belt is unlocked and enabled
        if (!gameState.isApothecaryBeltUnlocked()) return;

        const belt = gameState.getApothecaryBelt();
        if (!belt.enabled) return;

        // Don't auto-drink if Ascendant Concoction ('all') is active
        if (gameState.hasPotionConflict(category)) {
            const conflicts = gameState.getConflictingBuffs(category);
            if (conflicts.includes('all')) return;
        }

        // Find a slot with a potion of this category
        const slotInfo = gameState.findApothecarySlotByCategory(category);
        if (!slotInfo || slotInfo.slot.quantity <= 0) return;

        // Consume potion from belt and apply buff
        const consumeResult = gameState.consumeApothecaryPotion(category);
        if (!consumeResult.success) return;

        // Get potion data and apply buff
        const potion = getItem(consumeResult.itemId);
        if (!potion || !potion.effects) return;

        const effects = potion.effects;

        // Apply Essence Overflow talent (+15% potion duration)
        const durationBonus = getTalentFlatBonus(SKILLS.ALCHEMY, 'potionDuration');
        const adjustedDuration = Math.floor(effects.duration * (1 + durationBonus));

        // Create buff data
        const buffData = {
            potionId: consumeResult.itemId,
            potionName: potion.name,
            effects: { ...effects },
            combatTimeRemaining: adjustedDuration,
            originalDuration: adjustedDuration,
            startedAt: Date.now(),
            active: true
        };

        // Apply the buff
        gameState.setPotionBuff(category, buffData);
        logger.debug(`[COMBAT] Auto-potion: ${potion.name} from Apothecary Belt`);
    }

    /**
     * Get total potion bonus for attack stat
     * @returns {number} Total attack bonus from potions
     */
    getPotionAttackBonus() {
        const buffs = gameState.getPotionBuffs();
        let bonus = 0;

        // Attack category potions (Feral Draft)
        if (buffs.attack) {
            bonus += buffs.attack.effects.attackBonus || 0;
        }

        // Melee category potions (Rallying Elixir - has decay)
        if (buffs.melee) {
            // Check for decay (Rallying Elixir)
            if (buffs.melee.currentAttackBonus !== undefined) {
                bonus += buffs.melee.currentAttackBonus;
            } else {
                bonus += buffs.melee.effects.attackBonus || 0;
            }
        }

        // Hybrid category (Balanced Infusion)
        if (buffs.hybrid) {
            bonus += buffs.hybrid.effects.attackBonus || 0;
        }

        // All category (Ascendant Concoction)
        if (buffs.all) {
            bonus += buffs.all.effects.attackBonus || 0;
        }

        return bonus;
    }

    /**
     * Get total potion bonus for defense stat
     * @returns {number} Total defense bonus from potions
     */
    getPotionDefenseBonus() {
        const buffs = gameState.getPotionBuffs();
        let bonus = 0;

        // Defense category potions
        if (buffs.defense) {
            bonus += buffs.defense.effects.defenseBonus || 0;
        }

        // Hybrid category (Balanced Infusion)
        if (buffs.hybrid) {
            bonus += buffs.hybrid.effects.defenseBonus || 0;
        }

        // All category (Ascendant Concoction)
        if (buffs.all) {
            bonus += buffs.all.effects.defenseBonus || 0;
        }

        return bonus;
    }

    /**
     * Get potion accuracy bonus percentage
     * @returns {number} Accuracy bonus (additive %)
     */
    getPotionAccuracyBonus() {
        const buffs = gameState.getPotionBuffs();
        let bonus = 0;

        if (buffs.accuracy) {
            bonus += buffs.accuracy.effects.accuracyBonus || 0;
        }

        if (buffs.all) {
            bonus += buffs.all.effects.accuracyBonus || 0;
        }

        return bonus;
    }

    /**
     * Get potion crit chance bonus percentage
     * @returns {number} Crit chance bonus (additive %)
     */
    getPotionCritBonus() {
        const buffs = gameState.getPotionBuffs();
        let bonus = 0;

        if (buffs.crit) {
            bonus += buffs.crit.effects.critBonus || 0;
        }

        if (buffs.all) {
            bonus += buffs.all.effects.critBonus || 0;
        }

        return bonus;
    }

    /**
     * Get potion crit damage bonus percentage
     * @returns {number} Crit damage bonus (additive %)
     */
    getPotionCritDamageBonus() {
        const buffs = gameState.getPotionBuffs();
        let bonus = 0;

        if (buffs.crit) {
            bonus += buffs.crit.effects.critDamageBonus || 0;
        }

        if (buffs.all) {
            bonus += buffs.all.effects.critDamageBonus || 0;
        }

        return bonus;
    }

    /**
     * Get potion attack speed bonus percentage
     * @returns {number} Speed bonus (reduces attack interval by this %)
     */
    getPotionSpeedBonus() {
        const buffs = gameState.getPotionBuffs();
        let bonus = 0;

        if (buffs.speed) {
            bonus += buffs.speed.effects.speedBonus || 0;
        }

        // All category (Ascendant Concoction)
        if (buffs.all) {
            bonus += buffs.all.effects.speedBonus || 0;
        }

        return bonus;
    }

    /**
     * Get potion damage reduction percentage
     * @returns {number} Damage reduction % (0-100)
     */
    getPotionDamageReduction() {
        const buffs = gameState.getPotionBuffs();
        if (buffs.reduction) {
            return buffs.reduction.effects.damageReduction || 0;
        }
        return 0;
    }

    /**
     * Get potion enemy accuracy reduction percentage
     * @returns {number} Enemy accuracy reduction % (0-100)
     */
    getPotionEvasionBonus() {
        const buffs = gameState.getPotionBuffs();
        if (buffs.evasion) {
            return buffs.evasion.effects.enemyAccuracyReduction || 0;
        }
        return 0;
    }

    /**
     * Get potion melee damage bonus percentage
     * @returns {number} Melee damage bonus % (additive)
     */
    getPotionMeleeDamageBonus() {
        const buffs = gameState.getPotionBuffs();
        // Check melee-specific buff (Rallying Elixir) or Ascendant
        if (buffs.melee) {
            return buffs.melee.effects.meleeDamageBonus || 0;
        }
        if (buffs.all) {
            return buffs.all.effects.meleeDamageBonus || 0;
        }
        return 0;
    }

    /**
     * Get potion magic damage bonus percentage
     * @returns {number} Magic damage bonus % (additive)
     */
    getPotionMagicDamageBonus() {
        const buffs = gameState.getPotionBuffs();
        // Check magic-specific buff (Arcane Tincture) or Ascendant
        if (buffs.magic) {
            return buffs.magic.effects.magicDamageBonus || 0;
        }
        if (buffs.all) {
            return buffs.all.effects.magicDamageBonus || 0;
        }
        return 0;
    }

    /**
     * Get potion ranged damage bonus percentage
     * @returns {number} Ranged damage bonus % (additive)
     */
    getPotionRangedDamageBonus() {
        const buffs = gameState.getPotionBuffs();
        // Check ranged-specific buff (Sharpshot Brew) or Ascendant
        if (buffs.ranged) {
            return buffs.ranged.effects.rangedDamageBonus || 0;
        }
        if (buffs.all) {
            return buffs.all.effects.rangedDamageBonus || 0;
        }
        return 0;
    }

    /**
     * Get potion glyph save chance percentage
     * @returns {number} Glyph save chance % (0-100)
     */
    getPotionGlyphSaveChance() {
        const buffs = gameState.getPotionBuffs();
        if (buffs.magic) {
            return buffs.magic.effects.glyphSaveChance || 0;
        }
        return 0;
    }

    /**
     * Get potion ammo save chance percentage
     * @returns {number} Ammo save chance % (0-100)
     */
    getPotionAmmoSaveChance() {
        const buffs = gameState.getPotionBuffs();
        if (buffs.ranged) {
            return buffs.ranged.effects.ammoSaveChance || 0;
        }
        return 0;
    }

    /**
     * Get potion corruption proc bonus percentage
     * @returns {number} Corruption proc chance bonus % (additive)
     */
    getPotionCorruptionBonus() {
        const buffs = gameState.getPotionBuffs();
        if (buffs.all) {
            return buffs.all.effects.corruptionBonus || 0;
        }
        return 0;
    }

    /**
     * Get active potion buffs state for UI display
     * @returns {Object[]} Array of active buffs with remaining time
     */
    getPotionBuffsState() {
        const buffs = gameState.getPotionBuffs();
        const state = [];

        for (const [category, buff] of Object.entries(buffs)) {
            state.push({
                category,
                potionId: buff.potionId,
                potionName: buff.potionName,
                effects: buff.effects,
                combatTimeRemaining: buff.combatTimeRemaining,
                originalDuration: buff.originalDuration,
                currentAttackBonus: buff.currentAttackBonus // For Rallying Elixir decay
            });
        }

        return state;
    }

    /**
     * Combat tick function - called every game tick
     * @param {number} speedMultiplier - Speed multiplier from admin panel
     */
    tick(speedMultiplier = 1) {
        const combat = gameState.getCombat();

        // Handle respawn timer
        if (combat.isDead) {
            const remaining = gameState.getRespawnTimeRemaining();
            if (remaining <= 0) {
                this.handleRespawn();
            }
            return;
        }

        // Handle kill delay - wait before spawning next mob
        if (this.mobKillTime && !combat.currentMob && combat.inCombat) {
            const elapsed = Date.now() - this.mobKillTime;
            if (elapsed >= this.killDelay) {
                this.mobKillTime = null;
                this.spawnNextMob();
            }
            return; // Wait for delay or new mob to spawn
        }

        if (!combat.inCombat || !combat.currentMob) return;

        const now = Date.now();
        const playerStats = this.getPlayerStats();
        const mob = combat.currentMob;

        // Get Pyre penalty for speed
        const pyrePenalty = this.getPyrePenalty(combat.currentZone);

        // Process spell effects (DoT damage, expiration)
        const spellEffectResult = this.processSpellEffects();

        // Process HoT effect (heal over time from empowered food)
        this.processHotEffect();

        // Process potion buff durations (only while in combat)
        // Tick rate is 100ms, apply speed multiplier
        this.processPotionBuffs(100 * speedMultiplier);

        // Process combat talent effects
        // Combat regen (heal 1 HP every 5s from Regeneration talent)
        this.processCombatRegen();

        // Process pet companion regen (Rootling/Eidolon buff, 2/1 HP per second)
        this.processPetRegenBuff();

        // Process bleed damage on mob (from Deep Wounds / Barbed Arrows talents)
        const bleedDamage = this.processBleedDamage();
        if (bleedDamage > 0) {
            combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - bleedDamage);
            if (this.onDamageDealt) {
                this.onDamageDealt(bleedDamage, 'bleed');
            }
        }

        // Process Searing Arrows DoT (Cindered Shortbow weapon effect)
        const searingDotDamage = this.processSearingArrowsDot();
        if (searingDotDamage > 0 && combat.mobCurrentHp > 0) {
            combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - searingDotDamage);
            if (this.onDamageDealt) {
                this.onDamageDealt(searingDotDamage, 'dot');
            }
        }

        // Process Smoldering Resonance DoT (Emberheart Focus offhand effect)
        const smolderingDotDamage = this.processSmolderingDot();
        if (smolderingDotDamage > 0 && combat.mobCurrentHp > 0) {
            combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - smolderingDotDamage);
            if (this.onDamageDealt) {
                this.onDamageDealt(smolderingDotDamage, 'dot');
            }
        }

        // Process Afterimage clone attacks (Ashforged 6pc, 2H Sword)
        const afterimageDamage = this.processAfterimage(playerStats.attackSpeed);
        if (afterimageDamage > 0 && combat.mobCurrentHp > 0) {
            combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - afterimageDamage);
            if (this.onDamageDealt) {
                this.onDamageDealt(afterimageDamage, 'damage');
            }
        }

        // Process Phantom Archer spectral shots (Cindermark 6pc)
        const phantomDamage = this.processPhantomArcherShots();
        if (phantomDamage > 0 && combat.mobCurrentHp > 0) {
            combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - phantomDamage);
            if (this.onDamageDealt) {
                this.onDamageDealt(phantomDamage, 'damage');
            }
        }

        // Check if DoT or bleed killed the mob
        if (combat.mobCurrentHp <= 0) {
            this.clearMobDebuffs(); // Clear mob debuffs BEFORE completeFight (which re-renders UI)
            this.completeFight();
            this.lastKilledMob = mob;
            combat.currentMob = null;
            this.mobKillTime = Date.now();
            return;
        }

        // Process boss mechanics (phases, enrage, periodic effects)
        // Must run before player/mob attacks so modifiers are active this tick
        this.processBossMechanics();
        // Boss mechanics may kill the player (true damage, DoTs)
        if (gameState.isDead()) return;

        // Process mob mechanic DoT (fire, poison)
        const mobDotDamage = this.processMobDotTick();
        if (mobDotDamage > 0) {
            const dotResult = this.calculateDamageToPlayer(mobDotDamage, true);
            const result = gameState.takeDamage(dotResult.damage);
            logger.debug(`[COMBAT] Mob mechanic DoT dealt ${dotResult.damage} damage to player`);
            if (this.onDamageTaken) {
                this.onDamageTaken(dotResult.damage, 'dot');
            }
            if (result.died) {
                this.handleDeath();
                this.clearSpellEffects();
                return;
            }
        }

        // Player attack check (apply Pyre speed penalty + Relentless speed bonus + mob snare)
        const relentlessBonus = this.getRelentlessSpeedBonus();
        const snareAdjustedSpeed = this.getSnareModifiedSpeed(playerStats.attackSpeed);
        const playerAttackInterval = (snareAdjustedSpeed * pyrePenalty.speedMult * (1 - relentlessBonus)) / speedMultiplier;
        if (now - combat.lastPlayerAttack >= playerAttackInterval) {
            combat.lastPlayerAttack = now;

            // Check for mob phase shift (dodge entire player attack)
            const mechanicResult = this.checkMobMechanicOnPlayerAttack(mob);
            if (mechanicResult.dodged) {
                if (this.onDamageDealt) {
                    this.onDamageDealt(0, 'evaded');
                }
            }
            // Handle magic attack style
            else if (playerStats.attackStyle === 'magic') {
                const castResult = this.canCastSpell();
                if (!castResult.success) {
                    logger.debug(`Cannot cast spell: ${castResult.reason}`);
                    return; // Can't attack without spell/glyphs (UI shows warning)
                }

                // Cast spell and deal damage
                const spellResult = this.castSpell();
                if (spellResult.success) {
                    // Check if mob evades (based on mob defense, reduced by style weakness)
                    // Note: Magic uses mob's magic defense which is typically lower
                    if (this.checkMobEvasion()) {
                        if (this.onDamageDealt) {
                            this.onDamageDealt(0, 'evaded');
                        }
                    } else {
                    // Shatter fusion (fire+ice): next hit deals 2x damage
                    const spellDamage = this.processShatter(spellResult.damage);
                    combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - spellDamage);
                    logger.debug(`Cast ${spellResult.spell.name} on ${mob.name} for ${spellDamage} damage (${combat.mobCurrentHp}/${mob.hp} HP)`);
                    // Trigger damage callback for spell
                    if (this.onDamageDealt) {
                        this.onDamageDealt(spellDamage, 'damage');
                    }

                    // Process lifesteal (Bloodfang Pendant, Eclipse Reaver)
                    this.processLifesteal(spellDamage);

                    // Mob mechanic: damage reflection
                    const magicReflect = this.getMobReflectDamage(mob, spellDamage);
                    if (magicReflect > 0) {
                        const reflResult = gameState.takeDamage(magicReflect);
                        logger.debug(`[COMBAT] ${mob.name} reflected ${magicReflect} damage!`);
                        if (this.onDamageTaken) this.onDamageTaken(magicReflect, 'damage');
                        if (reflResult.died) { this.handleDeath(); this.clearSpellEffects(); return; }
                    }

                    // Check for Combat Companion proc (magic)
                    const companionResult = this.checkCombatCompanionProc(mob);
                    if (companionResult.procced && combat.mobCurrentHp > 0) {
                        combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - companionResult.damage);
                        if (this.onDamageDealt) {
                            this.onDamageDealt(companionResult.damage, 'pet');
                        }
                    }

                    // Roll for Smoldering Resonance fire DoT (Emberheart Focus)
                    this.applySmolderingResonance();

                    // Roll for Corrupted Prey on spell cast
                    this.rollCombatCorruption();

                    if (combat.mobCurrentHp <= 0) {
                        this.clearMobDebuffs(); // Clear mob debuffs BEFORE completeFight
                        this.completeFight();
                        this.lastKilledMob = mob;
                        combat.currentMob = null;
                        this.mobKillTime = Date.now();
                        return;
                    }
                    } // End of mob evasion else block
                }
            }
            // Handle ranged attack style
            else if (playerStats.attackStyle === 'ranged') {
                const ammo = gameState.getEquippedAmmo();
                const builtInAmmo = this.getBuiltInAmmo();
                const gear = gameState.getEquippedGear();
                const weapon = gear.weapon ? getItem(gear.weapon) : null;
                const hasFreeAmmo = weapon?.freeAmmo === true;

                // Check if player has ammo (equipped or built-in)
                const hasEquippedAmmo = ammo.itemId && ammo.quantity > 0;
                const hasBuiltInAmmo = !!builtInAmmo;

                if (!hasEquippedAmmo && !hasBuiltInAmmo) {
                    logger.debug('No ammo equipped and no built-in ammo - attack skipped');
                    return; // Can't attack without ammo (UI shows warning)
                }

                // Use built-in ammo if no equipped ammo available
                const usingBuiltIn = !hasEquippedAmmo && hasBuiltInAmmo;

                // Only consume ammo if using equipped ammo and weapon doesn't have freeAmmo
                if (!usingBuiltIn && !hasFreeAmmo) {
                    // Check for ammo save chance (potion + Ammo Conservation talent)
                    const potionAmmoSave = this.getPotionAmmoSaveChance();
                    const talentAmmoSave = getTalentProcChance(SKILLS.COMBAT, 'ammoSave') * 100; // Convert to %
                    const totalAmmoSaveChance = potionAmmoSave + talentAmmoSave;
                    const ammoSaved = totalAmmoSaveChance > 0 && Math.random() * 100 < totalAmmoSaveChance;
                    if (!ammoSaved) {
                        // consumeAmmo may still save ammo via quiver efficiency
                        const ammoResult = gameState.consumeAmmo(1);
                        if (ammoResult.saved) {
                            logger.debug(`[COMBAT] Quiver efficiency proc! No ammo consumed.`);
                        }
                    } else {
                        logger.debug(`[COMBAT] Ammo save proc! No ammo consumed.`);
                    }
                } else if (usingBuiltIn) {
                    logger.debug(`[COMBAT] Using built-in ammo (${builtInAmmo.name})`);
                } else if (hasFreeAmmo) {
                    logger.debug(`[COMBAT] Free ammo (Eclipse Piercer) - no consumption`);
                }

                // Roll for hit using effective accuracy (includes style modifiers)
                const playerAccuracy = this.getEffectiveAccuracy();
                if (this.rollHit(playerAccuracy)) {
                    // Check if mob evades (based on mob defense, reduced by style weakness)
                    if (this.checkMobEvasion()) {
                        if (this.onDamageDealt) {
                            this.onDamageDealt(0, 'evaded');
                        }
                    } else {
                    let damage = this.calculatePlayerDamage();
                    let totalDamage = damage;
                    let arrowCount = 1;

                    // Apply bleed from Barbed Arrows talent
                    this.applyBleedToMob('ranged');

                    // Check for Arrow Storm proc (10% chance for 5 arrows)
                    const arrowStormChance = getTalentProcChance(SKILLS.COMBAT, 'arrowStorm');
                    if (arrowStormChance > 0 && Math.random() < arrowStormChance) {
                        // Fire 4 extra arrows (5 total), no extra ammo
                        for (let i = 0; i < 4; i++) {
                            if (this.rollHit(playerAccuracy)) {
                                totalDamage += this.calculatePlayerDamage();
                            }
                        }
                        arrowCount = 5;
                        logger.debug(`[COMBAT] Arrow Storm! Fired 5 arrows`);
                    }
                    // Check for Multi-Shot proc (20% chance for 2 extra arrows)
                    else {
                        const multiShotChance = getTalentProcChance(SKILLS.COMBAT, 'multiShot');
                        if (multiShotChance > 0 && Math.random() < multiShotChance) {
                            // Fire 2 extra arrows, no ammo cost
                            for (let i = 0; i < 2; i++) {
                                if (this.rollHit(playerAccuracy)) {
                                    totalDamage += this.calculatePlayerDamage();
                                }
                            }
                            arrowCount = 3;
                            logger.debug(`[COMBAT] Multi-Shot! Fired 3 arrows`);
                        }
                    }

                    // Process weapon hit effects (hit counter for Smoldering Strikes, Eclipse Slash)
                    const rangedWeaponBonus = this.processWeaponHitEffects(damage);

                    // Process Volatile Arrows bonus fire damage (Cindered Quiver)
                    const volatileBonus = this.processVolatileArrows(damage, this.lastHitWasCrit);

                    // Apply Searing Arrows ignite DoT (Cindered Shortbow)
                    this.applySearingArrowsDot(damage);

                    totalDamage += rangedWeaponBonus + volatileBonus;
                    combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - totalDamage);

                    logger.debug(`Player hit ${mob.name} for ${totalDamage} damage with ${arrowCount} arrow(s) (${combat.mobCurrentHp}/${mob.hp} HP)`);
                    // Trigger damage callback
                    if (this.onDamageDealt) {
                        this.onDamageDealt(totalDamage, 'damage');
                    }

                    // Process lifesteal (Bloodfang Pendant, Eclipse Reaver)
                    this.processLifesteal(totalDamage);

                    // Mob mechanic: damage reflection
                    const rangedReflect = this.getMobReflectDamage(mob, totalDamage);
                    if (rangedReflect > 0) {
                        const reflResult = gameState.takeDamage(rangedReflect);
                        logger.debug(`[COMBAT] ${mob.name} reflected ${rangedReflect} damage!`);
                        if (this.onDamageTaken) this.onDamageTaken(rangedReflect, 'damage');
                        if (reflResult.died) { this.handleDeath(); this.clearSpellEffects(); return; }
                    }

                    // Check for Combat Companion proc (ranged)
                    const companionResult = this.checkCombatCompanionProc(mob);
                    if (companionResult.procced && combat.mobCurrentHp > 0) {
                        combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - companionResult.damage);
                        if (this.onDamageDealt) {
                            this.onDamageDealt(companionResult.damage, 'pet');
                        }
                    }

                    // Roll for Corrupted Prey on player attack
                    this.rollCombatCorruption();

                    // Piercing Shot (Cindermark 4pc): every 5th ranged hit fires a piercing shot
                    if (combat.mobCurrentHp > 0) {
                        const piercingResult = this.processPiercingShot();
                        if (piercingResult.triggered && piercingResult.damage > 0) {
                            combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - piercingResult.damage);
                            if (this.onDamageDealt) {
                                this.onDamageDealt(piercingResult.damage, 'damage');
                            }
                        }
                    }

                    if (combat.mobCurrentHp <= 0) {
                        this.completeFight();
                        this.lastKilledMob = mob;
                        combat.currentMob = null;
                        this.mobKillTime = Date.now();
                        return;
                    }
                    } // End of mob evasion else block
                } else {
                    logger.debug(`Player missed ${mob.name}!`);
                    // Trigger miss callback
                    if (this.onDamageDealt) {
                        this.onDamageDealt(0, 'miss');
                    }
                }
            }
            // Handle melee attack style (default)
            else {
                // Roll for hit using effective accuracy (includes style modifiers)
                const playerAccuracy = this.getEffectiveAccuracy();
                if (this.rollHit(playerAccuracy)) {
                    // Check if mob evades (based on mob defense, reduced by style weakness)
                    if (this.checkMobEvasion()) {
                        if (this.onDamageDealt) {
                            this.onDamageDealt(0, 'evaded');
                        }
                    } else {
                    let damage = this.calculatePlayerDamage();
                    let totalDamage = damage;

                    // Apply bleed from Deep Wounds talent
                    this.applyBleedToMob('melee');

                    // Check for Whirlwind proc (15% chance to strike twice)
                    const whirlwindChance = getTalentProcChance(SKILLS.COMBAT, 'doubleStrike');
                    if (whirlwindChance > 0 && Math.random() < whirlwindChance) {
                        const bonusDamage = this.calculatePlayerDamage();
                        totalDamage += bonusDamage;
                        logger.debug(`[COMBAT] Whirlwind! Extra strike for ${bonusDamage} damage`);
                    }

                    // Process weapon hit effects (Smoldering Strikes every 4th, Eclipse Slash every 3rd)
                    const meleeWeaponBonus = this.processWeaponHitEffects(damage);
                    totalDamage += meleeWeaponBonus;

                    // Devastating Blow (Ashforged 4pc): at max Momentum, 20% chance for 4x + lifesteal
                    const devBlow = this.processDevastatingBlow(totalDamage);
                    if (devBlow.triggered) {
                        totalDamage = devBlow.damage;
                    }

                    combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - totalDamage);

                    logger.debug(`Player hit ${mob.name} for ${totalDamage} damage (${combat.mobCurrentHp}/${mob.hp} HP)`);
                    // Trigger damage callback
                    if (this.onDamageDealt) {
                        this.onDamageDealt(totalDamage, 'damage');
                    }

                    // Process lifesteal (Bloodfang Pendant, Eclipse Reaver)
                    this.processLifesteal(totalDamage);

                    // Mob mechanic: damage reflection
                    const meleeReflect = this.getMobReflectDamage(mob, totalDamage);
                    if (meleeReflect > 0) {
                        const reflResult = gameState.takeDamage(meleeReflect);
                        logger.debug(`[COMBAT] ${mob.name} reflected ${meleeReflect} damage!`);
                        if (this.onDamageTaken) this.onDamageTaken(meleeReflect, 'damage');
                        if (reflResult.died) { this.handleDeath(); this.clearSpellEffects(); return; }
                    }

                    // Build Momentum stacks (Ashforged 2pc)
                    this.processMomentumHit();

                    // Check for Combat Companion proc (melee)
                    const companionResult = this.checkCombatCompanionProc(mob);
                    if (companionResult.procced && combat.mobCurrentHp > 0) {
                        combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - companionResult.damage);
                        if (this.onDamageDealt) {
                            this.onDamageDealt(companionResult.damage, 'pet');
                        }
                    }

                    // Roll for Corrupted Prey on player attack
                    this.rollCombatCorruption();

                    if (combat.mobCurrentHp <= 0) {
                        this.completeFight();
                        this.lastKilledMob = mob;
                        combat.currentMob = null;
                        this.mobKillTime = Date.now();
                        return;
                    }
                    } // End of mob evasion else block
                } else {
                    logger.debug(`Player missed ${mob.name}!`);
                    // Trigger miss callback
                    if (this.onDamageDealt) {
                        this.onDamageDealt(0, 'miss');
                    }
                }
            }
        }

        // Mob attack check (apply slow effect + boss speed + mob mechanic modifiers to attack speed)
        let effectiveMobSpeed = this.getMobEffectiveAttackSpeed(mob);
        // Apply boss speed modifier (enrage/phase: <1 = faster attacks)
        const bossMobMods = this.getBossModifiers();
        if (bossMobMods.mobSpeedMult !== 1.0) {
            effectiveMobSpeed = Math.floor(effectiveMobSpeed * bossMobMods.mobSpeedMult);
        }
        // Apply mob mechanic speed modifier (enrage, time warp)
        const mechanicSpeed = this.getMobMechanicAttackSpeed(mob);
        if (mechanicSpeed !== mob.attackSpeed) {
            // Scale proportionally: ratio of mechanic speed to base speed
            effectiveMobSpeed = Math.floor(effectiveMobSpeed * (mechanicSpeed / mob.attackSpeed));
        }
        const mobAttackInterval = effectiveMobSpeed / speedMultiplier;
        if (now - combat.lastMobAttack >= mobAttackInterval) {
            combat.lastMobAttack = now;

            // Roll for mob hit using mob's accuracy
            let mobAccuracy = mob.accuracy || 70;  // Default 70% if not defined
            // Apply evasion potion effect (reduces mob accuracy)
            const evasionBonus = this.getPotionEvasionBonus();
            if (evasionBonus > 0) {
                mobAccuracy = Math.max(5, mobAccuracy * (1 - evasionBonus / 100));
            }
            if (this.rollHit(mobAccuracy)) {
                // Mob hit! Calculate and deal damage
                const rawDamage = calculateMobDamage(mob);
                const damageResult = this.calculateDamageToPlayer(rawDamage);
                const damage = damageResult.damage;

                // Check Last Stand talent before applying damage
                const currentHp = gameState.getCurrentHp();
                const lastStandResult = this.checkLastStand(damage, currentHp);

                if (lastStandResult.triggered) {
                    // Last Stand saved us! Set HP to 1 instead of dying
                    gameState.setCurrentHp(1);
                    logger.debug(`${mob.name} hit player for ${damage} damage - Last Stand activated! (1/${gameState.getMaxHp()} HP)`);
                } else {
                    const result = gameState.takeDamage(damage);
                    logger.debug(`${mob.name} hit player for ${damage} damage (${gameState.getCurrentHp()}/${gameState.getMaxHp()} HP)`);

                    if (result.died) {
                        this.handleDeath();
                        this.clearSpellEffects(); // Clear effects on death
                        return;
                    }
                }

                // Trigger damage taken callback (include block/evade status)
                if (this.onDamageTaken) {
                    const hitType = damageResult.evaded ? 'evaded' : (damageResult.blocked ? 'blocked' : 'damage');
                    this.onDamageTaken(damage, hitType);
                }

                // Process equipment defensive effects (counter, shield buff, heal, reflect)
                const defenseEffects = this.processOnHitDefensiveEffects(damage, damageResult.blocked);

                // Apply counter damage from Searing Parry
                if (defenseEffects.counterDamage > 0 && combat.mobCurrentHp > 0) {
                    combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - defenseEffects.counterDamage);
                    if (this.onDamageDealt) {
                        this.onDamageDealt(defenseEffects.counterDamage, 'damage');
                    }
                    if (combat.mobCurrentHp <= 0) {
                        this.completeFight();
                        this.lastKilledMob = mob;
                        combat.currentMob = null;
                        this.mobKillTime = Date.now();
                        return;
                    }
                }

                // Show heal from Warden's Resolve
                if (defenseEffects.healed > 0 && this.onHeal) {
                    this.onHeal(defenseEffects.healed);
                }

                // Apply Thorn Armor reflection damage
                const thornReflect = this.getCombatTalentThornDamage();
                let totalReflect = 0;
                if (thornReflect > 0) {
                    totalReflect += Math.floor(damage * thornReflect);
                }
                // Add Eclipse Reflection damage
                totalReflect += defenseEffects.reflectDamage;

                if (totalReflect > 0 && combat.mobCurrentHp > 0) {
                    combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - totalReflect);
                    logger.debug(`[COMBAT] Reflected ${totalReflect} damage!`);

                    // Check if reflection killed the mob
                    if (combat.mobCurrentHp <= 0) {
                        this.completeFight();
                        this.lastKilledMob = mob;
                        combat.currentMob = null;
                        this.mobKillTime = Date.now();
                        return;
                    }
                }

                // Riposte counter (Ashforged 6pc, 1H weapon): every 3rd hit taken
                if (combat.mobCurrentHp > 0) {
                    const riposteDamage = this.processRiposteCounter();
                    if (riposteDamage > 0) {
                        combat.mobCurrentHp = Math.max(0, combat.mobCurrentHp - riposteDamage);
                        if (this.onDamageDealt) {
                            this.onDamageDealt(riposteDamage, 'damage');
                        }
                        if (combat.mobCurrentHp <= 0) {
                            this.completeFight();
                            this.lastKilledMob = mob;
                            combat.currentMob = null;
                            this.mobKillTime = Date.now();
                            return;
                        }
                    }
                }

                // Apply mob mechanic effects (snare, DoT, curse, armor shred, self-heal, absorption)
                this.applyMobMechanicOnHit(mob, damage);
            } else {
                // Mob missed!
                logger.debug(`${mob.name} missed!`);
                // Trigger miss callback for mob
                if (this.onDamageTaken) {
                    this.onDamageTaken(0, 'miss');
                }
            }
        }

        // Auto-eat check
        this.checkAutoEat(speedMultiplier);

        // Trigger UI update
        if (this.onCombatUpdate) {
            this.onCombatUpdate();
        }
    }

    /**
     * Complete a fight (mob killed)
     */
    completeFight() {
        const combat = gameState.getCombat();
        const mob = combat.currentMob;
        const zoneId = combat.currentZone;

        if (!mob || !zoneId) return;

        const zone = getZone(zoneId);

        // Add Berserker's Rampage stack (if talent unlocked)
        this.addBerserkerStack();

        // Clear bleed when mob dies
        this.talentState.mobBleed = null;

        // Clear mob mechanic state when mob dies
        this.clearMobMechanicState();

        // Store old max HP before level up for HP scaling
        const oldMaxHp = gameState.getMaxHp();

        // Award XP (apply Corrupted Prey XP mark if active)
        let effectiveXP = mob.xp;
        if (this.corruptedPrey.marked) {
            effectiveXP = Math.floor(effectiveXP * this.corruptedPrey.xpMultiplier);
        }
        const xpResult = addXP(SKILLS.COMBAT, effectiveXP, mob.id, { skipCorruption: true });
        logger.game(`Killed ${mob.name} (+${effectiveXP} XP${this.corruptedPrey.marked ? ' [Corrupted Prey]' : ''})`);

        // Level-up HP adjustment: scale HP proportionally when max HP increases
        if (xpResult.leveledUp) {
            const newMaxHp = gameState.getMaxHp();
            if (newMaxHp > oldMaxHp) {
                // Scale current HP proportionally to new max
                const hpRatio = combat.currentHp / oldMaxHp;
                const newHp = Math.floor(newMaxHp * hpRatio);
                // Add a small bonus for leveling up (heal 10% of new max)
                const bonusHeal = Math.floor(newMaxHp * 0.1);
                gameState.setCurrentHp(Math.min(newHp + bonusHeal, newMaxHp));
                logger.game(`Max HP increased to ${newMaxHp}!`);
            }
        }

        // Check for Lingering Essence talent (15% chance to extend a random potion)
        if (rollTalentProc(SKILLS.ALCHEMY, 'lingeringEssence')) {
            const buffs = gameState.getPotionBuffs();
            const activeBuffs = Object.entries(buffs).filter(([_, buff]) =>
                buff && buff.combatTimeRemaining > 0 &&
                (buff.totalExtension || 0) < 30000 // Max 30s extension
            );

            if (activeBuffs.length > 0) {
                // Pick random active buff
                const [category, buff] = activeBuffs[Math.floor(Math.random() * activeBuffs.length)];
                const extensionAmount = Math.min(5000, 30000 - (buff.totalExtension || 0)); // 5s, capped at 30s total

                if (extensionAmount > 0) {
                    buff.combatTimeRemaining += extensionAmount;
                    buff.totalExtension = (buff.totalExtension || 0) + extensionAmount;
                    gameState.setPotionBuff(category, buff);
                    logger.game(`Lingering Essence! ${buff.potionName} extended by ${extensionAmount / 1000}s`);
                    recordTalentProc(SKILLS.ALCHEMY, 'Lingering Essence', `+${extensionAmount / 1000}s potion!`, null);
                }
            }
        }

        // Track kill for bounty system
        const prevKillCount = gameState.getKillCount(mob.id);
        gameState.trackKill(mob.id);
        bountySkill.trackKillProgress(mob.id);

        // Check for bounty mark rank-up
        const newKillCount = prevKillCount + 1;
        import('../../ui/bountyMarkNotification.js').then(({ checkBountyMarkRankUp }) => {
            checkBountyMarkRankUp(mob.id, mob.name, prevKillCount, newKillCount);
        });

        // Track boss kill and clear boss state
        const wasBossKill = !!this.bossState;
        if (wasBossKill) {
            gameState.recordBossKill(mob.id);
            logger.game(`Boss defeated: ${mob.name}!`);
            this.clearBossState();
        }

        // Clear target-specific equipment effects (keep player buffs like Ember Shield, Relentless)
        this.equipmentState.searingArrowsDot = null;
        this.equipmentState.smolderingDot = null;
        this.equipmentState.markedPrey = null;
        this.equipmentState.hitCounter = 0;

        // Clear target-specific set bonus state (keep player-side buffs)
        this.clearTargetSetBonusState();

        // Track zone kills and check for zone clear
        const lifetimeKills = gameState.incrementZoneLifetimeKills(zoneId);
        const wasCleared = gameState.isZoneCleared(zoneId);
        if (!wasCleared && zone && lifetimeKills >= zone.clearThreshold) {
            gameState.clearZone(zoneId, lifetimeKills);
            logger.game(`Zone cleared: ${zone.name}! You can now target specific mobs.`);

            // Trigger zone cleared callback for UI
            if (this.onZoneCleared) {
                this.onZoneCleared(zone);
            }
        }

        // Roll for loot - add to combat loot chest (not directly to inventory)
        // Check for Bloom Touched talent (auto-deposit herbs to BloomHold)
        const hasBloomTouched = getTalentFlatBonus(SKILLS.ALCHEMY, 'bloomAutoDeposit') > 0;
        const bloomholdUnlocked = gameState.isBloomholdUnlocked();

        const loot = this.rollLoot(zoneId, mob.id);

        // Apply Corrupted Prey loot mark (multiply quantities)
        if (this.corruptedPrey.marked) {
            for (const drop of loot) {
                drop.quantity = Math.floor(drop.quantity * this.corruptedPrey.lootMultiplier);
            }
        }

        // Pilfering Bonus Loot Find - 20% chance for +50% bonus drops from combat
        if (rollTalentProc(SKILLS.PILFERING, 'bonusLootFind')) {
            for (const drop of loot) {
                const bonusQty = Math.max(1, Math.floor(drop.quantity * 0.5));
                drop.quantity += bonusQty;
            }
            recordTalentProc(SKILLS.PILFERING, 'Bonus Loot', 'Extra combat drops!', null);
        }

        let lootChestFull = false;
        for (const drop of loot) {
            const item = getItem(drop.itemId);
            const name = item ? item.name : drop.itemId;

            // Track loot history for Bestiary
            gameState.trackMobLoot(mob.id, drop.itemId, drop.quantity);

            // Check if this is a herb and should go to BloomHold
            const isHerb = drop.itemId.startsWith('grimy_');
            if (isHerb && hasBloomTouched && bloomholdUnlocked) {
                // Auto-deposit herb to BloomHold
                gameState.addBloomholdItem(drop.itemId, drop.quantity);
                logger.game(`Bloom Touched: ${drop.quantity}x ${name} sent to BloomHold`);
            } else {
                const lootResult = gameState.addCombatLoot(drop.itemId, drop.quantity);
                if (!lootResult.success) lootChestFull = true;
                logger.debug(`[COMBAT] Added ${drop.quantity}x ${name} to loot chest`);
            }
            // Track progress for bounty contracts when loot is AWARDED (not when claimed)
            bountySkill.trackItemProgress(drop.itemId, drop.quantity);
        }

        // Warn if chest is at capacity or loot was lost
        if (lootChestFull) {
            toastWarning('Loot chest full! Collect loot to avoid losing drops.');
        } else if (gameState.getCombatLootSlotCount() >= gameState.getCombatLootCapacity()) {
            toastWarning('Loot chest full! Collect loot to make room.');
        }

        // Show boss loot notification for notable drops
        if (wasBossKill && loot.length > 0) {
            const notableDrops = loot.filter(drop => {
                const item = getItem(drop.itemId);
                return item && (item.specialEffect || item.uniqueEffect || item.setId || item.slot);
            });
            if (notableDrops.length > 0) {
                const dropNames = notableDrops.map(d => {
                    const item = getItem(d.itemId);
                    return item ? item.name : d.itemId;
                }).join(', ');
                toastSuccess(`Boss loot: ${dropNames}`);
            }
        }

        // Clear Corrupted Prey mark after applying bonuses
        this.clearCorruptedPrey();

        // Check for pilferer contract assistance (rolls once per mob kill)
        pilferingSkill.tryContractAssist('combat');

        // Callback for UI
        if (this.onMobKilled) {
            this.onMobKilled(mob, xpResult, loot);
        }
    }

    /**
     * Roll for loot drops from killing a mob
     * @param {string} zoneId - Zone ID
     * @param {string} mobId - Mob ID for per-mob loot
     * @returns {Array<{ itemId: string, quantity: number }>}
     */
    rollLoot(zoneId, mobId) {
        // Use per-mob loot if available, fall back to zone loot
        const lootTable = getMobLoot(mobId, zoneId);
        const drops = [];

        // Get Pyre loot penalty
        const pyrePenalty = this.getPyrePenalty(zoneId);

        // Bounty mark drop rate bonus
        const mark = getBountyMark(mobId);
        const markDropBonus = mark.dropRate;

        // Roll common drops (apply Pyre loot penalty + bounty mark bonus to chance)
        for (const drop of lootTable.common) {
            const adjustedChance = drop.chance * pyrePenalty.lootMult * (1 + markDropBonus);
            if (Math.random() < adjustedChance) {
                const quantity = Math.floor(Math.random() * (drop.maxQty - drop.minQty + 1)) + drop.minQty;
                drops.push({ itemId: drop.itemId, quantity });
            }
        }

        // Roll rare drops (apply Pyre loot penalty + bounty mark bonus + Treasure Nose global bonus)
        const globalRareBonus = getGlobalRareLootBonus();
        for (const drop of lootTable.rare) {
            // Apply pyre penalty and mark bonus to base chance, then add Treasure Nose bonus
            const adjustedChance = (drop.chance * pyrePenalty.lootMult * (1 + markDropBonus)) + globalRareBonus;
            if (Math.random() < adjustedChance) {
                const quantity = Math.floor(Math.random() * (drop.maxQty - drop.minQty + 1)) + drop.minQty;
                drops.push({ itemId: drop.itemId, quantity });
            }
        }

        return drops;
    }

    /**
     * Handle player death
     */
    handleDeath() {
        const combat = gameState.getCombat();
        combat.inCombat = false;
        combat.currentMob = null;
        combat.mobCurrentHp = 0;
        // Don't clear action - keep it so tick() continues running for respawn timer

        // Clear all potion buffs on death
        gameState.clearAllPotionBuffs();

        // Clear corrupted prey marks and corruption timer on death
        this.clearCorruptedPrey();
        if (isSkillCorrupted(SKILLS.COMBAT)) deactivateCorruption(SKILLS.COMBAT);

        // Clear boss mechanic state on death
        this.clearBossState();
        this.clearMobMechanicState();
        this.clearEquipmentEffectState();
        this.clearSetBonusState();

        logger.game('You have died! Respawning in 25 seconds...');

        if (this.onPlayerDeath) {
            this.onPlayerDeath();
        }
    }

    /**
     * Handle respawn after death timer
     */
    handleRespawn() {
        const combat = gameState.getCombat();
        const zoneId = combat.currentZone;

        gameState.respawn();
        logger.game('You have respawned!');

        // Resume combat in the same zone
        if (zoneId) {
            combat.inCombat = true;
            combat.currentZone = zoneId;
            this.spawnNextMob();
        }

        if (this.onRespawn) {
            this.onRespawn();
        }
    }

    /**
     * Manually eat food to heal
     * @param {string} foodId - Food item ID
     * @returns {{ success: boolean, healed?: number, reason?: string }}
     */
    eatFood(foodId) {
        const combat = gameState.getCombat();
        const now = Date.now();

        // Check if already at full HP
        const currentHp = gameState.getCurrentHp();
        const maxHp = gameState.getMaxHp();
        if (currentHp >= maxHp) {
            return { success: false, reason: 'Already at full HP' };
        }

        // Check cooldown only when in combat
        if (this.inCombat && now - combat.lastEatTime < COMBAT_CONFIG.MANUAL_EAT_COOLDOWN) {
            const remaining = Math.ceil((COMBAT_CONFIG.MANUAL_EAT_COOLDOWN - (now - combat.lastEatTime)) / 1000);
            return { success: false, reason: `Eat cooldown: ${remaining}s` };
        }

        // Check if we have the food
        if (gameState.getItemCount(foodId) < 1) {
            return { success: false, reason: 'No food in inventory' };
        }

        // Get food heal amount
        const food = getItem(foodId);
        if (!food || !food.healAmount) {
            return { success: false, reason: 'Not a food item' };
        }

        // Apply Nourishing Meals talent (+3 flat HP from Cooking tree)
        const cookingHealBonus = getTalentFlatBonus(SKILLS.COOKING, 'foodHealBonus') || 0;
        // Apply Combat Medic talent bonus (+10% food healing per rank)
        const foodHealBonus = this.getCombatTalentFoodHealingBonus();
        // Apply Flame Barrier ring bonus (+20% food healing)
        const equipFoodBonus = this.getEquipmentFoodHealBonus();
        const boostedHealAmount = Math.floor((food.healAmount + cookingHealBonus) * (1 + foodHealBonus + equipFoodBonus));

        // Second Helping: 10% chance to not consume food (Cooking talent)
        const secondHelpingProc = rollTalentProc(SKILLS.COOKING, 'secondHelping');
        if (!secondHelpingProc) {
            gameState.removeItem(foodId, 1);
        } else {
            logger.game('Second Helping! Food not consumed!');
            recordTalentProc(SKILLS.COOKING, 'Second Helping', 'Food preserved!', null);
        }

        // Heal and update cooldown
        const healed = gameState.healHp(boostedHealAmount);
        combat.lastEatTime = now;

        // Show heal number
        if (this.onHeal && healed > 0) {
            this.onHeal(healed);
        }

        // Apply HoT effect if food has it (empowered food)
        if (food.hot) {
            this.applyHotEffect(food.hot);
        }

        logger.game(`Ate ${food.name} and healed ${healed} HP`);

        return { success: true, healed };
    }

    /**
     * Check and trigger auto-eat if conditions are met
     * @param {number} speedMultiplier - Speed multiplier
     */
    checkAutoEat(speedMultiplier = 1) {
        const autoEat = gameState.getAutoEat();
        // Auto-unlock if requirements met (milestone may have been built while unlocked was false)
        if (!autoEat.unlocked) gameState.checkAutoEatUnlock();
        if (!autoEat.unlocked) return;
        if (!autoEat.storedFood || autoEat.storedFood.quantity <= 0) return;

        const combat = gameState.getCombat();
        const now = Date.now();

        // Check auto-eat cooldown (slightly longer than manual)
        const cooldown = COMBAT_CONFIG.AUTO_EAT_COOLDOWN / speedMultiplier;
        if (now - combat.lastEatTime < cooldown) return;

        // Check HP threshold
        const currentHp = gameState.getCurrentHp();
        const maxHp = gameState.getMaxHp();
        const hpPercent = (currentHp / maxHp) * 100;

        if (hpPercent <= autoEat.threshold) {
            const food = getItem(autoEat.storedFood.itemId);
            if (food && food.healAmount) {
                // Second Helping: 10% chance to not consume food (Cooking talent)
                const secondHelpingProc = rollTalentProc(SKILLS.COOKING, 'secondHelping');

                // Consume stored food (unless Second Helping procs)
                if (!secondHelpingProc) {
                    const result = gameState.consumeAutoEatFood();
                    if (!result.success) return; // Failed to consume, abort
                } else {
                    logger.game('Second Helping! Auto-eat food not consumed!');
                    recordTalentProc(SKILLS.COOKING, 'Second Helping', 'Food preserved!', null);
                }

                // Apply Nourishing Meals talent (+3 flat HP from Cooking tree)
                const cookingHealBonus = getTalentFlatBonus(SKILLS.COOKING, 'foodHealBonus') || 0;
                // Apply Combat Medic talent bonus (+10% food healing per rank)
                const foodHealBonus = this.getCombatTalentFoodHealingBonus();
                // Apply Flame Barrier ring bonus (+20% food healing)
                const equipFoodBonus = this.getEquipmentFoodHealBonus();
                const boostedHealAmount = Math.floor((food.healAmount + cookingHealBonus) * (1 + foodHealBonus + equipFoodBonus));
                const healed = gameState.healHp(boostedHealAmount);
                combat.lastEatTime = now;

                // Show heal number
                if (this.onHeal && healed > 0) {
                    this.onHeal(healed);
                }

                // Apply HoT effect if food has it (empowered food)
                if (food.hot) {
                    this.applyHotEffect(food.hot);
                }

                logger.game(`Auto-ate ${food.name} and healed ${healed} HP`);
                // Award architecting XP via Kitchen Overseer talent
                architectingSkill.awardAutoEatXP();
            }
        }
    }

    /**
     * Get zones grouped by tier
     * @returns {Object} Zones by tier number
     */
    getZonesByTier() {
        const byTier = { 1: [], 2: [], 3: [] };
        for (const zone of Object.values(COMBAT_ZONES)) {
            if (byTier[zone.tier]) {
                byTier[zone.tier].push(zone);
            }
        }
        return byTier;
    }

    /**
     * Get current combat state for UI
     * @returns {Object}
     */
    getCombatState() {
        const combat = gameState.getCombat();
        const playerStats = this.getPlayerStats();
        const zoneId = combat.currentZone;
        const zone = zoneId ? getZone(zoneId) : null;

        // Use current mob, or killed mob during delay for UI display
        const isInKillDelay = this.mobKillTime !== null && this.lastKilledMob !== null;
        const mob = combat.currentMob || (isInKillDelay ? this.lastKilledMob : null);

        // Calculate effective accuracy if in combat
        let effectiveAccuracy = playerStats.accuracy;
        let styleMods = { accuracyMod: 1, damageMod: 1 };
        if (mob && playerStats.attackStyle) {
            styleMods = this.getStyleModifiers(playerStats.attackStyle);
            effectiveAccuracy = this.getEffectiveAccuracy();
        }

        // Get Pyre penalty if in a zone
        const pyrePenalty = zoneId ? this.getPyrePenalty(zoneId) : { damageMult: 1, speedMult: 1, lootMult: 1, deficit: 0 };

        // Calculate attack progress (0-1) for progress bars
        let playerAttackProgress = 0;
        let mobAttackProgress = 0;
        if (combat.inCombat && combat.currentMob) {
            const now = Date.now();
            // Include Pyre speed penalty + Relentless bonus + snare in player attack progress
            const relBonus = this.getRelentlessSpeedBonus();
            const snareSpeed = this.getSnareModifiedSpeed(playerStats.attackSpeed);
            const effectivePlayerSpeed = snareSpeed * pyrePenalty.speedMult * (1 - relBonus);
            playerAttackProgress = Math.min(1, (now - combat.lastPlayerAttack) / effectivePlayerSpeed);
            // Include slow effect + boss speed + mob mechanic modifiers in mob attack progress
            let effectiveMobSpeed = this.getMobEffectiveAttackSpeed(combat.currentMob);
            const bMods = this.getBossModifiers();
            if (bMods.mobSpeedMult !== 1.0) effectiveMobSpeed = Math.floor(effectiveMobSpeed * bMods.mobSpeedMult);
            const resolvedMob = typeof combat.currentMob === 'string' ? getMob(combat.currentMob) : combat.currentMob;
            if (resolvedMob) {
                const uiMechSpeed = this.getMobMechanicAttackSpeed(resolvedMob);
                if (uiMechSpeed !== resolvedMob.attackSpeed) {
                    effectiveMobSpeed = Math.floor(effectiveMobSpeed * (uiMechSpeed / resolvedMob.attackSpeed));
                }
            }
            mobAttackProgress = Math.min(1, (now - combat.lastMobAttack) / effectiveMobSpeed);
        }

        // Get zone progress if in zone
        const zoneProgress = zoneId ? gameState.getZoneProgress(zoneId) : null;

        // Magic combat state
        const activeSpell = this.getActiveSpell();
        const canCast = playerStats.attackStyle === 'magic' ? this.canCastSpell() : { success: true };
        const spellEffectsState = this.getSpellEffectsState();

        // Calculate mob's effective attack speed (with slow effect)
        const mobEffectiveAttackSpeed = mob ? this.getMobEffectiveAttackSpeed(mob) : 0;

        return {
            currentHp: gameState.getCurrentHp(),
            maxHp: gameState.getMaxHp(),
            inCombat: combat.inCombat,
            currentZone: zone,
            currentZoneId: zoneId,
            currentTier: combat.currentTier || null,
            currentMob: mob,
            mobCurrentHp: isInKillDelay ? 0 : combat.mobCurrentHp,
            mobMaxHp: mob ? mob.hp : 0,
            isDead: combat.isDead,
            respawnRemaining: gameState.getRespawnTimeRemaining(),
            totalKills: combat.totalKills,
            playerStats: playerStats,
            effectiveAccuracy: effectiveAccuracy,
            styleMods: styleMods,
            mobWeakTo: mob ? mob.weakTo : null,
            mobResistantTo: mob ? mob.resistantTo : null,
            playerAttackProgress: playerAttackProgress,
            mobAttackProgress: isInKillDelay ? 0 : mobAttackProgress,
            pyrePenalty: pyrePenalty,
            zoneProgress: zoneProgress,
            targetMob: this.targetMob,
            isInKillDelay: isInKillDelay,
            // Preview state
            isPreviewing: this.isPreviewing(),
            previewState: this.getPreviewState(),
            // Magic combat state
            activeSpell: activeSpell,
            canCastSpell: canCast,
            spellEffects: spellEffectsState,
            staffMagicDamage: this.getStaffMagicDamage(),
            mobEffectiveAttackSpeed: mobEffectiveAttackSpeed,
            // Boss mechanic state
            bossState: this.bossState ? {
                isBoss: true,
                mechanics: this.bossState.mechanics,
                currentPhase: this.bossState.currentPhase,
                enrageStage: this.bossState.enrageStage,
                fightStartTime: this.bossState.fightStartTime,
                modifiers: this.getBossModifiers(),
                activePlayerDots: this.bossState.activePlayerDots
            } : null,
            // Equipment special effect state
            equipmentEffects: {
                relentlessStacks: this.equipmentState.relentlessStacks.length,
                relentlessSpeedBonus: this.getRelentlessSpeedBonus(),
                markedPrey: !!this.equipmentState.markedPrey,
                emberShieldActive: !!this.equipmentState.emberShieldBuff && Date.now() < this.equipmentState.emberShieldBuff.endTime,
                searingArrowsDot: !!this.equipmentState.searingArrowsDot,
                smolderingDot: !!this.equipmentState.smolderingDot,
                hitCounter: this.equipmentState.hitCounter,
                lifestealPercent: this.getLifestealPercent(),
                equippedEffects: this.getEquippedEffects()
            },
            // Set bonus state
            setBonus: {
                activeBonuses: this.getActiveSetBonuses(),
                momentum: this.setBonusState.momentum,
                afterimageActive: this.setBonusState.afterimage && Date.now() < this.setBonusState.afterimage.endTime,
                riposteCounter: this.setBonusState.riposteCounter,
                rangedHitCounter: this.setBonusState.rangedHitCounter,
                phantomArcherActive: this.setBonusState.phantomShots && this.setBonusState.phantomShots.remaining > 0,
                activeFusions: this.getActiveSpellFusions().map(f => f.name),
                shatterReady: this.setBonusState.spellFusion.shatterReady
            }
        };
    }
}

// Export singleton instance
export const combatSkill = new CombatSkill();
export default combatSkill;
